import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCcw, X, ArrowRight, Users as UsersIcon, ExternalLink, Search, ChevronDown, ChevronUp, Tablet } from "lucide-react";
import TripChat from "@/components/TripChat";
import InvoicesTab from "@/components/InvoicesTab";
import QuickChargeTab from "@/components/QuickChargeTab";
import DriverGarage from "@/components/DriverGarage";
import DriverLiveMap from "@/components/DriverLiveMap";
import EventsTab from "@/components/EventsTab";
import CarRentalTab from "@/components/CarRentalTab";
import CouponsTab, { CouponPicker } from "@/components/Coupons";
import RateSheetButton from "@/components/RateSheetButton";
import PlacesInput from "@/components/PlacesInput";
import UseLocationButton from "@/components/UseLocationButton";
import PaymentSelector from "@/components/PaymentSelector";
import MarketingTab from "@/components/MarketingTab";
import FleetTab from "@/components/FleetTab";
import TrashTab from "@/components/TrashTab";
import A2PStatusBanner from "@/components/A2PStatusBanner";
import DispatchNotices from "@/components/DispatchNotices";
import UnpaidAgingTile from "@/components/UnpaidAgingTile";
import CombineInvoiceModal from "@/components/CombineInvoiceModal";
import EmailHealthTile from "@/components/EmailHealthTile";
import WeeklyPayoutRun from "@/components/WeeklyPayoutRun";
import DriverPayrollSheet from "@/components/DriverPayrollSheet";
import Annual1099Summary from "@/components/Annual1099Summary";
import FileUploadSection from "@/components/FileUploadSection";
import { DriverApplicationsTab } from "@/components/DriverApplicationsTab";
import AddressFavorites from "@/components/AddressFavorites";
import RoutePreviewMap from "@/components/RoutePreviewMap";
import DriverLinkBadge from "@/components/DriverLinkBadge";
import DispatcherAddCard from "@/components/DispatcherAddCard";
import { StaffThemeProvider, StaffThemeToggle } from "@/lib/StaffTheme";
import AiChatWidget from "@/components/AiChatWidget";
import VehicleClassBadge, { isEconomy } from "@/components/VehicleClassBadge";
import { realEmail as displayEmail, isPlaceholderEmail } from "@/lib/email";
import { formatPickupShort } from "@/lib/datetime";
import useDispatchStream, { pushBrowserNotification, requestBrowserNotificationPermission } from "@/lib/useDispatchStream";
import { ensureWebPushSubscribed, unsubscribeWebPush, sendTestPush, isWebPushSupported } from "@/lib/webpush";

const COLUMNS = [
  { key: "pending",              label: "Pending",        legacy: ["new"] },
  { key: "accepted",             label: "Accepted",       legacy: ["assigned", "dispatched", "accepted_by_driver"] },
  { key: "en_route",             label: "En route" },
  { key: "on_location",          label: "On location" },
  { key: "customer_in_car",      label: "Customer in car", legacy: ["in_progress"] },
  { key: "clear_at_destination", label: "Clear at dest." },
  { key: "completed",            label: "Completed" },
];

// Canonical statuses shown as action buttons in the booking drawer.
// STATUS_META still maps every DB value to a display label/color, but only these
// keys appear as dispatcher actions (so duplicates like new/pending or assigned/accepted
// aren't shown twice).
const PRIMARY_STATUS_BUTTONS = [
  "pending",
  "accepted",
  "dispatched",
  "accepted_by_driver",
  "en_route",
  "on_location",
  "customer_in_car",
  "clear_at_destination",
  "completed",
  "cancelled",
  "paused",
];

const STATUS_META = {
  pending:              { label: "Pending",              color: "#A1A1AA" },
  new:                  { label: "Pending",              color: "#A1A1AA" },
  accepted:             { label: "Accepted",             color: "#3B82F6" },
  assigned:             { label: "Accepted",             color: "#3B82F6" },
  dispatched:           { label: "Dispatched",           color: "#6366F1" },
  accepted_by_driver:   { label: "Driver accepted",      color: "#8B5CF6" },
  en_route:             { label: "En route",             color: "#F59E0B" },
  on_location:          { label: "On location",          color: "#EC4899" },
  customer_in_car:      { label: "Customer in car",      color: "#D4AF37" },
  in_progress:          { label: "Customer in car",      color: "#D4AF37" },
  clear_at_destination: { label: "Clear at destination", color: "#06B6D4" },
  completed:            { label: "Completed",            color: "#10B981" },
  cancelled:            { label: "Cancelled",            color: "#EF4444" },
  paused:               { label: "Paused",               color: "#71717A" },
};

export default function Dispatcher() {
  return (
    <StaffThemeProvider>
      <DispatcherInner/>
    </StaffThemeProvider>
  );
}

function DispatcherInner() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  const [tab, setTab] = useState("board"); // board | drivers | users | stats
  // Allow other pages to deep-link into a specific dispatcher tab via ?tab=invoices etc.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [bookings, setBookings] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showManualInvoice, setShowManualInvoice] = useState(false);
  const [showNewReservation, setShowNewReservation] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  // Users-tab client search — matches name, email, phone (digits) or address
  const filteredUsers = React.useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    const qDigits = q.replace(/\D/g, "");
    return users.filter(u =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (qDigits.length >= 3 && (u.phone || "").replace(/\D/g, "").includes(qDigits)) ||
      (u.address || "").toLowerCase().includes(q)
    );
  }, [users, userSearch]);
  const [stats, setStats] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [corpAccounts, setCorpAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showImportCustomers, setShowImportCustomers] = useState(false);
  const [showQuickInvoice, setShowQuickInvoice] = useState(false);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [customerInitialSection, setCustomerInitialSection] = useState("trips");
  const [openDriverId, setOpenDriverId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [b, d, u, s, ser, corp, veh] = await Promise.all([
        api.get("/bookings"),
        api.get("/drivers"),
        api.get("/admin/users?role=customer"),
        api.get("/admin/stats"),
        api.get("/bookings/series").catch(() => ({ data: [] })),
        api.get("/corporate/accounts").catch(() => ({ data: [] })),
        api.get("/vehicles?staff=1").catch(() => ({ data: [] })),
      ]);
      setBookings(b.data); setDrivers(d.data); setUsers(u.data); setStats(s.data);
      setSeriesList(ser.data); setCorpAccounts(corp.data); setVehicles(veh.data);
    } catch (e) {
      // ignore — auth might still be loading
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login", { replace: true }); return; }
    if (user.role !== "dispatcher") { nav("/customer", { replace: true }); return; }
    refresh();
  }, [user, loading, nav, refresh]);

  const byStatus = useMemo(() => {
    const m = Object.fromEntries(COLUMNS.map(c => [c.key, []]));
    // Build legacy-status → canonical mapping (e.g., "new" → "pending")
    const aliasToCol = {};
    for (const col of COLUMNS) {
      aliasToCol[col.key] = col.key;
      for (const lg of (col.legacy || [])) aliasToCol[lg] = col.key;
    }
    for (const b of bookings) {
      const col = aliasToCol[b.status];
      if (col && m[col]) m[col].push(b);
    }
    return m;
  }, [bookings]);

  // SSE live stream: refresh data + show browser notifications on new bookings/status changes
  const onStreamEvent = useCallback((type, payload) => {
    if (type === "new_booking") {
      const b = payload.booking;
      toast.success(`New booking ${b.confirmation_code} · ${b.customer_name}`);
      pushBrowserNotification(
        `New booking · ${b.confirmation_code}`,
        `${b.customer_name} · ${b.vehicle_name} · $${(b.fare || 0).toFixed(0)}`,
        `bk-${b.booking_id}`,
      );
      refresh();
    } else if (type === "status_change") {
      // Quietly refresh — too noisy to toast every status update
      refresh();
    } else if (type === "notice") {
      toast(payload.title, { description: payload.body, duration: 12000 });
      pushBrowserNotification(payload.title, payload.body, `nt-${payload.notice_id}`);
      setNoticeTick(t => t + 1);
    }
  }, [refresh]);

  const [noticeTick, setNoticeTick] = useState(0);
  const { connected: streamConnected } = useDispatchStream(
    onStreamEvent,
    !!user && user.role === "dispatcher",
  );

  if (!user || user.role !== "dispatcher") return null;

  return (
    <div data-testid="dispatcher-dashboard" className="dispatcher-shell bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <div className="pt-24">
        <A2PStatusBanner/>
        <DispatchNotices refreshKey={noticeTick}/>
      </div>

      <section className="pt-4 pb-6 px-6 md:px-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <div className="overline mb-2">Dispatcher Console</div>
              <h1 className="font-serif text-4xl md:text-5xl leading-none">Operations.</h1>
            </div>
            <div className="flex items-center gap-3">
              {stats && (
                <div className="flex items-center gap-6 mr-4">
                  <Stat n={stats.total_bookings || 0} l="Total bookings"/>
                  <Stat n={`$${(stats.revenue_pipeline || 0).toFixed(0)}`} l="Pipeline" gold/>
                  <Stat n={stats.by_status?.new || 0} l="Unassigned" alert={stats.by_status?.new > 0}/>
                </div>
              )}
              <button data-testid="refresh-button" onClick={refresh} className="btn-ghost !py-2.5 !px-4 text-[11px]">
                <RefreshCcw size={14}/> {refreshing ? "…" : "Refresh"}
              </button>
              <LiveBadge connected={streamConnected}/>
              <StaffThemeToggle/>
              <RateSheetButton/>
              <button
                data-testid="passenger-screen-button"
                onClick={() => window.open("/passenger", "_blank", "noopener")}
                title="Open the rear-seat Passenger screen (welcome board + Spotify)"
                className="btn-ghost !py-2.5 !px-4 text-[11px]"
              ><Tablet size={14}/> Passenger</button>
              <button
                data-testid="new-reservation-button"
                onClick={() => setShowNewReservation(true)}
                className="btn-gold !py-2.5 !px-4 text-[11px]"
              >+ New Reservation</button>
              <button
                data-testid="new-invoice-button"
                onClick={() => setShowManualInvoice(true)}
                className="btn-ghost !py-2.5 !px-4 text-[11px]"
              >+ Custom Invoice</button>
            </div>
          </div>

          <div className="flex flex-wrap border-b border-white/10 mb-8 gap-y-1">
            {[
              ["board", "Dispatch Board"],
              ["live-map", "Live Map"],
              ["calendar", "Calendar"],
              ["events", "Events"],
              ["rental", "Car Rental"],
              ["invoices", "Invoices"],
              ["quick-charge", "Quick Charge"],
              ["payments", "Payments"],
              ["users", "Users"],
              ["drivers", "Drivers"],
              ["applications", "Applications"],
              ["settlements", "Driver Payouts"],
              ["route-audit", "Route Audit"],
              ["series", `Series (${seriesList.length})`],
              ["corporate", `Corporate (${corpAccounts.length})`],
              ["fleet", "Fleet"],
              ["coupons", "Coupons"],
              ["marketing", "Marketing"],
              ["stats", "Analytics"],
              ["trash", "Trash"],
            ].map(([k, l]) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => setTab(k)}
                className={`px-5 py-3.5 text-[11px] uppercase tracking-[0.22em] whitespace-nowrap transition border-b-2 ${tab === k ? 'border-[#D4AF37] bg-[#D4AF37] text-[#0A0A0A] font-bold shadow-[0_0_14px_rgba(212,175,55,0.45)]' : 'border-transparent text-[#A1A1AA] hover:text-white hover:bg-white/5'}`}
              >{l}</button>
            ))}
          </div>
        </div>
      </section>

      {/* BOARD */}
      {tab === "board" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-[1500px] mx-auto">
            <EmailHealthTile/>
            <UnpaidAgingTile/>
            <div className="flex gap-4 overflow-x-auto pb-8">
              {COLUMNS.map(col => (
                <div key={col.key} data-testid={`col-${col.key}`} className="min-w-[300px] max-w-[300px] bg-[#0A0A0A] border border-white/5 flex flex-col">
                  <div className="px-4 py-3 border-b border-white/5 flex justify-between items-center">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#F5F2EB]">{col.label}</span>
                    <span className="text-xs text-[#D4AF37]">{byStatus[col.key]?.length || 0}</span>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60vh]">
                    {byStatus[col.key]?.map(b => <BoardCard key={b.booking_id} b={b} onOpen={() => setSelected(b)}/>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SETTLEMENTS — Driver payouts */}
      {tab === "settlements" && <SettlementsTab bookings={bookings}/>}

      {/* CALENDAR — Month view of all trips + car rentals */}
      {tab === "live-map" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-[1500px] mx-auto">
            <DriverLiveMap/>
          </div>
        </section>
      )}
      {tab === "calendar" && <CalendarTab bookings={bookings} onOpen={setSelected} onOpenRentals={() => setTab("rental")}/>}

      {/* EVENTS — Wedding & special events workspace */}
      {tab === "events" && <EventsTab/>}
      {tab === "rental" && <CarRentalTab/>}

      {/* INVOICES — Consolidated multi-trip invoices */}
      {tab === "invoices" && <InvoicesTab/>}

      {/* PAYMENTS — completed/paid bookings ledger */}
      {tab === "payments" && <PaymentsTab bookings={bookings} onOpen={setSelected}/>}

      {/* QUICK CHARGE — charge a card on file without a reservation (special cases) */}
      {tab === "quick-charge" && <QuickChargeTab/>}

      {/* FLEET — vehicle classes on/off for new bookings (live, no redeploy) */}
      {tab === "fleet" && <FleetTab/>}

      {/* COUPONS — Promo codes & event coupons */}
      {tab === "coupons" && <CouponsTab/>}

      {/* MARKETING — Coupon performance + referral cycle + email blast ROI */}
      {tab === "marketing" && <MarketingTab/>}

      {/* ROUTE AUDIT — Compare quoted vs actual driven miles */}
      {tab === "route-audit" && <RouteAuditTab onChange={refresh}/>}

      {/* APPLICATIONS — public "Drive with us" hiring pipeline */}
      {tab === "applications" && <DriverApplicationsTab/>}

      {/* DRIVERS */}
      {tab === "drivers" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Fleet drivers</h2>
              <div className="flex items-center gap-3">
                <ContractTemplateButton/>
                <button data-testid="add-driver-btn" onClick={() => setShowAddDriver(true)} className="btn-gold !py-2.5 !px-5 text-[11px]"><Plus size={14}/> Add Driver</button>
              </div>
            </div>
            <ComplianceBanner/>
            <div className="border border-white/10 bg-[#0A0A0A]">
              <table className="w-full text-sm">
                <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Vehicle</th>
                    <th className="text-left p-3">Color</th>
                    <th className="text-left p-3">Plate</th>
                    <th className="text-left p-3">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map(d => (
                    <DriverRow key={d.driver_id} d={d} onChange={refresh}
                      onOpen={() => setOpenDriverId(d.driver_id)}/>
                  ))}
                  {drivers.length === 0 && (
                    <tr><td colSpan="7" className="p-10 text-center text-[#71717A]">No drivers yet. Add one to assign trips.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-[#71717A] mt-4">
              Tip: drivers must sign in once with the same Google email to access the driver mobile view.
            </p>
          </div>
        </section>
      )}

      {/* USERS — customers only, inline-editable */}
      {tab === "users" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
              <h2 className="font-serif text-2xl">Customers · inline edit</h2>
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]"/>
                <input
                  data-testid="users-search-input"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search client by name, email, phone…"
                  className="w-full bg-[#0a0a0a] border border-white/10 pl-9 pr-8 py-2 text-sm text-[#F5F2EB] placeholder:text-[#52525B] focus:border-[#D4AF37] outline-none"
                />
                {userSearch && (
                  <button data-testid="users-search-clear" onClick={() => setUserSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white text-base leading-none">×</button>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-[#A1A1AA]" data-testid="users-count">
                  {userSearch ? `${filteredUsers.length} of ${users.length}` : users.length} customers
                </span>
                <button data-testid="quick-invoice-btn" onClick={() => setShowQuickInvoice(true)}
                  className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">⚡ Quick Invoice</button>
                <button data-testid="import-customers-btn" onClick={() => setShowImportCustomers(true)}
                  className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">↓ Import</button>
                <ExportContactsButton/>
                <button data-testid="add-customer-btn" onClick={() => setShowAddCustomer(true)}
                  className="btn-gold !py-2.5 !px-5 text-[11px]"><Plus size={14}/> Add Customer</button>
              </div>
            </div>
            <div className="border border-white/10 bg-[#0A0A0A]">
              <table className="w-full text-sm">
                <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Phone</th>
                    <th className="text-left p-3">Address</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <UserRow key={u.user_id} u={u} onChange={refresh}
                      onOpen={(section) => { setCustomerInitialSection(section || "trips"); setOpenCustomerId(u.user_id); }}/>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan="5" className="p-10 text-center text-[#71717A]">No customers yet.</td></tr>
                  )}
                  {users.length > 0 && filteredUsers.length === 0 && (
                    <tr><td colSpan="5" className="p-10 text-center text-[#71717A]">No clients match “{userSearch}”.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* STATS */}
      {tab === "stats" && stats && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries({
              "Total bookings": stats.total_bookings || 0,
              "Pipeline revenue": `$${(stats.revenue_pipeline || 0).toFixed(2)}`,
              "Unassigned (New)": stats.by_status?.new || 0,
            }).map(([k, v]) => (
              <div key={k} className="border border-white/10 bg-[#0A0A0A] p-8">
                <div className="overline mb-3">{k}</div>
                <div className="font-serif text-5xl text-[#D4AF37]">{v}</div>
              </div>
            ))}
            <div className="md:col-span-3 border border-white/10 bg-[#0A0A0A] p-8">
              <div className="overline mb-5">By status</div>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <div key={k}>
                    <div className="text-3xl font-serif" style={{ color: m.color }}>{stats.by_status?.[k] || 0}</div>
                    <div className="overline mt-1">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-3"><ReportsPanel/></div>
          </div>
        </section>
      )}

      {/* SERIES */}
      {tab === "series" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Recurring series · all customers</h2>
              <span className="text-xs text-[#A1A1AA]">{seriesList.length} active series</span>
            </div>
            {seriesList.length === 0 ? (
              <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center text-[#A1A1AA]">No recurring series yet.</div>
            ) : (
              <div className="border border-white/10 bg-[#0A0A0A]">
                <table className="w-full text-sm">
                  <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                    <tr>
                      <th className="text-left p-4">Customer</th>
                      <th className="text-left p-4">Route</th>
                      <th className="text-left p-4">Frequency</th>
                      <th className="text-right p-4">Active / Done / Cxl</th>
                      <th className="text-right p-4">$ / ride</th>
                      <th className="text-right p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seriesList.map(s => (
                      <tr key={s.series_id} data-testid={`series-tr-${s.series_id}`} className="border-t border-white/5">
                        <td className="p-4">
                          <div>{s.customer_name}</div>
                          <div className="text-xs text-[#71717A]">{s.customer_email}</div>
                        </td>
                        <td className="p-4 text-xs text-[#A1A1AA]">{s.pickup_address}<br/><span className="text-[#71717A]">→ {s.dropoff_address}</span></td>
                        <td className="p-4 uppercase text-xs tracking-[0.18em] text-[#D4AF37]">{s.frequency}<div className="text-[#71717A] mt-1">{new Date(s.first_pickup).toLocaleDateString()} → {new Date(s.last_pickup).toLocaleDateString()}</div></td>
                        <td className="p-4 text-right text-xs">
                          <span className="text-[#D4AF37]">{s.active_count}</span> / <span>{s.completed_count}</span> / <span className="text-[#71717A]">{s.cancelled_count}</span>
                        </td>
                        <td className="p-4 text-right font-serif text-lg text-[#D4AF37]">${s.fare_per_ride.toFixed(0)}</td>
                        <td className="p-4 text-right">
                          <SeriesActions series={s} onChange={refresh}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CORPORATE */}
      {tab === "corporate" && (
        <section className="pb-20 px-6 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Corporate accounts</h2>
              <a data-testid="open-corporate-page" href="/corporate" className="btn-gold !py-2 !px-4 text-[11px]">Open full view</a>
            </div>
            {corpAccounts.length === 0 ? (
              <div className="border border-white/10 bg-[#0A0A0A] p-12 text-center text-[#A1A1AA]">No corporate accounts yet. Use the full view to create one.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {corpAccounts.map(a => (
                  <div key={a.account_id} className="border border-white/10 bg-[#0A0A0A] p-5">
                    <div className="overline mb-1">{a.billing_period}</div>
                    <div className="font-serif text-xl">{a.name}</div>
                    <div className="text-xs text-[#A1A1AA] mt-1">{a.billing_email}</div>
                    <div className="flex justify-between items-center mt-4 pt-3 border-t border-white/5">
                      <span className="text-xs text-[#A1A1AA]">{(a.employee_emails || []).length} employees</span>
                      <span className="font-serif text-xl text-[#D4AF37]">{a.discount_percent}% off</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* AI DISPATCH ASSISTANT */}
      <AiChatWidget mode="dispatcher"/>

      {/* DETAIL DRAWER */}
      {selected && (
        <BookingDetail
          booking={selected}
          drivers={drivers}
          onClose={() => setSelected(null)}
          onChange={refresh}
        />
      )}

      {showAddDriver && <AddDriverModal onClose={() => setShowAddDriver(false)} onCreated={() => { setShowAddDriver(false); refresh(); }}/>}
      {showAddCustomer && <AddCustomerModal onClose={() => setShowAddCustomer(false)} onCreated={() => { setShowAddCustomer(false); refresh(); }}/>}
      {showImportCustomers && <ImportContactsModal onClose={() => setShowImportCustomers(false)} onDone={() => { setShowImportCustomers(false); refresh(); }}/>}
      {showQuickInvoice && <QuickInvoiceModal customers={users} onClose={() => setShowQuickInvoice(false)} onCreated={() => { setShowQuickInvoice(false); refresh(); }}/>}
      {openCustomerId && <CustomerDetailDrawer userId={openCustomerId} initialSection={customerInitialSection} onClose={() => setOpenCustomerId(null)} onChanged={refresh}/>}
      {openDriverId && <DriverDetailDrawer driverId={openDriverId} onClose={() => setOpenDriverId(null)}/>}
      {showManualInvoice && (
        <ManualInvoiceModal
          vehicles={vehicles}
          onClose={() => setShowManualInvoice(false)}
          onCreated={(doc) => { setShowManualInvoice(false); refresh(); toast.success(`Invoice ${doc.invoice_number} created`); }}
        />
      )}
      {showNewReservation && (
        <DispatcherNewBookingModal
          vehicles={vehicles}
          onClose={() => setShowNewReservation(false)}
          onCreated={(doc) => {
            setShowNewReservation(false);
            refresh();
            toast.success(`Reservation ${doc.confirmation_code} · ${doc.customer_name} created`);
          }}
        />
      )}
    </div>
  );
}

function Stat({ n, l, gold, alert }) {
  return (
    <div className="text-right">
      <div className={`font-serif text-2xl ${gold ? 'text-[#D4AF37]' : alert ? 'text-[#F59E0B]' : 'text-[#F5F2EB]'}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">{l}</div>
    </div>
  );
}

function LiveBadge({ connected }) {
  const [perm, setPerm] = React.useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [pushOn, setPushOn] = React.useState(false);

  React.useEffect(() => {
    // Check if already subscribed
    if (isWebPushSupported()) {
      navigator.serviceWorker.getRegistration("/sw.js").then(reg => {
        if (reg) reg.pushManager.getSubscription().then(s => setPushOn(!!s));
      }).catch(() => {});
    }
  }, []);

  const enable = async () => {
    const p = await requestBrowserNotificationPermission();
    setPerm(p);
    if (p === "granted") {
      const r = await ensureWebPushSubscribed();
      if (r.ok) {
        setPushOn(true);
        toast.success("Web Push enabled · You'll get alerts even when this tab is closed");
        pushBrowserNotification("Notifications enabled", "Even when the dispatcher tab is closed, new bookings will ping you.", "perm-test");
      } else if (r.reason === "denied") {
        toast.error("Notifications blocked in browser settings");
      } else {
        toast.error(`Push setup failed: ${r.reason}`);
      }
    } else if (p === "denied") {
      toast.error("Notifications blocked in browser settings");
    }
  };
  const disable = async () => {
    await unsubscribeWebPush();
    setPushOn(false);
    toast("Web Push disabled");
  };

  // Master switch for automated SMS to the dispatch phone (wait alerts etc.)
  const [smsAlerts, setSmsAlerts] = React.useState(null);
  React.useEffect(() => {
    api.get("/admin/dispatcher-sms-alerts").then(r => setSmsAlerts(r.data.enabled)).catch(() => setSmsAlerts(null));
  }, []);
  const flipSmsAlerts = async () => {
    try {
      const r = await api.post("/admin/dispatcher-sms-alerts", { enabled: !smsAlerts });
      setSmsAlerts(r.data.enabled);
      toast.success(r.data.enabled
        ? "Dispatcher SMS alerts ON - wait alerts & summaries will text the dispatch phone"
        : "Dispatcher SMS alerts OFF - only sign-in codes will be texted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  return (
    <div className="flex items-center gap-3" data-testid="live-badge">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
        <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`}/>
        {connected ? "Live" : "Offline"}
      </div>
      {smsAlerts !== null && (
        <button
          data-testid="dispatcher-sms-alerts-toggle"
          onClick={flipSmsAlerts}
          role="switch" aria-checked={smsAlerts}
          title={smsAlerts ? "Automated texts to the dispatch phone are ON - click to mute" : "Automated texts to the dispatch phone are OFF - click to resume"}
          className={`text-[10px] uppercase tracking-[0.18em] hover:underline ${smsAlerts ? "text-emerald-400" : "text-red-400"}`}
        >SMS alerts {smsAlerts ? "on" : "off"}</button>
      )}
      {pushOn ? (
        <>
          <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400" data-testid="push-on-indicator">🔔 Push on</span>
          <button data-testid="push-test-btn" onClick={sendTestPush} className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:underline">Test</button>
          <button data-testid="push-disable-btn" onClick={disable} className="text-[10px] uppercase tracking-[0.18em] text-red-400 hover:underline">Disable</button>
        </>
      ) : (
        <button
          data-testid="enable-notifications-btn"
          onClick={enable}
          className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline"
        >🔔 Enable Web Push</button>
      )}
    </div>
  );
}

function FareBreakdownPanel({ booking, onChange, customerView = false }) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const bd = booking.fare_breakdown || {};
  const flex = Number(bd.cancellation_protection_fee || 0);
  const cc = Number(bd.cc_fee || 0);
  const airport = Number(bd.airport_fee || 0);
  const tolls = Number(bd.tolls || 0);
  const subtotal = Number(bd.subtotal || 0);
  const gratuity = Number(bd.gratuity || 0);
  const tax = Number(bd.tax || 0);
  const taxPct = Number(bd.tax_pct || 0.055);
  const priceAdjust = Number(bd.price_adjustment || 0);
  const waitCharge = Number(bd.wait_charge || 0);
  const miscCharges = Array.isArray(bd.misc_charges) ? bd.misc_charges : [];
  const couponDisc = Number(bd.coupon_discount || 0);
  const otherDisc = Number(bd.promo_discount || 0) + Number(bd.manual_discount || 0);

  // POST a pricing override to set a line item to a new value (in $). Backend
  // recomputes tax + total based on the new line and persists fare_breakdown.
  const setLine = async (field, amount, label) => {
    if (!window.confirm(`${amount === 0 ? "Remove" : "Restore"} ${label} ($${amount.toFixed(2)}) for trip ${booking.confirmation_code}?`)) return;
    setBusy(true);
    try {
      const body = {};
      body[field] = amount;
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, body);
      toast.success(`${label} ${amount === 0 ? "removed" : "restored"} · new total $${(r.data?.fare || 0).toFixed(2)}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const saveMiscList = async (list, action) => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/pricing`, { misc_charges: list });
      toast.success(`${action} · new total $${(r.data?.fare || 0).toFixed(2)}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const addMiscCharge = async () => {
    const label = (newLabel || "").trim();
    const amount = parseFloat(newAmount);
    if (!label) { toast.error("Charge label required"); return; }
    if (!Number.isFinite(amount) || amount === 0) { toast.error("Enter a valid non-zero amount"); return; }
    const next = [...miscCharges, { label, amount: +amount.toFixed(2) }];
    await saveMiscList(next, `Added "${label}" $${amount.toFixed(2)}`);
    setAdding(false);
    setNewLabel("");
    setNewAmount("");
  };

  const removeMiscCharge = async (idx) => {
    const item = miscCharges[idx];
    if (!window.confirm(`Remove "${item.label}" ($${Number(item.amount).toFixed(2)}) from this booking?`)) return;
    const next = miscCharges.filter((_, i) => i !== idx);
    await saveMiscList(next, `Removed "${item.label}"`);
  };

  // Customer-view bundled subtotal: subtotal + gratuity + airport + tolls +
  // cc fee + flex cancellation + sum of misc charges. State sales tax stays
  // visible as a separate line so customers can see regulatory tax clearly.
  const miscTotal = miscCharges.reduce((s, m) => s + Number(m.amount || 0), 0);
  const bundledSubtotal = subtotal - couponDisc - otherDisc + gratuity + airport + tolls + cc + flex + miscTotal + priceAdjust;

  if (customerView) {
    return (
      <div className="border-t border-white/10 pt-4 space-y-2" data-testid="fare-breakdown-panel">
        <div className="overline mb-2">Fare Breakdown</div>
        <BdLine label="Subtotal" amount={bundledSubtotal} />
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] -mt-1 mb-1" data-testid="subtotal-caveat">
          All-inclusive · taxes &amp; fees shown below
        </div>
        <BdLine label={`State sales tax (${(taxPct * 100).toFixed(1)}%)`} amount={tax} />
        {waitCharge > 0 && <BdLine label={`Pickup wait · ${Math.round(bd.wait_billable_minutes || 0)} min`} amount={waitCharge} />}
        <div className="flex justify-between items-baseline pt-2 border-t border-white/10">
          <span className="overline">Total</span>
          <span className="font-serif text-3xl text-[#D4AF37]" data-testid="booking-total">${(booking.fare || 0).toFixed(2)}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Pay later · Gratuity included</div>
      </div>
    );
  }

  return (
    <div className="border-t border-white/10 pt-4 space-y-2" data-testid="fare-breakdown-panel">
      <div className="overline mb-2">Fare Breakdown</div>
      {Number(bd.event_surcharge || 0) !== 0 && (
        <div className="flex justify-between items-center text-sm" data-testid="event-surcharge-line">
          <span className="text-[#D4AF37]">{bd.event_surcharge_label || "Scheduled rate"} ({bd.event_surcharge_pct > 0 ? "+" : ""}{bd.event_surcharge_pct}%)</span>
          <span className="text-[#D4AF37] tabular-nums">{bd.event_surcharge < 0 ? "−" : "+"}${Math.abs(Number(bd.event_surcharge)).toFixed(2)}</span>
        </div>
      )}
      <BdLine label="Subtotal" amount={subtotal} />
      {couponDisc > 0 && (
        <div className="flex justify-between items-center text-sm" data-testid="coupon-discount-line">
          <span className="text-emerald-400">Coupon{bd.coupon_code ? ` · ${bd.coupon_code}` : ""}{bd.coupon_pct ? ` (${Number(bd.coupon_pct).toFixed(0)}% off)` : ""}</span>
          <span className="text-emerald-400 tabular-nums">−${couponDisc.toFixed(2)}</span>
        </div>
      )}
      {otherDisc > 0 && (
        <div className="flex justify-between items-center text-sm" data-testid="discount-line">
          <span className="text-emerald-400">Discount{bd.promo_code ? ` · ${bd.promo_code}` : ""}</span>
          <span className="text-emerald-400 tabular-nums">−${otherDisc.toFixed(2)}</span>
        </div>
      )}
      <BdLine label={`Gratuity (${Math.round((bd.tip_pct || 0) * 100)}%)`} amount={gratuity} />
      {airport > 0 && <BdLine label="Airport fee" amount={airport} />}
      {tolls > 0 && <BdLine label="Toll fee" amount={tolls} />}
      <BdLine
        label="Credit card fee (3.5%)"
        amount={cc}
        removable={cc > 0}
        disabled={busy}
        onRemove={() => setLine("credit_card_fee", 0, "Credit card fee")}
        onRestore={() => setLine("credit_card_fee", +((subtotal - couponDisc) * 0.035).toFixed(2), "Credit card fee")}
        restorable={cc === 0 && subtotal > 0}
      />
      <BdLine
        label="Flexible cancellation"
        amount={flex}
        removable={flex > 0}
        disabled={busy}
        onRemove={() => setLine("flexible_cancellation", 0, "Flexible cancellation")}
        onRestore={() => setLine("flexible_cancellation", 8, "Flexible cancellation")}
        restorable={flex === 0}
      />

      {/* Misc / custom charges */}
      {miscCharges.map((m, i) => (
        <BdLine
          key={`misc-${i}`}
          label={m.label}
          amount={Number(m.amount || 0)}
          removable
          disabled={busy}
          onRemove={() => removeMiscCharge(i)}
        />
      ))}

      {/* + Add charge / + Misc */}
      {!adding ? (
        <button
          data-testid="add-misc-charge-btn"
          onClick={() => setAdding(true)}
          disabled={busy}
          className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#E5C158] disabled:opacity-40 transition-colors mt-1"
        >
          + Add Charge / Misc
        </button>
      ) : (
        <div className="bg-black/30 border border-[#D4AF37]/30 p-3 mt-2 space-y-2" data-testid="add-misc-charge-form">
          <input
            type="text"
            data-testid="misc-charge-label"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder="Label (e.g. Meet & Greet · Stop · Discount)"
            className="w-full bg-transparent border-b border-white/20 text-sm text-[#F5F2EB] py-2 focus:outline-none focus:border-[#D4AF37]"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <span className="text-[#A1A1AA] text-sm">$</span>
            <input
              type="number"
              step="0.01"
              data-testid="misc-charge-amount"
              value={newAmount}
              onChange={e => setNewAmount(e.target.value)}
              placeholder="0.00  (use negative for discount)"
              className="flex-1 bg-transparent border-b border-white/20 text-sm text-[#F5F2EB] py-2 focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button
              data-testid="misc-charge-cancel"
              onClick={() => { setAdding(false); setNewLabel(""); setNewAmount(""); }}
              disabled={busy}
              className="text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white px-3 py-1 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="misc-charge-save"
              onClick={addMiscCharge}
              disabled={busy}
              className="text-[11px] uppercase tracking-[0.2em] bg-[#D4AF37] text-black px-4 py-1 hover:bg-[#E5C158] disabled:opacity-40 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      )}

      <BdLine label={`State sales tax (${(taxPct * 100).toFixed(1)}%)`} amount={tax} />
      {waitCharge > 0 && (
        <BdLine label={`Pickup wait · ${Math.round(bd.wait_billable_minutes || 0)} min × $${Number(bd.wait_rate_per_minute || 0).toFixed(2)}/min`} amount={waitCharge} />
      )}
      {priceAdjust !== 0 && (
        <div className="flex justify-between items-center text-sm" data-testid="price-adjustment-line">
          <span className="text-[#D4AF37]">Price adjust · match{booking.fare_adjusted_reason ? ` — ${booking.fare_adjusted_reason}` : ""}</span>
          <span className="text-[#D4AF37] tabular-nums">{priceAdjust < 0 ? "−" : "+"}${Math.abs(priceAdjust).toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between items-baseline pt-2 border-t border-white/10">
        <span className="overline">Total</span>
        <span className="font-serif text-3xl text-[#D4AF37]" data-testid="booking-total">${(booking.fare || 0).toFixed(2)}</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Pay later · Gratuity included</div>
    </div>
  );
}

function BdLine({ label, amount, removable, restorable, disabled, onRemove, onRestore }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <div className="flex items-center gap-2">
        <span className="text-[#A1A1AA]">{label}</span>
        {removable && (
          <button
            data-testid={`remove-line-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            disabled={disabled}
            onClick={onRemove}
            title={`Remove ${label}`}
            className="text-[10px] uppercase tracking-[0.18em] text-red-400/80 hover:text-red-300 disabled:opacity-40 transition-colors"
          >
            ✕ Remove
          </button>
        )}
        {restorable && (
          <button
            data-testid={`restore-line-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            disabled={disabled}
            onClick={onRestore}
            title={`Add ${label} back`}
            className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#E5C158] disabled:opacity-40 transition-colors"
          >
            + Add back
          </button>
        )}
      </div>
      <span className="text-[#F5F2EB] tabular-nums">${amount.toFixed(2)}</span>
    </div>
  );
}

function WaitFeePanel({ booking, onChange }) {
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [graceBusy, setGraceBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/bookings/${booking.booking_id}/wait-fee`);
      setSnap(r.data);
    } catch {}
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);   // refresh every 15s
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [booking.booking_id]);

  const setGrace = async (minutes) => {
    setGraceBusy(true);
    try {
      await api.patch(`/bookings/${booking.booking_id}/wait-grace`, { minutes });
      toast.success(minutes === null ? "Reverted to auto-detect" : `Grace set to ${minutes} min`);
      onChange();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set grace");
    } finally {
      setGraceBusy(false);
    }
  };

  const customGrace = async () => {
    const v = window.prompt("Custom grace (minutes, 0–240):", String(snap?.grace_minutes || 15));
    if (v === null) return;
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 0 || n > 240) {
      toast.error("Enter a number between 0 and 240");
      return;
    }
    await setGrace(n);
  };

  const bill = async () => {
    if (!window.confirm(`Bill $${snap.fee_amount.toFixed(2)} wait fee (${snap.billable_minutes} min @ $${snap.rate_per_min.toFixed(2)}/min)?\nCustomer will receive an SMS.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/wait-fee`);
      toast.success(`Wait fee $${r.data.wait_fee.toFixed(2)} added — new fare $${r.data.new_fare.toFixed(2)}`);
      onChange();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to bill wait fee");
    } finally {
      setBusy(false);
    }
  };

  if (!snap || !snap.applicable) return null;
  const mins = snap.waited_minutes;
  const inGrace = !snap.grace_expired;
  const isOverride = booking.wait_grace_override_minutes != null;
  const GRACE_PRESETS = [10, 30, 45, 60];
  return (
    <div className="border-t border-white/10 pt-6" data-testid="wait-fee-panel">
      <div className="overline mb-3">Wait time</div>
      <div className={`border ${inGrace ? "border-amber-500/30" : "border-red-500/30"} bg-[#0a0a0a] p-4 rounded`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-mono tabular-nums">
              {String(Math.floor(mins / 60)).padStart(2, "0")}:{String(mins % 60).padStart(2, "0")} <span className="text-xs text-[#71717A]">elapsed</span>
            </div>
            <div className="text-xs text-[#A1A1AA] mt-1" data-testid="grace-summary">{snap.category} · {snap.grace_minutes} min free · ${snap.rate_per_min.toFixed(2)}/min after</div>
          </div>
          <div className="text-right">
            {inGrace ? (
              <span className="text-amber-300 text-sm">In grace period</span>
            ) : (
              <>
                <div className="text-red-300 font-medium">{snap.billable_minutes} min billable</div>
                <div className="text-2xl font-serif text-[#D4AF37] mt-1">${snap.fee_amount.toFixed(2)}</div>
              </>
            )}
          </div>
        </div>

        {/* Per-trip grace override picker */}
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="grace-override">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
              Grace · {isOverride ? <span className="text-[#D4AF37]">manual</span> : <span>auto</span>}
            </span>
            {isOverride && (
              <button
                data-testid="grace-clear"
                disabled={graceBusy}
                onClick={() => setGrace(null)}
                className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#D4AF37] disabled:opacity-50">
                ← Revert to auto
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {GRACE_PRESETS.map(m => (
              <button
                key={m}
                data-testid={`grace-preset-${m}`}
                disabled={graceBusy}
                onClick={() => setGrace(m)}
                className={`text-[11px] px-2.5 py-1 border rounded transition ${
                  snap.grace_minutes === m
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                } disabled:opacity-50`}>
                {m} min
              </button>
            ))}
            <button
              data-testid="grace-custom"
              disabled={graceBusy}
              onClick={customGrace}
              className={`text-[11px] px-2.5 py-1 border rounded transition ${
                isOverride && !GRACE_PRESETS.includes(snap.grace_minutes)
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                  : "border-white/10 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
              } disabled:opacity-50`}>
              Custom…
            </button>
          </div>
        </div>

        {snap.already_billed && (
          <div className="text-xs text-[#71717A] mt-3 italic">
            Previously billed: ${snap.already_billed_amount.toFixed(2)} — clicking below will overwrite with the latest amount.
          </div>
        )}
        <button
          data-testid="bill-wait-fee-btn"
          disabled={inGrace || busy || snap.fee_amount <= 0}
          onClick={bill}
          className="btn-gold w-full mt-3 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Billing…" : inGrace ? `Free for ${snap.grace_minutes - mins} more min` : `Bill $${snap.fee_amount.toFixed(2)} wait fee`}
        </button>
      </div>
    </div>
  );
}


function BoardCard({ b, onOpen }) {
  const meta = STATUS_META[b.status] || STATUS_META.pending;
  const isPending = ["pending", "new"].includes(b.status);
  const [busy, setBusy] = React.useState(false);
  const autoAssign = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${b.booking_id}/auto-assign`);
      toast.success(`Assigned to ${r.data.driver.name}${r.data.matched_vehicle ? " (matched vehicle)" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-assign failed");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid={`board-card-${b.booking_id}`}
      className="w-full text-left bg-[#141414] border border-white/10 p-3 hover:border-[#D4AF37]/50 transition cursor-pointer"
      onClick={onOpen}
    >
      <div className="flex justify-between items-start mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{b.confirmation_code}</span>
        <span className="text-[10px]" style={{ color: meta.color }}>{meta.label}</span>
      </div>
      <div className="font-serif text-base text-[#F5F2EB] mb-1 truncate">{b.customer_name}</div>
      <div className="text-xs text-[#A1A1AA] mb-2 flex items-center gap-1.5 min-w-0">
        <span className="truncate">{b.vehicle_name}</span>
        <VehicleClassBadge vehicleId={b.vehicle_id} vehicleName={b.vehicle_name}/>
      </div>
      <div className="text-xs text-[#71717A] truncate">{b.pickup_address}</div>
      {b.dropoff_address && <div className="text-xs text-[#71717A] truncate">→ {b.dropoff_address}</div>}
      <div className="flex justify-between items-center mt-3 pt-2 border-t border-white/5">
        <span className="text-[10px] uppercase text-[#71717A]" title="Business timezone (America/Chicago)">{formatPickupShort(b.pickup_datetime)}</span>
        <span className="text-sm font-serif text-[#D4AF37]">${(b.fare || 0).toFixed(0)}</span>
      </div>
      {b.assigned_driver_name && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#10B981]">↳ {b.assigned_driver_name}</div>
      )}
      {b.flight_cancelled && (
        <div data-testid={`board-card-flight-cancelled-${b.booking_id}`}
          className="mt-2 inline-block text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/50 bg-red-500/10 px-1.5 py-0.5">
          ✈ Flight cancelled
        </div>
      )}
      <div className="flex gap-2 items-center justify-between mt-2">
        <Link
          to={`/dispatcher/reservation/${b.booking_id}`}
          data-testid={`board-card-deep-${b.booking_id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:underline"
        >
          Deep view →
        </Link>
        {isPending && (
          <button
            data-testid={`board-card-autoassign-${b.booking_id}`}
            onClick={autoAssign}
            disabled={busy}
            className="text-[10px] uppercase tracking-[0.18em] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-1 hover:bg-emerald-500/30 disabled:opacity-50"
            title="Assign to the first available driver"
          >
            {busy ? "…" : "⚡ Auto-assign"}
          </button>
        )}
      </div>
    </div>
  );
}

function BookingDetail({ booking, drivers, onClose, onChange }) {
  const [driverId, setDriverId] = useState(booking.assigned_driver_id || "");
  const [vehicles, setVehicles] = useState([]);
  const [deleting, setDeleting] = useState(false);
  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [editPickupDt, setEditPickupDt] = useState(booking.pickup_datetime || "");
  const [editPickup, setEditPickup] = useState(booking.pickup_address || "");
  const [editDropoff, setEditDropoff] = useState(booking.dropoff_address || "");
  const [editFlight, setEditFlight] = useState(booking.flight_number || "");
  const [editNotes, setEditNotes] = useState(booking.notes || "");
  const [editName, setEditName] = useState(booking.customer_name || "");
  const [editEmail, setEditEmail] = useState(booking.customer_email || "");
  const [editPhone, setEditPhone] = useState(booking.customer_phone || "");
  const [editVehicleId, setEditVehicleId] = useState(booking.vehicle_id || "");
  const [editPax, setEditPax] = useState(booking.pax || booking.passengers || "");
  const [editLuggage, setEditLuggage] = useState(booking.luggage || "");
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [savingEdit, setSavingEdit] = useState(false);
  // Light-mode toggle for night-time tablet readability — persisted per dispatcher
  const [lightMode, setLightMode] = useState(() => {
    try { return localStorage.getItem("dispatcher_modal_light") === "1"; } catch { return false; }
  });
  const toggleLight = () => {
    setLightMode(v => {
      const n = !v;
      try { localStorage.setItem("dispatcher_modal_light", n ? "1" : "0"); } catch {}
      return n;
    });
  };

  // Load vehicle list once for the dropdown
  useEffect(() => {
    api.get("/vehicles?staff=1").then(r => setVehicles(r.data || [])).catch(() => setVehicles([]));
  }, []);

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        pickup_datetime: editPickupDt,
        pickup_address: editPickup,
        dropoff_address: editDropoff,
        flight_number: editFlight || null,
        notes: editNotes,
        customer_name: editName,
        customer_email: editEmail || null,
        customer_phone: editPhone || null,
        vehicle_id: editVehicleId || null,
        pax: editPax ? Number(editPax) : null,
        luggage: editLuggage !== "" ? Number(editLuggage) : null,
        notify_customer: notifyCustomer,
      };
      const r = await api.patch(`/bookings/${booking.booking_id}/edit`, payload);
      if (r.data?.changed) {
        toast.success(notifyCustomer ? "Trip updated · customer notified" : "Trip updated");
      } else {
        toast("No changes detected");
      }
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteBooking = async () => {
    const code = booking.confirmation_code || booking.booking_id;
    const ok = window.confirm(
      `Permanently delete trip ${code}?\n\nThis removes the booking, driver locations, and chat history. Past SMS/email logs are kept for audit.\n\nThis cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/bookings/${booking.booking_id}`);
      toast.success(`Trip ${code} deleted`);
      onChange();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const assign = async () => {
    if (!driverId) return toast.error("Pick a driver");
    try {
      await api.patch(`/bookings/${booking.booking_id}/assign`, { driver_id: driverId });
      toast.success("Driver assigned");
      onChange(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/bookings/${booking.booking_id}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      onChange(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="booking-drawer" className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 overflow-y-auto">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}/>
      <div className={`relative w-full max-w-2xl max-h-[92vh] ${lightMode ? "dispatcher-modal-light bg-white" : "bg-[#0A0A0A]"} border border-white/10 rounded shadow-2xl overflow-y-auto`}>
        <div className={`p-6 border-b border-white/10 flex justify-between items-center sticky top-0 z-10 ${lightMode ? "bg-white" : "bg-[#0A0A0A]"}`}>
          <div>
            <div className="overline">{booking.confirmation_code}</div>
            <div className="font-serif text-2xl">{booking.customer_name}</div>
          </div>
          <div className="flex items-center gap-3">
            <a
              data-testid="modal-deep-view"
              href={`/dispatcher/reservation/${booking.booking_id}`}
              target="_blank"
              rel="noreferrer"
              title="Open full reservation view in a new tab — edit fares, view audit log, settle, refund, send receipts"
              className={`text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 ${lightMode ? "border-[#D4AF37] text-[#B8941F] hover:bg-[#D4AF37] hover:text-white" : "border-[#D4AF37]/60 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"} transition-colors`}
            >
              <ExternalLink size={11}/> Deep View
            </a>
            <button
              data-testid="modal-theme-toggle"
              onClick={toggleLight}
              title={lightMode ? "Switch to dark theme" : "Switch to light theme for tablet visibility"}
              className={`text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1.5 rounded ${lightMode ? "border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" : "border-white/20 text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"} transition-colors`}
            >
              {lightMode ? "☀ Light" : "☾ Dark"}
            </button>
            <button data-testid="drawer-close" onClick={onClose} className={lightMode ? "text-[#52525b] hover:text-[#18181b]" : "text-[#A1A1AA] hover:text-white"}><X size={20}/></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <div className="overline mb-2">Vehicle</div>
            <div className="font-serif text-xl text-[#F5F2EB] flex items-center gap-2">
              {booking.vehicle_name}
              <VehicleClassBadge vehicleId={booking.vehicle_id} vehicleName={booking.vehicle_name}/>
            </div>
            <div className="text-xs text-[#71717A]">{booking.trip_type === "point_to_point" ? (booking.origin_invoice_id ? "Invoiced trip · distance not tracked" : `${booking.distance_miles} mi · ${booking.duration_minutes} min`) : `${booking.hours} hr hourly`}</div>
          </div>
          <div>
            <div className="overline mb-2 flex justify-between items-center">
              Pickup
              {!editing && (
                <button
                  data-testid="edit-trip-btn"
                  onClick={() => setEditing(true)}
                  className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline"
                >Edit trip</button>
              )}
            </div>
            {!editing ? (
              <>
                <div className="text-sm">{new Date(booking.pickup_datetime).toLocaleString()}</div>
                <div className="text-sm text-[#F5F2EB] mt-1">{booking.pickup_address}</div>
                {booking.dropoff_address && <div className="text-sm text-[#F5F2EB] mt-1">→ {booking.dropoff_address}</div>}
                {booking.flight_number && (
                  <div className="text-xs text-[#D4AF37] mt-2">
                    ✈ Flight {booking.flight_number}
                    {booking.flight_cancelled && (
                      <span data-testid="drawer-flight-cancelled" className="ml-2 text-red-400 border border-red-500/50 bg-red-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em]">Cancelled</span>
                    )}
                    <a
                      href={`https://flightaware.com/live/flight/${booking.flight_number}`}
                      target="_blank" rel="noreferrer"
                      className="ml-2 underline"
                    >Track on FlightAware ↗</a>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2 mt-2 bg-[#0a0a0a] border border-white/10 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Customer name</label>
                    <input
                      data-testid="edit-customer-name"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Phone</label>
                    <input
                      data-testid="edit-customer-phone"
                      value={editPhone}
                      onChange={e => setEditPhone(e.target.value)}
                      className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Email</label>
                <input
                  data-testid="edit-customer-email"
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                />
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Vehicle</label>
                <select
                  data-testid="edit-vehicle"
                  value={editVehicleId}
                  onChange={e => setEditVehicleId(e.target.value)}
                  className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"
                >
                  <option value="">— Keep current ({booking.vehicle_name || "none"}) —</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>
                      {v.name}
                    </option>
                  ))}
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Passengers</label>
                    <input
                      type="number"
                      min="1"
                      data-testid="edit-pax"
                      value={editPax}
                      onChange={e => setEditPax(e.target.value)}
                      className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Luggage</label>
                    <input
                      type="number"
                      min="0"
                      data-testid="edit-luggage"
                      value={editLuggage}
                      onChange={e => setEditLuggage(e.target.value)}
                      className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Pickup date & time</label>
                <input
                  type="datetime-local"
                  data-testid="edit-pickup-dt"
                  value={editPickupDt.slice(0, 16)}
                  onChange={e => setEditPickupDt(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"
                />
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Pickup address</label>
                <input
                  data-testid="edit-pickup-addr"
                  value={editPickup}
                  onChange={e => setEditPickup(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                />
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Drop-off address</label>
                <input
                  data-testid="edit-dropoff-addr"
                  value={editDropoff}
                  onChange={e => setEditDropoff(e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                />
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Flight # (e.g. UA123)</label>
                <input
                  data-testid="edit-flight"
                  value={editFlight}
                  onChange={e => setEditFlight(e.target.value.toUpperCase())}
                  placeholder="Leave blank if none"
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                />
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Notes</label>
                <textarea
                  data-testid="edit-notes"
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"
                />
                <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    data-testid="edit-notify"
                    checked={notifyCustomer}
                    onChange={e => setNotifyCustomer(e.target.checked)}
                    className="accent-[#D4AF37]"
                  />
                  Notify customer of changes (SMS + email)
                </label>
                <div className="flex gap-2 pt-2">
                  <button
                    data-testid="edit-save"
                    onClick={saveEdit}
                    disabled={savingEdit}
                    className="btn-gold flex-1 justify-center"
                  >{savingEdit ? "Saving…" : "Save changes"}</button>
                  <button
                    data-testid="edit-cancel"
                    onClick={() => setEditing(false)}
                    className="btn-ghost"
                  >Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="overline mb-2">Contact</div>
            <div className="text-sm">{booking.customer_email}</div>
            <div className="text-sm">{booking.customer_phone}</div>
          </div>
          {booking.notes && (
            <div>
              <div className="overline mb-2">Notes</div>
              <div className="text-sm text-[#A1A1AA] italic">{booking.notes}</div>
            </div>
          )}
          <FareBreakdownPanel booking={booking} onChange={onChange} customerView={lightMode} />

          {/* Full payment panel — Card / Apple Pay / Google Pay / Card on File / Zelle / Venmo / Cash / Pay later.
              Dispatcher can take or record payment via any channel right here without leaving the modal. */}
          {booking.payment_status !== "paid" && (
            <div className="border-t border-white/10 pt-6">
              <div className="overline mb-3">Take Payment</div>
              <PaymentSelector
                booking={booking}
                dispatcherMode
                onPaid={(b) => { onChange(); }}
              />
            </div>
          )}
          {booking.payment_status === "balance_due" && (
            <div className="border-t border-white/10 pt-6" data-testid="board-balance-due">
              <div className="overline mb-2">Payment</div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-amber-300">Partially paid · Balance due ${((booking.balance_due_cents || 0) / 100).toFixed(2)}</span>
                <span className="text-xs text-[#A1A1AA]">Open the reservation → Charge Balance</span>
              </div>
            </div>
          )}
          {booking.payment_status === "paid" && (
            <div className="border-t border-white/10 pt-6">
              <div className="overline mb-2">Payment</div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">Paid</span>
                {booking.payment_method && (
                  <span className="text-xs text-[#A1A1AA]">via {booking.payment_method.replace("_", " ").toUpperCase()}</span>
                )}
                <button
                  data-testid="mark-unpaid"
                  onClick={async () => {
                    if (!window.confirm("Mark this booking back to unpaid?")) return;
                    try {
                      await api.post(`/payments/mark-unpaid`, { booking_id: booking.booking_id });
                      toast.success("Marked unpaid");
                      onChange();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                  }}
                  className="text-[10px] uppercase tracking-[0.18em] text-red-300 hover:underline ml-auto"
                >Mark unpaid</button>
              </div>
            </div>
          )}

          <div className="border-t border-white/10 pt-6">
            <div className="overline mb-3">Assign chauffeur</div>
            <div className="flex gap-2">
              <select data-testid="assign-driver-select" value={driverId} onChange={e => setDriverId(e.target.value)} className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm">
                <option value="">— Select driver —</option>
                {drivers.map(d => <option key={d.driver_id} value={d.driver_id}>{d.name} · {d.email}</option>)}
              </select>
              <button data-testid="assign-driver-btn" onClick={assign} className="btn-gold !py-2 !px-4 text-[11px]">Assign</button>
            </div>
            {booking.assigned_driver_name && (
              <div className="mt-2 text-[11px] text-emerald-300 flex items-center gap-2">
                <span>↳ {booking.assigned_driver_name}</span>
              </div>
            )}
            <DriverLinkBadge booking={booking} />
          </div>

          <div className="border-t border-white/10 pt-6">
            <div className="overline mb-3">Update status</div>
            <div className="grid grid-cols-2 gap-2">
              {(() => {
                // Show only canonical status buttons; skip current status; dedupe by display label
                const currentLabel = (STATUS_META[booking.status] || {}).label;
                const seen = new Set([currentLabel].filter(Boolean));
                return PRIMARY_STATUS_BUTTONS.flatMap(k => {
                  if (k === booking.status) return [];
                  const m = STATUS_META[k];
                  if (!m || seen.has(m.label)) return [];
                  seen.add(m.label);
                  return [(
                    <button key={k} data-testid={`status-btn-${k}`} onClick={() => setStatus(k)} className="px-3 py-2 text-xs uppercase tracking-[0.18em] border border-white/10 hover:border-[#D4AF37]/60 transition">
                      {m.label}
                    </button>
                  )];
                });
              })()}
            </div>
          </div>

          {/* Trip chat — read-only / participating as dispatcher when trip is active */}
          {["assigned", "en_route", "in_progress"].includes(booking.status) && (
            <div className="border-t border-white/10 pt-6">
              <div className="overline mb-3">Trip chat</div>
              <TripChat bookingId={booking.booking_id} viewerHint="You · Dispatcher" compact/>
            </div>
          )}

          {/* Wait-fee panel — visible whenever driver has tapped 'On Location' */}
          {booking.on_location_at && booking.status !== "completed" && (
            <WaitFeePanel booking={booking} onChange={onChange}/>
          )}

          {/* Danger zone — permanently delete the trip */}
          <div className="border-t border-red-500/20 pt-6 mt-4">
            <div className="overline text-red-400/80 mb-2">Danger zone</div>
            <p className="text-xs text-[#71717A] mb-3">
              Permanently remove this booking. Past SMS/email logs are preserved for audit; the trip record itself, driver locations, and chat history are deleted. This cannot be undone.
            </p>
            <button
              data-testid="delete-booking-btn"
              onClick={deleteBooking}
              disabled={deleting}
              className="w-full px-4 py-2.5 border border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors text-xs uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? "Deleting…" : `Delete trip ${booking.confirmation_code || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddDriverModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", email: "", phone: "", license_no: "", license_expiry: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_color: "",
    license_plate: "",
    insurance_provider: "", insurance_policy_no: "", insurance_expiry: "",
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.name || !f.email || !f.phone) return toast.error("Name, email and phone are required");
    setBusy(true);
    try {
      await api.post("/drivers", {
        ...f,
        vehicle_year: f.vehicle_year ? Number(f.vehicle_year) : null,
      });
      toast.success(`Driver ${f.name} added`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6 overflow-y-auto py-10">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}/>
      <div data-testid="add-driver-modal" className="relative w-full max-w-2xl bg-[#0A0A0A] border border-white/10 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="overline">Add chauffeur</div>
            <div className="font-serif text-2xl">New driver</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA]"><X size={18}/></button>
        </div>
        <div className="space-y-6">
          <div>
            <div className="overline mb-3">Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name"><input data-testid="new-driver-name" className="field-input" value={f.name} onChange={e => upd("name", e.target.value)}/></Field>
              <Field label="Email (must match their Google sign-in)"><input data-testid="new-driver-email" className="field-input" type="email" value={f.email} onChange={e => upd("email", e.target.value)}/></Field>
              <Field label="Phone"><input data-testid="new-driver-phone" className="field-input" value={f.phone} onChange={e => upd("phone", e.target.value)}/></Field>
              <Field label="License #"><input data-testid="new-driver-license" className="field-input" value={f.license_no} onChange={e => upd("license_no", e.target.value)}/></Field>
              <Field label="License expiry"><input data-testid="new-driver-license-expiry" type="date" className="field-input" value={f.license_expiry} onChange={e => upd("license_expiry", e.target.value)}/></Field>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Vehicle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Make"><input data-testid="new-driver-vmake" className="field-input" placeholder="Mercedes" value={f.vehicle_make} onChange={e => upd("vehicle_make", e.target.value)}/></Field>
              <Field label="Model"><input data-testid="new-driver-vmodel" className="field-input" placeholder="S-Class" value={f.vehicle_model} onChange={e => upd("vehicle_model", e.target.value)}/></Field>
              <Field label="Year"><input data-testid="new-driver-vyear" type="number" className="field-input" placeholder="2023" value={f.vehicle_year} onChange={e => upd("vehicle_year", e.target.value)}/></Field>
              <Field label="Color"><input data-testid="new-driver-vcolor" className="field-input" placeholder="Black" value={f.vehicle_color} onChange={e => upd("vehicle_color", e.target.value)}/></Field>
              <Field label="License plate"><input data-testid="new-driver-plate" className="field-input uppercase" placeholder="ABC-1234" value={f.license_plate} onChange={e => upd("license_plate", e.target.value)}/></Field>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Insurance</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Provider"><input data-testid="new-driver-ins-prov" className="field-input" placeholder="Progressive" value={f.insurance_provider} onChange={e => upd("insurance_provider", e.target.value)}/></Field>
              <Field label="Policy #"><input data-testid="new-driver-ins-policy" className="field-input" value={f.insurance_policy_no} onChange={e => upd("insurance_policy_no", e.target.value)}/></Field>
              <Field label="Expiry"><input data-testid="new-driver-ins-expiry" type="date" className="field-input" value={f.insurance_expiry} onChange={e => upd("insurance_expiry", e.target.value)}/></Field>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost !py-2.5 !px-4 text-[11px]">Cancel</button>
          <button data-testid="save-driver-btn" onClick={submit} disabled={busy} className="btn-gold !py-2.5 !px-4 text-[11px]">{busy ? "Saving…" : "Save driver"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><div className="field-label">{label}</div>{children}</div>);
}

async function setRole(user_id, role, refresh) {
  try {
    await api.post("/admin/role", { user_id, role });
    toast.success(`Role updated to ${role}`);
    refresh();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed");
  }
}

async function deleteDriver(driver_id, refresh) {
  if (!window.confirm("Remove this driver?")) return;
  try {
    await api.delete(`/drivers/${driver_id}`);
    toast.success("Driver removed");
    refresh();
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed");
  }
}

function ManualInvoiceModal({ vehicles, onClose, onCreated }) {
  const [f, setF] = React.useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_address: "",
    dropoff_address: "",
    pickup_datetime: "",
    vehicle_id: vehicles?.[0]?.vehicle_id || "",
    distance_miles: 0,
    trip_type: "point_to_point",
    hours: 1,
    pax: 1, luggage: 1,
    base: "",
    distance_cost: "",
    tolls: 0,
    airport_fee: 0,
    other_charges: 0,
    other_charges_label: "",
    tip_amount: 0,
    tax_pct: 0.055,
    notes: "",
    mark_paid: true,
    payment_method: "cash",
    paid_date: "",
    payment_due_date: "",
    total_override: "",
    coupon_code: "",
  });
  const [busy, setBusy] = React.useState(false);
  const [calcing, setCalcing] = React.useState(false);
  const [created, setCreated] = React.useState(null);

  // Auto-calculate distance + base + distance_cost from pickup/dropoff using Google Routes API
  const calcDistance = async () => {
    if (!f.pickup_address || !f.dropoff_address) {
      toast.error("Enter pickup and drop-off addresses first");
      return;
    }
    setCalcing(true);
    try {
      const r = await api.post("/quote", {
        trip_type: "point_to_point",
        pickup_address: f.pickup_address,
        dropoff_address: f.dropoff_address,
        pax: f.pax, luggage: f.luggage,
        tip_pct: 0,
      });
      const vehQuote = r.data.vehicles.find(v => v.vehicle_id === f.vehicle_id);
      if (vehQuote) {
        setF(p => ({
          ...p,
          distance_miles: r.data.distance_miles,
          base: vehQuote.breakdown.base,
          distance_cost: vehQuote.breakdown.distance_cost,
          tolls: r.data.tolls_usd || 0,
          airport_fee: vehQuote.breakdown.airport_fee,
        }));
        toast.success(`Distance: ${r.data.distance_miles} mi · auto-filled charges`);
      } else {
        toast.error("Could not match vehicle in quote");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Distance calc failed");
    } finally {
      setCalcing(false);
    }
  };
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Auto-fill base + distance_cost when vehicle/distance changes (advisory; user can override)
  const veh = vehicles?.find(v => v.vehicle_id === f.vehicle_id);
  const autoBase = veh?.base_rate || 0;
  const autoDist = +(Number(f.distance_miles || 0) * Number(veh?.per_mile || 0)).toFixed(2);

  // Live total — mirrors POST /bookings/manual math so the dispatcher sees
  // the invoice total update as they type (coupon % is applied server-side).
  const liveEst = React.useMemo(() => {
    let subtotal;
    if (f.base !== "" && f.distance_cost !== "") {
      subtotal = (Number(f.base) || 0) + (Number(f.distance_cost) || 0);
    } else if (f.trip_type === "hourly") {
      subtotal = (Number(veh?.hourly_rate) || 0) * Math.max(Number(f.hours || 0), Number(veh?.hourly_min || 0));
    } else {
      const dc = Math.ceil(Number(f.distance_miles || 0)) * (Number(veh?.per_mile) || 0);
      subtotal = Math.max(dc, Number(veh?.min_fare) || 0);
    }
    subtotal = Math.round(subtotal * 100) / 100;
    const fees = (Number(f.tolls) || 0) + (Number(f.airport_fee) || 0) + (Number(f.other_charges) || 0);
    const tax = Math.round((subtotal + fees) * (Number(f.tax_pct) || 0) * 100) / 100;
    const tip = Number(f.tip_amount) || 0;
    const calc = Math.round((subtotal + fees + tax + tip) * 100) / 100;
    const override = f.total_override !== "" && !isNaN(Number(f.total_override)) && Number(f.total_override) > 0;
    return { subtotal, fees, tax, tip, calc, override, total: override ? Number(f.total_override) : calc };
  }, [f, veh]);

  const submit = async () => {
    if (!f.customer_name || !f.pickup_address || !f.pickup_datetime || !f.vehicle_id) {
      toast.error("Fill required fields (name, pickup, datetime, vehicle)");
      return;
    }
    if (!f.customer_email && !f.customer_phone) {
      toast.error("Add a customer email, a phone, or both — at least one is required.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...f,
        distance_miles: Number(f.distance_miles || 0),
        tolls: Number(f.tolls || 0),
        airport_fee: Number(f.airport_fee || 0),
        other_charges: Number(f.other_charges || 0),
        tip_amount: Number(f.tip_amount || 0),
        tax_pct: Number(f.tax_pct || 0),
        hours: Number(f.hours || 0) || null,
        base: f.base === "" ? null : Number(f.base),
        distance_cost: f.distance_cost === "" ? null : Number(f.distance_cost),
        total_override: f.total_override === "" ? null : Number(f.total_override),
      };
      const r = await api.post("/bookings/manual", body);
      setCreated(r.data);
      toast.success(`Invoice ${r.data.invoice_number} created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-0 manual-invoice-light" data-testid="manual-invoice-overlay" onClick={onClose}>
      <div
        className="bg-white text-neutral-900 border border-neutral-300 shadow-2xl max-w-3xl w-full mx-auto p-6 my-6 sm:my-8 md:my-12 rounded"
        data-testid="manual-invoice-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white -mx-6 -mt-6 px-6 pt-6 pb-3 border-b border-neutral-200 z-10">
          <div>
            <div className="overline !text-neutral-500">Custom invoice</div>
            <h2 className="font-serif text-2xl text-neutral-900">Backdated / past trip</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none" data-testid="manual-invoice-close">×</button>
        </div>
        {created ? (
          <div className="space-y-4" data-testid="manual-success">
            <div className="border border-[#D4AF37]/60 bg-[#D4AF37]/10 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#A8841A] mb-2">✓ Invoice created</div>
              <div className="text-xl text-neutral-900 font-serif">{created.invoice_number}</div>
              <div className="text-sm text-neutral-600 mt-1">
                Confirmation: <span className="text-neutral-900 font-medium">{created.confirmation_code}</span>
                {" · "}Total: <span className="text-[#A8841A] font-semibold">${created.fare.toFixed(2)}</span>
              </div>
              {created.payment_status === "paid" && (
                <div className="text-xs text-emerald-700 mt-2 uppercase tracking-[0.18em]">
                  Marked paid · {(created.payment_method || "—").replace(/_/g, " ")}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                data-testid="created-trip-pdf"
                href={`${API}/bookings/${created.confirmation_code}/trip-sheet.pdf?contact=${encodeURIComponent(created.customer_email || created.customer_phone || "")}&detailed=true`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-neutral-900 font-semibold text-xs uppercase tracking-[0.18em] px-4 py-2.5 hover:bg-[#C29B2E]"
              >📄 Trip Sheet PDF</a>
              {created.payment_status === "paid" && (
                <a
                  data-testid="created-receipt-pdf"
                  href={`${API}/bookings/${created.confirmation_code}/receipt.pdf?contact=${encodeURIComponent(created.customer_email || created.customer_phone || "")}&detailed=true`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-neutral-900 font-semibold text-xs uppercase tracking-[0.18em] px-4 py-2.5 hover:bg-[#C29B2E]"
                >🧾 Payment Receipt PDF</a>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 pt-2">
              Click any PDF to view/print. Customer can also access via /lookup using confirmation code + email.
            </div>
            <div className="flex gap-3 pt-4 border-t border-neutral-200">
              <button
                data-testid="manual-done"
                onClick={() => { onCreated(created); }}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-neutral-300 text-neutral-800 text-xs uppercase tracking-[0.18em] px-4 py-2.5 hover:bg-neutral-100"
              >Done</button>
              <button
                data-testid="manual-create-another"
                onClick={() => {
                  setCreated(null);
                  setF({...f, customer_name:"", customer_email:"", customer_phone:"", pickup_address:"", dropoff_address:"", pickup_datetime:"", notes:"", distance_miles:0, base:"", distance_cost:"", tip_amount:0, paid_date:"", payment_due_date:""});
                }}
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-neutral-900 font-semibold text-xs uppercase tracking-[0.18em] px-4 py-2.5 hover:bg-[#C29B2E]"
              >+ Create another</button>
            </div>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld k="Customer Name *" v={f.customer_name} on={v=>upd("customer_name",v)} t="manual-name"/>
          <Fld k="Customer Email" v={f.customer_email} on={v=>upd("customer_email",v)} t="manual-email" placeholder="email or phone required"/>
          <Fld k="Customer Phone" v={f.customer_phone} on={v=>upd("customer_phone",v)} t="manual-phone" placeholder="email or phone required"/>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Pickup date & time *</label>
            <input type="datetime-local" data-testid="manual-pickup-dt" value={f.pickup_datetime} onChange={e=>upd("pickup_datetime", e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Pickup Address *</label>
            <PlacesInput
              testId="manual-pickup"
              value={f.pickup_address}
              onChange={v => upd("pickup_address", v)}
              placeholder="MKE Airport, hotel, or address"
              className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Drop-off Address</label>
            <PlacesInput
              testId="manual-dropoff"
              value={f.dropoff_address}
              onChange={v => upd("dropoff_address", v)}
              placeholder="Destination"
              className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Vehicle *</label>
            <select data-testid="manual-vehicle" value={f.vehicle_id} onChange={e=>upd("vehicle_id", e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]">
              {vehicles.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Distance (miles)</label>
            <div className="flex gap-2">
              <input
                type="number"
                data-testid="manual-distance"
                value={f.distance_miles}
                onChange={e => upd("distance_miles", e.target.value)}
                className="flex-1 bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
              />
              <button
                type="button"
                data-testid="manual-calc-distance"
                onClick={calcDistance}
                disabled={calcing}
                className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50 whitespace-nowrap"
              >{calcing ? "…" : "Auto-calc"}</button>
            </div>
          </div>
          <Fld k={`Base ($) — auto: $${autoBase}`} v={f.base} on={v=>upd("base",v)} t="manual-base" placeholder={`${autoBase}`} type="number"/>
          <Fld k={`Distance cost ($) — auto: $${autoDist}`} v={f.distance_cost} on={v=>upd("distance_cost",v)} t="manual-distcost" placeholder={`${autoDist}`} type="number"/>
          <Fld k="Tolls ($)" v={f.tolls} on={v=>upd("tolls",v)} t="manual-tolls" type="number"/>
          <Fld k="Airport fee ($)" v={f.airport_fee} on={v=>upd("airport_fee",v)} t="manual-airport" type="number"/>
          <Fld k="Other charges ($)" v={f.other_charges} on={v=>upd("other_charges",v)} t="manual-other" type="number"/>
          <Fld k="Other charges label" v={f.other_charges_label} on={v=>upd("other_charges_label",v)} t="manual-other-label" placeholder="e.g. Waiting time"/>
          <Fld k="Tip ($)" v={f.tip_amount} on={v=>upd("tip_amount",v)} t="manual-tip" type="number"/>
          <Fld k="Tax %" v={f.tax_pct * 100} on={v=>upd("tax_pct", Number(v)/100)} t="manual-tax" type="number" placeholder="5.5"/>
          <div className="md:col-span-2 border-t border-white/10 pt-3 mt-1">
            <Fld
              k="💰 Custom Total Override (skips line-item math)"
              v={f.total_override}
              on={v=>upd("total_override",v)}
              t="manual-total-override"
              type="number"
              placeholder="Leave blank to auto-calculate"
            />
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#71717A] mt-1">
              For past trips where you just want to enter the final amount you charged.
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment method</label>
            <select data-testid="manual-paymethod" value={f.payment_method} onChange={e=>upd("payment_method", e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              <option value="cash">Cash</option><option value="card">Card</option>
              <option value="zelle">Zelle</option><option value="venmo">Venmo</option>
              <option value="check">Check</option><option value="other">Other</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer pt-6">
            <input type="checkbox" data-testid="manual-paid" checked={f.mark_paid} onChange={e=>upd("mark_paid", e.target.checked)} className="accent-[#D4AF37]"/>
            Mark as paid
          </label>
          {f.mark_paid && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Amount paid date</label>
              <input type="date" data-testid="manual-paid-date" value={f.paid_date} onChange={e=>upd("paid_date", e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
            </div>
          )}
          {!f.mark_paid && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment due date</label>
              <input type="date" data-testid="manual-due-date" value={f.payment_due_date} onChange={e=>upd("payment_due_date", e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
            </div>
          )}
        </div>
        <div className="md:col-span-2 mt-3">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Notes</label>
          <textarea data-testid="manual-notes" value={f.notes} onChange={e=>upd("notes", e.target.value)} rows={2} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm"/>
        </div>
        <div className="md:col-span-2 mt-3 border-t border-white/5 pt-3">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Coupon / promo code</label>
          <CouponPicker
            customer_email={f.customer_email}
            value={f.coupon_code || ""}
            onChange={(code) => upd("coupon_code", code)}
            dataTestidPrefix="manual-coupon"
          />
        </div>
        {/* Live total — updates as prices are typed */}
        <div className="mt-5 border border-[#D4AF37]/60 bg-[#D4AF37]/10 p-4 rounded" data-testid="manual-live-total">
          <div className="grid grid-cols-4 gap-2 text-center mb-3">
            {[["Subtotal", liveEst.subtotal], ["Fees", liveEst.fees], [`Tax ${(Number(f.tax_pct) * 100).toFixed(1)}%`, liveEst.tax], ["Tip", liveEst.tip]].map(([k, v]) => (
              <div key={k}>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">{k}</div>
                <div className="text-sm text-[#F5F2EB] mt-0.5">${Number(v).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-baseline border-t border-[#D4AF37]/40 pt-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">Invoice total</span>
            <span className="font-serif text-3xl text-[#D4AF37]" data-testid="manual-total-display">${liveEst.total.toFixed(2)}</span>
          </div>
          {liveEst.override && (
            <div className="text-[10px] uppercase tracking-[0.16em] text-amber-400 mt-1 text-right">Custom override active (auto-calc was ${liveEst.calc.toFixed(2)})</div>
          )}
          {f.coupon_code && (
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#71717A] mt-1 text-right">Coupon {f.coupon_code} will be applied at creation</div>
          )}
        </div>

        <div className="flex gap-3 mt-4 pt-4 border-t border-white/10">
          <button data-testid="manual-submit" onClick={submit} disabled={busy} className="btn-gold flex-1 justify-center">{busy ? "Creating…" : `Create invoice · $${liveEst.total.toFixed(2)}`}</button>
          <button data-testid="manual-cancel" onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-3">
          PDF Trip Sheet + Receipt will be downloadable immediately after creation from /lookup or by code.
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function Fld({ k, v, on, t, type="text", placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{k}</label>
      <input
        type={type}
        data-testid={t}
        value={v}
        onChange={e => on(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
      />
    </div>
  );
}

// =============================================================
// DISPATCHER · NEW RESERVATION MODAL
// For taking phone/text bookings on behalf of a customer. Creates a
// real customer booking (not an invoice) so all the normal SMS/email
// confirmation + reminder flows fire automatically.
// =============================================================
function DispatcherNewBookingModal({ vehicles, onClose, onCreated }) {
  const isoLocal = () => {
    const d = new Date(Date.now() + 60 * 60 * 1000); // default: 1 hour from now
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const [f, setF] = React.useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    trip_type: "point_to_point",
    pickup_address: "",
    dropoff_address: "",
    stops: [],
    pickup_datetime: isoLocal(),
    hours: 3,
    pax: 1, luggage: 1,
    vehicle_id: vehicles?.[0]?.vehicle_id || "",
    flight_number: "",
    notes: "",
    guest_name: "",
    guest_phone: "",
    guest_count: "",
    notify_customer: true,
    save_to_address_book: true,
    tip_pct: 0.20,
  });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  // Stop helpers — keep the logic centralised because we use them in 4 places
  // (add, set value, remove, reorder up/down).
  const addStop = () => setF(p => p.stops.length >= 4 ? p : ({ ...p, stops: [...p.stops, ""] }));
  const setStop = (i, v) => setF(p => ({ ...p, stops: p.stops.map((s, j) => j === i ? v : s) }));
  const removeStop = (i) => setF(p => ({ ...p, stops: p.stops.filter((_, j) => j !== i) }));
  const moveStop = (i, dir) => setF(p => {
    const arr = [...p.stops];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...p, stops: arr };
  });
  const [quote, setQuote] = React.useState(null);
  const [quoting, setQuoting] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Customer typeahead state
  const [custMatches, setCustMatches] = React.useState([]);
  const [custOpen, setCustOpen] = React.useState(false);

  // Debounced typeahead — fires while the dispatcher types a name/phone/email.
  React.useEffect(() => {
    const q = f.customer_name.trim();
    if (q.length < 2) { setCustMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/customers/search`, { params: { q, limit: 8 } });
        setCustMatches(r.data || []);
      } catch { setCustMatches([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [f.customer_name]);

  const pickCustomer = (c) => {
    setF(p => ({
      ...p,
      customer_name: c.name || p.customer_name,
      customer_email: displayEmail(c.email) || p.customer_email,
      customer_phone: c.phone || p.customer_phone,
    }));
    setCustMatches([]);
    setCustOpen(false);
  };

  // Auto-quote whenever both addresses + vehicle are set
  React.useEffect(() => {
    const t = setTimeout(async () => {
      if (!f.pickup_address || !f.vehicle_id) { setQuote(null); return; }
      if (f.trip_type === "point_to_point" && !f.dropoff_address) { setQuote(null); return; }
      const cleanStops = f.stops.map(s => (s || "").trim()).filter(Boolean);
      setQuoting(true);
      try {
        const useMulti = cleanStops.length > 0 && f.trip_type === "point_to_point";
        const r = await api.post(useMulti ? "/quote/multistop" : "/quote", {
          trip_type: f.trip_type,
          pickup_address: f.pickup_address,
          dropoff_address: f.trip_type === "point_to_point" ? f.dropoff_address : null,
          hours: f.trip_type === "hourly" ? Number(f.hours) : null,
          pax: Number(f.pax), luggage: Number(f.luggage),
          tip_pct: Number(f.tip_pct) || 0,
          pickup_datetime: f.pickup_datetime || null,
          ...(useMulti ? { stops: cleanStops } : {}),
        });
        const v = r.data.vehicles.find(x => x.vehicle_id === f.vehicle_id) || r.data.vehicles[0];
        setQuote({
          total: v?.fare ?? v?.total ?? 0,
          distance: r.data.distance_miles,
          duration: r.data.duration_minutes,
          vehicle_name: v?.name,
          vehicles: r.data.vehicles || [],
        });
      } catch { setQuote(null); }
      finally { setQuoting(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [f.pickup_address, f.dropoff_address, f.trip_type, f.vehicle_id, f.hours, f.pax, f.luggage, f.tip_pct, f.stops]);

  const submit = async () => {
    // Validation — keep it tight so dispatchers can't accidentally create
    // a booking with no way to reach the customer.
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!f.customer_email.trim() && !f.customer_phone.trim()) {
      toast.error("Add a customer email, a phone, or both — at least one is required to send the confirmation");
      return;
    }
    if (f.customer_email && (!f.customer_email.includes("@") || !f.customer_email.split("@")[1]?.includes("."))) {
      toast.error("That email looks invalid — fix it or leave it blank");
      return;
    }
    if (!f.pickup_address.trim()) { toast.error("Pickup address is required"); return; }
    if (f.trip_type === "point_to_point" && !f.dropoff_address.trim()) {
      toast.error("Drop-off address is required for point-to-point trips");
      return;
    }
    if (!f.pickup_datetime) { toast.error("Pickup date & time is required"); return; }
    if (!f.vehicle_id) { toast.error("Choose a vehicle"); return; }

    setBusy(true);
    try {
      const body = {
        customer_name: f.customer_name.trim(),
        customer_email: f.customer_email.trim() || null,
        customer_phone: f.customer_phone.trim() || null,
        trip_type: f.trip_type,
        pickup_address: f.pickup_address.trim(),
        dropoff_address: f.trip_type === "point_to_point" ? f.dropoff_address.trim() : null,
        pickup_datetime: f.pickup_datetime,
        hours: f.trip_type === "hourly" ? Number(f.hours) : null,
        pax: Number(f.pax),
        luggage: Number(f.luggage),
        vehicle_id: f.vehicle_id,
        flight_number: f.flight_number.trim() || null,
        notes: f.notes.trim() || null,
        guest_name: f.guest_name.trim() || null,
        guest_phone: f.guest_phone.trim() || null,
        guest_count: Number(f.guest_count) || null,
        tip_pct: Number(f.tip_pct) || 0,
        cancellation_protection: true,
        stops: f.stops.map(s => (s || "").trim()).filter(Boolean),
        notify_customer: f.notify_customer,
      };
      const r = await api.post("/bookings", body);
      // Save the customer to the dispatcher address book so the next time
      // their name is typed, the email/phone autofill instantly.
      if (f.save_to_address_book) {
        api.post("/admin/customers/upsert", {
          name: f.customer_name.trim(),
          email: f.customer_email.trim(),
          phone: f.customer_phone.trim(),
        }).catch(() => { /* non-blocking; booking already created */ });
      }
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create reservation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto" data-testid="new-reservation-modal" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 w-full max-w-4xl my-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">New Reservation</div>
            <div className="font-serif text-xl text-[#F5F2EB]">Book a trip for a customer</div>
          </div>
          <button data-testid="nr-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white text-xl px-2">×</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Customer</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Name field with typeahead — start typing and the address book
                  surfaces matches (by name, email, or phone). Pick one and the
                  other two fields auto-fill. */}
              <div className="relative">
                <Fld
                  k="Full name *"
                  v={f.customer_name}
                  on={v => { upd("customer_name", v); setCustOpen(true); }}
                  t="nr-name"
                  placeholder="Start typing — autofills from address book"
                />
                {custOpen && custMatches.length > 0 && (
                  <div
                    data-testid="nr-cust-suggestions"
                    className="absolute z-30 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#D4AF37]/40 shadow-2xl max-h-64 overflow-y-auto"
                  >
                    {custMatches.map(c => (
                      <button
                        key={c.user_id}
                        data-testid={`nr-cust-pick-${c.user_id}`}
                        type="button"
                        onClick={() => pickCustomer(c)}
                        className="w-full text-left px-3 py-2 hover:bg-[#D4AF37]/10 border-b border-white/5 last:border-b-0 transition-colors"
                      >
                        <div className="text-sm text-[#F5F2EB]">{c.name}</div>
                        <div className="text-[10px] text-[#A1A1AA] mt-0.5 truncate">
                          {[displayEmail(c.email), c.phone].filter(Boolean).join(" · ") || "no contact saved"}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Fld k="Email" v={f.customer_email} on={v=>upd("customer_email",v)} t="nr-email" placeholder="joseph@email.com"/>
              <Fld k="Phone" v={f.customer_phone} on={v=>upd("customer_phone",v)} t="nr-phone" placeholder="+1 414 555 1234"/>
            </div>
            <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
              <div className="text-[10px] text-[#71717A]">At least one of email or phone is required so we can send the confirmation.</div>
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] cursor-pointer select-none" data-testid="nr-save-address-book-label">
                <input
                  data-testid="nr-save-address-book"
                  type="checkbox"
                  checked={f.save_to_address_book}
                  onChange={e => upd("save_to_address_book", e.target.checked)}
                  className="accent-[#D4AF37]"
                />
                Save customer to address book
              </label>
            </div>
            {/* Guest rider info — booker ≠ passenger (shown on tracking + driver pages) */}
            <div className="mt-3 border-t border-white/10 pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
                Guest rider info <span className="text-[#71717A] normal-case tracking-normal">(optional — if the passenger isn't the booker)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Fld k="Guest name(s)" v={f.guest_name} on={v=>upd("guest_name",v)} t="nr-guest-name" placeholder="e.g. Amber and Sara"/>
                <Fld k="Guest phone" v={f.guest_phone} on={v=>upd("guest_phone",v)} t="nr-guest-phone" placeholder="+1 210 555 1234"/>
                <Fld k="Number of guests" v={f.guest_count} on={v=>upd("guest_count",v)} t="nr-guest-count" placeholder="2"/>
              </div>
            </div>
          </div>

          {/* Trip type + vehicle */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Trip</div>
            <div className="flex gap-2 mb-3">
              <button
                data-testid="nr-tt-p2p"
                onClick={() => upd("trip_type", "point_to_point")}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${f.trip_type === "point_to_point" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold shadow-[0_0_12px_rgba(212,175,55,0.4)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
              >Point-to-Point</button>
              <button
                data-testid="nr-tt-hourly"
                onClick={() => upd("trip_type", "hourly")}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${f.trip_type === "hourly" ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold shadow-[0_0_12px_rgba(212,175,55,0.4)]" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
              >Hourly Charter</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Pickup address *</label>
                <AddressFavorites kind="pickup" currentValue={f.pickup_address} onPick={a => upd("pickup_address", a)}/>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 min-w-0"><PlacesInput data-testid="nr-pickup" value={f.pickup_address} onChange={v => upd("pickup_address", v)} placeholder="Pickup address"/></div>
                  <UseLocationButton compact testId="nr-use-current-location" onResolve={v => upd("pickup_address", v)}/>
                </div>
              </div>
              {f.trip_type === "point_to_point" ? (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Drop-off address *</label>
                  <AddressFavorites kind="dropoff" currentValue={f.dropoff_address} onPick={a => upd("dropoff_address", a)}/>
                  <PlacesInput data-testid="nr-dropoff" value={f.dropoff_address} onChange={v => upd("dropoff_address", v)} placeholder="Destination"/>
                </div>
              ) : (
                <Fld k="Hours *" type="number" v={f.hours} on={v => upd("hours", v)} t="nr-hours" placeholder="3"/>
              )}
            </div>

            {/* Multi-stop editor — point-to-point only. Each stop adds $15 base
                + 5 miles to the fare. Stops can be reordered up/down to match
                the actual ride sequence. */}
            {f.trip_type === "point_to_point" && (
              <div className="mt-3">
                {f.stops.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {f.stops.map((s, i) => (
                      <div key={i} className="flex items-end gap-2" data-testid={`nr-stop-row-${i}`}>
                        <div className="flex-1">
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">
                            Stop {i + 1} <span className="text-[#D4AF37]/70 normal-case tracking-normal">(+$15 · +5 mi)</span>
                          </label>
                          <PlacesInput
                            data-testid={`nr-stop-${i}`}
                            value={s}
                            onChange={v => setStop(i, v)}
                            placeholder="Intermediate address"
                          />
                        </div>
                        <div className="flex items-center gap-1 pb-1">
                          <button
                            type="button"
                            data-testid={`nr-stop-up-${i}`}
                            onClick={() => moveStop(i, -1)}
                            disabled={i === 0}
                            title="Move up"
                            className="px-2 py-1.5 border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >↑</button>
                          <button
                            type="button"
                            data-testid={`nr-stop-down-${i}`}
                            onClick={() => moveStop(i, 1)}
                            disabled={i === f.stops.length - 1}
                            title="Move down"
                            className="px-2 py-1.5 border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >↓</button>
                          <button
                            type="button"
                            data-testid={`nr-stop-remove-${i}`}
                            onClick={() => removeStop(i)}
                            title="Remove stop"
                            className="px-2 py-1.5 border border-white/10 text-[#A1A1AA] hover:text-red-400 hover:border-red-400/60 transition-colors"
                          >×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {f.stops.length < 4 && (
                  <button
                    type="button"
                    data-testid="nr-add-stop"
                    onClick={addStop}
                    className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#C29B2E] transition-colors"
                  >+ Add a stop {f.stops.length > 0 && <span className="text-[#71717A] normal-case tracking-normal">({f.stops.length}/4)</span>}</button>
                )}

                {/* Live route preview — auto-redraws when any address or stop
                    order changes. Helps the dispatcher spot a misrouted trip
                    (e.g. stops out of geographical order) before saving. */}
                <RoutePreviewMap
                  pickup={f.pickup_address}
                  stops={f.stops}
                  dropoff={f.dropoff_address}
                />
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
              <Fld k="Pickup date & time *" type="datetime-local" v={f.pickup_datetime} on={v => upd("pickup_datetime", v)} t="nr-pickup-dt"/>
              <Fld k="Passengers" type="number" v={f.pax} on={v => upd("pax", v)} t="nr-pax"/>
              <Fld k="Luggage" type="number" v={f.luggage} on={v => upd("luggage", v)} t="nr-luggage"/>
              <Fld k="Flight # (optional)" v={f.flight_number} on={v => upd("flight_number", v.toUpperCase())} t="nr-flight" placeholder="AA 1072"/>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Vehicle *</label>
                <select
                  data-testid="nr-vehicle"
                  value={f.vehicle_id}
                  onChange={e => upd("vehicle_id", e.target.value)}
                  className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
                >
                  {vehicles?.map(v => (
                    <option key={v.vehicle_id} value={v.vehicle_id} className="bg-[#0A0A0A]">{v.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Notes (visible to customer)</label>
              <textarea
                data-testid="nr-notes"
                value={f.notes}
                onChange={e => upd("notes", e.target.value)}
                rows={2}
                placeholder="Special instructions, signage, child seat needs, etc."
                className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
              />
            </div>
          </div>

          {/* Live quote */}
          <div className="border border-white/10 bg-black/40 p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Live fare preview</div>
              {quoting ? (
                <div className="text-sm text-[#A1A1AA]">Calculating…</div>
              ) : quote ? (
                <div>
                  <div className="font-serif text-2xl text-[#D4AF37]" data-testid="nr-quote-total">${Number(quote.total || 0).toFixed(2)}</div>
                  <div className="text-[11px] text-[#A1A1AA]">
                    {quote.vehicle_name} · {quote.distance ? `${quote.distance} mi` : ""}
                    {quote.duration ? ` · ${Math.round(quote.duration)} min` : ""}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[#71717A]">Fill in pickup, drop-off & vehicle to see the fare</div>
              )}
            </div>
            <div className="text-right">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Tip</label>
              <div className="flex gap-1">
                {[0, 0.15, 0.20, 0.30].map(t => (
                  <button
                    key={t}
                    data-testid={`nr-tip-${t}`}
                    onClick={() => upd("tip_pct", t)}
                    className={`px-2.5 py-1 text-[11px] border transition-colors ${Math.abs(f.tip_pct - t) < 0.001 ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/30"}`}
                  >{t === 0 ? "None" : `${Math.round(t * 100)}%`}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Economy vs Luxury fare comparison — click a card to switch vehicle */}
          {!quoting && quote?.vehicles?.length > 1 && (
            <div className="border border-white/10 bg-black/40 p-3" data-testid="nr-fare-compare">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Compare fares · tap to switch vehicle</div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {quote.vehicles.map(v => {
                  const selected = v.vehicle_id === f.vehicle_id;
                  const diff = (v.fare ?? 0) - (quote.total ?? 0);
                  const economy = isEconomy(v.vehicle_id, v.name);
                  const overCap = v.available === false;
                  return (
                    <button
                      key={v.vehicle_id}
                      data-testid={`nr-compare-${v.vehicle_id}`}
                      onClick={() => upd("vehicle_id", v.vehicle_id)}
                      title={overCap ? `Over capacity for ${f.pax} pax / ${f.luggage} bags` : v.name}
                      className={`shrink-0 min-w-[108px] border px-3 py-2 text-left transition-colors ${selected ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 hover:border-white/30"} ${overCap ? "opacity-40" : ""}`}
                    >
                      <div className={`text-[9px] font-bold uppercase tracking-[0.18em] ${economy ? "text-[#38BDF8]" : "text-[#D4AF37]"}`}>{economy ? "Economy" : "Luxury"}</div>
                      <div className="text-xs text-[#F5F2EB] truncate max-w-[120px]">{v.name}</div>
                      <div className="font-serif text-lg text-[#F5F2EB]">${Number(v.fare || 0).toFixed(0)}</div>
                      <div className="text-[10px]">
                        {selected
                          ? <span className="text-[#D4AF37] uppercase tracking-[0.14em]">Selected</span>
                          : overCap
                            ? <span className="text-[#F87171]">over capacity</span>
                            : <span className={diff >= 0 ? "text-[#F87171]" : "text-[#34D399]"}>{diff >= 0 ? `+$${Math.abs(diff).toFixed(0)}` : `save $${Math.abs(diff).toFixed(0)}`}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notify toggle + actions */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <label className="flex items-center gap-2 text-[11px] text-[#A1A1AA] cursor-pointer">
              <input
                type="checkbox"
                data-testid="nr-notify"
                checked={f.notify_customer}
                onChange={e => upd("notify_customer", e.target.checked)}
                className="accent-[#D4AF37]"
              />
              Send confirmation email + SMS to customer
            </label>
            <div className="flex gap-2">
              <button data-testid="nr-cancel" onClick={onClose} className="btn-ghost !py-2 !px-4 text-[11px]">Cancel</button>
              <button data-testid="nr-submit" onClick={submit} disabled={busy} className="btn-gold !py-2 !px-5 text-[11px]">
                {busy ? "Creating…" : "Create Reservation"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettlementsTab({ bookings }) {
  const [pending, setPending] = React.useState([]);
  const [settled, setSettled] = React.useState([]);
  const [createFor, setCreateFor] = React.useState(null); // booking object
  const [viewing, setViewing] = React.useState(null);     // settlement object
  const [editing, setEditing] = React.useState(null);     // settlement object
  const [paying, setPaying] = React.useState(null);       // settlement object
  const [filter, setFilter] = React.useState("weekly");

  const refresh = React.useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/settlements/pending-trips"),
        api.get("/settlements"),
      ]);
      setPending(p.data || []);
      setSettled(s.data || []);
    } catch (e) { toast.error("Could not load settlements"); }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const paidList = settled.filter(s => s.status === "paid");
  const approvedList = settled.filter(s => s.status === "approved");
  const totalSettled = paidList.reduce((a, b) => a + (b.amount || 0), 0);
  const totalRevenue = paidList.reduce((a, b) => a + (b.trip_fare || 0), 0);
  const totalProfit = totalRevenue - totalSettled;
  const totalApproved = approvedList.reduce((a, b) => a + (b.amount || 0), 0);

  const del = async (settlement_id) => {
    if (!window.confirm("Delete this settlement? This cannot be undone.")) return;
    try {
      await api.delete(`/settlements/${settlement_id}`);
      toast.success("Settlement deleted");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="settlements-tab">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl">Driver Payouts &amp; Settlement</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Awaiting approval" value={pending.length} alert={pending.length > 0}/>
          <StatCard label="Approved (to pay)" value={`$${totalApproved.toFixed(2)}`} gold={totalApproved > 0}/>
          <StatCard label="Paid to drivers" value={`$${totalSettled.toFixed(2)}`}/>
          <StatCard label="Company profit (paid)" value={`$${totalProfit.toFixed(2)}`} gold/>
        </div>

        <div className="flex gap-2 mb-4 border-b border-white/10">
          {[
            ["weekly", "Weekly payout run"],
            ["pending", `Trips awaiting settlement (${pending.length})`],
            ["approved", `Approved · ready to pay (${approvedList.length})`],
            ["settled", `Paid (${paidList.length})`],
          ].map(([k, l]) => (
            <button key={k} data-testid={`settle-tab-${k}`} onClick={() => setFilter(k)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] ${filter===k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#A1A1AA]"}`}>{l}</button>
          ))}
        </div>

        {filter === "weekly" && <WeeklyPayoutRun onPaid={refresh}/>}

        {filter === "pending" && (
          <div className="space-y-2">
            {pending.length === 0 ? <div className="text-[#A1A1AA] text-sm">No trips awaiting settlement.</div> :
              pending.map(b => (
                <div key={b.booking_id} className="border border-white/10 bg-[#0A0A0A] p-4 flex justify-between items-center">
                  <div>
                    <div className="text-[#F5F2EB] text-sm">{b.confirmation_code} · {b.assigned_driver_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1">
                      {new Date(b.pickup_datetime).toLocaleDateString()} · {b.vehicle_name} · Fare ${b.fare.toFixed(2)}
                    </div>
                  </div>
                  <button data-testid={`settle-btn-${b.booking_id}`} onClick={() => setCreateFor(b)} className="btn-gold !py-1.5 !px-4 text-[10px]">+ Create Settlement</button>
                </div>
              ))}
          </div>
        )}

        {filter === "approved" && (
          <SettlementList list={approvedList} onView={setViewing} onEdit={setEditing} onPay={setPaying} onDelete={del}/>
        )}
        {filter === "settled" && (
          <SettlementList list={paidList} onView={setViewing} onEdit={setEditing} onPay={setPaying} onDelete={del}/>
        )}
      </div>

      {createFor && <SettlementCreateModal booking={createFor} onClose={() => setCreateFor(null)} onDone={() => { setCreateFor(null); refresh(); }}/>}
      {viewing && <SettlementViewModal settlement={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }}/>}
      {editing && <SettlementEditModal settlement={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); refresh(); }}/>}
      {paying && <SettlementPayModal settlement={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); refresh(); }}/>}
    </section>
  );
}

function SettlementList({ list, onView, onEdit, onPay, onDelete }) {
  if (!list.length) return <div className="text-[#A1A1AA] text-sm">No settlements here.</div>;
  return (
    <div className="border border-white/10 bg-[#0A0A0A]">
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#71717A] border-b border-white/10">
        <div className="col-span-3">Driver</div>
        <div className="col-span-2">Amount</div>
        <div className="col-span-2">Custom Charges</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1">Date</div>
        <div className="col-span-3 text-right">Actions</div>
      </div>
      {list.map(s => (
        <div key={s.settlement_id} data-testid={`settlement-row-${s.settlement_id}`}
          className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 items-center text-sm">
          <div className="col-span-3">
            <div className="text-[#F5F2EB]">{s.driver_name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-0.5">ID: {s.driver_id || "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[#D4AF37] font-serif text-base">${(s.amount || 0).toFixed(2)}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Fare: ${(s.driver_fare || 0).toFixed(2)}</div>
          </div>
          <div className="col-span-2 text-[#A1A1AA] text-xs">
            {s.custom_charge && s.custom_charge !== 0
              ? `$${s.custom_charge.toFixed(2)}${s.custom_charge_label ? ` · ${s.custom_charge_label}` : ""}`
              : <span className="text-[#71717A]">No custom charges</span>}
          </div>
          <div className="col-span-1">
            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 ${
              s.status === "paid" ? "text-emerald-400 border border-emerald-400/40"
              : s.status === "approved" ? "text-[#D4AF37] border border-[#D4AF37]/40"
              : "text-[#A1A1AA] border border-white/10"
            }`}>{s.status}</span>
          </div>
          <div className="col-span-1 text-[11px] text-[#A1A1AA]">
            {s.paid_date || s.approved_date || (s.created_at ? new Date(s.created_at).toLocaleDateString() : "—")}
          </div>
          <div className="col-span-3 flex gap-2 justify-end text-[10px] uppercase tracking-[0.18em]">
            <button data-testid={`settle-view-${s.settlement_id}`} onClick={() => onView(s)} className="text-[#A1A1AA] hover:text-[#F5F2EB]">View</button>
            {s.status !== "paid" && <button data-testid={`settle-edit-${s.settlement_id}`} onClick={() => onEdit(s)} className="text-[#A1A1AA] hover:text-[#F5F2EB]">Edit</button>}
            {s.status === "approved" && <button data-testid={`settle-pay-${s.settlement_id}`} onClick={() => onPay(s)} className="text-[#D4AF37] hover:underline">Pay</button>}
            <a data-testid={`settle-receipt-${s.settlement_id}`} href={`${API}/settlements/${s.settlement_id}/receipt.pdf`} target="_blank" rel="noreferrer" className="text-[#A1A1AA] hover:text-[#F5F2EB]">PDF</a>
            <button data-testid={`settle-delete-${s.settlement_id}`} onClick={() => onDelete(s.settlement_id)} className="text-red-400 hover:underline">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModalShell({ children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-6" data-testid={testid}>{children}</div>
    </div>
  );
}

function SettlementCreateModal({ booking, onClose, onDone }) {
  const [driverFare, setDriverFare] = React.useState((booking.fare * 0.77).toFixed(2));
  const [driverGratuity, setDriverGratuity] = React.useState((Number((booking.fare_breakdown || {}).gratuity || 0)).toFixed(2));
  const [customCharge, setCustomCharge] = React.useState("0");
  const [customLabel, setCustomLabel] = React.useState("");
  const [method, setMethod] = React.useState("zelle");
  const [paidDate, setPaidDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(null);
  const total = (Number(driverFare || 0) + Number(driverGratuity || 0) + Number(customCharge || 0));

  const submit = async (markPaid) => {
    setBusy(true);
    try {
      const r = await api.post("/settlements", {
        booking_id: booking.booking_id,
        driver_fare: Number(driverFare),
        driver_gratuity: Number(driverGratuity),
        custom_charge: Number(customCharge),
        custom_charge_label: customLabel || null,
        method,
        paid_date: markPaid ? paidDate : null,
        status: markPaid ? "paid" : "approved",
        notes,
      });
      setDone(r.data);
      toast.success(markPaid ? `Paid · ${r.data.receipt_number}` : `Approved · ${r.data.receipt_number}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Settlement failed"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <ModalShell testid="settlement-modal">
        <div className="space-y-4">
          <div className="overline text-[#D4AF37]">Settlement {done.status === "paid" ? "paid" : "approved"}</div>
          <div className="font-serif text-xl text-[#F5F2EB]">{done.receipt_number}</div>
          <div className="text-sm text-[#A1A1AA]">
            ${done.amount.toFixed(2)} to {done.driver_name} via {done.method.toUpperCase()}.
            <br/>Company profit: <span className="text-[#D4AF37]">${done.company_profit.toFixed(2)}</span>
          </div>
          <a data-testid="settle-done-pdf" href={`${API}/settlements/${done.settlement_id}/receipt.pdf`} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center">📄 Download Receipt PDF</a>
          <button onClick={onDone} className="btn-ghost w-full">Close</button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell testid="settlement-modal">
      <div className="space-y-3">
        <div>
          <div className="overline">Create settlement</div>
          <h3 className="font-serif text-xl">{booking.confirmation_code} · {booking.assigned_driver_name}</h3>
          <div className="text-xs text-[#71717A] mt-1">Trip fare: ${booking.fare.toFixed(2)} · {booking.vehicle_name}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumField label="Driver Fare" value={driverFare} onChange={setDriverFare} testid="settle-driver-fare"/>
          <NumField label="Driver Gratuity" value={driverGratuity} onChange={setDriverGratuity} testid="settle-driver-gratuity"/>
        </div>
        <NumField label="Custom Charge" value={customCharge} onChange={setCustomCharge} testid="settle-custom-charge"/>
        {Number(customCharge) !== 0 && (
          <Fld k="Custom Charge Label (e.g. Tolls, Bonus)" v={customLabel} on={setCustomLabel} t="settle-custom-label"/>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Method</label>
            <select data-testid="settle-method" value={method} onChange={e=>setMethod(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              <option value="zelle">Zelle</option><option value="venmo">Venmo</option>
              <option value="cash">Cash</option><option value="check">Check</option><option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment date (if paying now)</label>
            <input type="date" data-testid="settle-date" value={paidDate} onChange={e=>setPaidDate(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
          </div>
        </div>

        <Fld k="Notes (optional)" v={notes} on={setNotes} t="settle-notes"/>

        <div className="border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex justify-between items-center mt-2">
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-400">Total Settlement Amount</span>
          <span className="text-2xl font-serif text-emerald-300" data-testid="settle-total">${total.toFixed(2)}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
          Company profit: ${(booking.fare - total).toFixed(2)}
        </div>

        <div className="flex gap-2 pt-2">
          <button data-testid="settle-approve" onClick={() => submit(false)} disabled={busy || total <= 0} className="btn-ghost flex-1 justify-center">{busy ? "…" : "Approve only"}</button>
          <button data-testid="settle-pay-now" onClick={() => submit(true)} disabled={busy || total <= 0} className="btn-gold flex-1 justify-center">{busy ? "Saving…" : "Approve & pay now"}</button>
        </div>
        <button onClick={onClose} className="btn-ghost w-full">Cancel</button>
      </div>
    </ModalShell>
  );
}

function SettlementViewModal({ settlement: s, onClose, onEdit }) {
  return (
    <ModalShell testid="settlement-view-modal">
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-xl">Settlement Details</h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#F5F2EB] text-xl leading-none">×</button>
        </div>

        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Driver Information</div>
          <div className="font-serif text-lg text-[#F5F2EB]">{s.driver_name}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mt-0.5">
            ID: {s.driver_id} {s.driver_email ? ` · ${s.driver_email}` : ""}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Detail k="Settlement #" v={s.receipt_number}/>
          <Detail k="Reservation #" v={s.confirmation_code || "—"}/>
          <Detail k="Total Amount" v={`$${(s.amount || 0).toFixed(2)}`} gold/>
          <Detail k="Created" v={s.created_at ? new Date(s.created_at).toLocaleString() : "—"}/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card label="Driver Fare" value={`$${(s.driver_fare || 0).toFixed(2)}`}/>
          <Card label="Driver Gratuity" value={`$${(s.driver_gratuity || 0).toFixed(2)}`}/>
        </div>
        {Number(s.custom_charge || 0) !== 0 && (
          <Card label={`Custom Charge${s.custom_charge_label ? ` · ${s.custom_charge_label}` : ""}`}
                value={`$${(s.custom_charge || 0).toFixed(2)}`}/>
        )}

        <div className="border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex justify-between items-center">
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-400">Total Settlement Amount</span>
          <span className="text-2xl font-serif text-emerald-300">${(s.amount || 0).toFixed(2)}</span>
        </div>

        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Settlement Status</span>
            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border ${
              s.status === "paid" ? "text-emerald-400 border-emerald-400/40"
              : s.status === "approved" ? "text-[#D4AF37] border-[#D4AF37]/40"
              : "text-[#A1A1AA] border-white/10"
            }`}>{s.status}</span>
          </div>
          <div className="text-xs text-[#A1A1AA]">
            {s.status === "paid" ? (
              <>Paid via {(s.method || "").toUpperCase()} on {s.paid_date}</>
            ) : s.status === "approved" ? (
              <>Approved &amp; ready for payment{s.approved_date ? ` · ${s.approved_date}` : ""}</>
            ) : "Pending review"}
          </div>
          {s.notes && (
            <div className="text-xs text-[#A1A1AA] mt-2 whitespace-pre-wrap"><b className="text-[#F5F2EB]">Notes:</b> {s.notes}</div>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          {s.status !== "paid" && (
            <button data-testid="settle-view-edit" onClick={onEdit} className="btn-gold flex-1 justify-center">Edit Settlement</button>
          )}
          <a data-testid="settle-view-pdf" href={`${API}/settlements/${s.settlement_id}/receipt.pdf`} target="_blank" rel="noreferrer" className="btn-ghost flex-1 justify-center">📄 PDF</a>
          <button onClick={onClose} className="btn-ghost">Close</button>
        </div>
      </div>
    </ModalShell>
  );
}

function SettlementEditModal({ settlement: s, onClose, onDone }) {
  const [driverFare, setDriverFare] = React.useState(String(s.driver_fare || 0));
  const [driverGratuity, setDriverGratuity] = React.useState(String(s.driver_gratuity || 0));
  const [customCharge, setCustomCharge] = React.useState(String(s.custom_charge || 0));
  const [customLabel, setCustomLabel] = React.useState(s.custom_charge_label || "");
  const [notes, setNotes] = React.useState(s.notes || "");
  const [busy, setBusy] = React.useState(false);
  const total = (Number(driverFare || 0) + Number(driverGratuity || 0) + Number(customCharge || 0));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/settlements/${s.settlement_id}`, {
        driver_fare: Number(driverFare),
        driver_gratuity: Number(driverGratuity),
        custom_charge: Number(customCharge),
        custom_charge_label: customLabel || null,
        notes,
      });
      toast.success("Settlement updated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <ModalShell testid="settlement-edit-modal">
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-xl">Edit Settlement</h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#F5F2EB] text-xl leading-none">×</button>
        </div>
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Driver</div>
          <div className="font-serif text-lg text-[#F5F2EB]">{s.driver_name}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <NumField label="Driver Fare" value={driverFare} onChange={setDriverFare} testid="settle-edit-driver-fare"/>
          <NumField label="Driver Gratuity" value={driverGratuity} onChange={setDriverGratuity} testid="settle-edit-driver-gratuity"/>
        </div>
        <NumField label="Custom Charge" value={customCharge} onChange={setCustomCharge} testid="settle-edit-custom-charge"/>
        {Number(customCharge) !== 0 && (
          <Fld k="Custom Charge Label" v={customLabel} on={setCustomLabel} t="settle-edit-custom-label"/>
        )}

        <Fld k="Notes" v={notes} on={setNotes} t="settle-edit-notes"/>

        <div className="border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex justify-between items-center">
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-400">Total Settlement Amount</span>
          <span className="text-2xl font-serif text-emerald-300" data-testid="settle-edit-total">${total.toFixed(2)}</span>
        </div>

        <div className="flex gap-2 pt-2">
          <button data-testid="settle-edit-save" onClick={save} disabled={busy || total <= 0} className="btn-gold flex-1 justify-center">{busy ? "Saving…" : "Update Settlement"}</button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

function SettlementPayModal({ settlement: s, onClose, onDone }) {
  const [method, setMethod] = React.useState(s.method || "zelle");
  const [paidDate, setPaidDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/settlements/${s.settlement_id}/pay`, { method, paid_date: paidDate, notes: notes || null });
      toast.success(`Marked as paid · ${s.receipt_number}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell testid="settlement-pay-modal">
      <div className="space-y-3">
        <div className="flex justify-between items-start">
          <h3 className="font-serif text-xl">Mark as paid</h3>
          <button onClick={onClose} className="text-[#71717A] hover:text-[#F5F2EB] text-xl leading-none">×</button>
        </div>
        <div className="text-sm text-[#A1A1AA]">
          Paying <span className="text-[#D4AF37] font-serif text-base">${(s.amount || 0).toFixed(2)}</span> to {s.driver_name}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Method</label>
            <select data-testid="settle-pay-method" value={method} onChange={e=>setMethod(e.target.value)} className="w-full bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              <option value="zelle">Zelle</option><option value="venmo">Venmo</option>
              <option value="cash">Cash</option><option value="check">Check</option><option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment date</label>
            <input type="date" data-testid="settle-pay-date" value={paidDate} onChange={e=>setPaidDate(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
          </div>
        </div>
        <Fld k="Notes (optional)" v={notes} on={setNotes} t="settle-pay-notes"/>
        <div className="flex gap-2 pt-2">
          <button data-testid="settle-pay-confirm" onClick={submit} disabled={busy} className="btn-gold flex-1 justify-center">{busy ? "…" : "Confirm payment"}</button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</label>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#71717A]">$</span>
        <input
          type="number" step="0.01" data-testid={testid}
          value={value} onChange={e => onChange(e.target.value)}
          className="w-full bg-transparent border border-white/10 pl-5 pr-2 py-1.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37]/60 focus:outline-none"
        />
      </div>
    </div>
  );
}

function Detail({ k, v, gold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">{k}</div>
      <div className={`text-sm ${gold ? "text-[#D4AF37] font-serif text-base" : "text-[#F5F2EB]"}`}>{v}</div>
    </div>
  );
}

function Card({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#080808] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
      <div className="font-serif text-lg text-[#F5F2EB]">{value}</div>
    </div>
  );
}

function StatCard({ label, value, gold, alert }) {
  return (
    <div className={`border ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/10" : alert ? "border-amber-500/30 bg-amber-500/5" : "border-white/10 bg-[#0A0A0A]"} p-4`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
      <div className={`font-serif text-2xl ${gold ? "text-[#D4AF37]" : alert ? "text-amber-400" : "text-[#F5F2EB]"}`}>{value}</div>
    </div>
  );
}



function RouteAuditTab({ onChange }) {
  const [completed, setCompleted] = React.useState([]);
  const [flagged, setFlagged] = React.useState([]);
  const [openBooking, setOpenBooking] = React.useState(null);
  const [filter, setFilter] = React.useState("flagged"); // flagged | all-completed

  const refresh = React.useCallback(async () => {
    try {
      const [c, f] = await Promise.all([
        api.get("/bookings", { params: { status: "completed" } }),
        api.get("/bookings/route-diff/flagged"),
      ]);
      setCompleted(c.data || []);
      setFlagged(f.data || []);
    } catch (e) { toast.error("Could not load route audit data"); }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const totalExtraBilled = flagged.reduce((a, b) => a + (b.route_diff_applied?.amount || 0), 0);
  const unbilledCount = flagged.filter(b => !b.route_diff_applied).length;
  const list = filter === "flagged" ? flagged : completed;

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="route-audit-tab">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-serif text-2xl">Route Audit</h2>
            <div className="text-xs text-[#71717A] mt-1">Compare actual driven miles vs the original quote. Flag &amp; rebill significant overruns.</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label="Flagged trips" value={flagged.length}/>
          <StatCard label="Unbilled overruns" value={unbilledCount} gold={unbilledCount > 0}/>
          <StatCard label="Extra billed total" value={`$${totalExtraBilled.toFixed(2)}`} gold/>
        </div>

        <div className="flex gap-2 mb-4 border-b border-white/10">
          {[
            ["flagged", `Flagged (${flagged.length})`],
            ["all-completed", `All completed (${completed.length})`],
          ].map(([k, l]) => (
            <button key={k} data-testid={`route-audit-tab-${k}`} onClick={() => setFilter(k)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] ${filter===k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#A1A1AA]"}`}>{l}</button>
          ))}
        </div>

        <div className="space-y-2">
          {list.length === 0 ? (
            <div className="text-[#A1A1AA] text-sm">
              {filter === "flagged" ? "No trips flagged for route overrun." : "No completed trips."}
            </div>
          ) : list.map(b => {
            const rd = b.route_diff;
            const applied = b.route_diff_applied;
            return (
              <div key={b.booking_id} className="border border-white/10 bg-[#0A0A0A] p-4 flex justify-between items-center">
                <div className="flex-1">
                  <div className="text-[#F5F2EB] text-sm flex items-center gap-2">
                    {b.confirmation_code} · {b.assigned_driver_name || "—"}
                    {rd?.flagged && !applied && (
                      <span className="text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 px-1.5 py-0.5">Flagged</span>
                    )}
                    {applied && (
                      <span className="text-[9px] uppercase tracking-[0.18em] text-emerald-400 border border-emerald-400/40 px-1.5 py-0.5">Rebilled +${applied.amount.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1">
                    {new Date(b.pickup_datetime).toLocaleDateString()} · {b.vehicle_name} ·
                    Quoted {(b.distance_miles || 0).toFixed(1)} mi
                    {rd ? ` · Actual ${rd.actual_miles.toFixed(1)} mi · Diff ${rd.diff_miles >= 0 ? "+" : ""}${rd.diff_miles.toFixed(1)} mi (${rd.diff_pct}%)` : " · No route data yet"}
                  </div>
                </div>
                <button
                  data-testid={`route-audit-review-${b.booking_id}`}
                  onClick={() => setOpenBooking(b)}
                  className="btn-gold !py-1.5 !px-4 text-[10px]"
                >Review</button>
              </div>
            );
          })}
        </div>
      </div>

      {openBooking && (
        <RouteDiffModal
          booking={openBooking}
          onClose={() => setOpenBooking(null)}
          onDone={() => { setOpenBooking(null); refresh(); onChange && onChange(); }}
        />
      )}
    </section>
  );
}

function RouteDiffModal({ booking, onClose, onDone }) {
  const [diff, setDiff] = React.useState(null);
  const [actualMiles, setActualMiles] = React.useState("");
  const [rebillAmount, setRebillAmount] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/bookings/${booking.booking_id}/route-diff`);
        setDiff(r.data);
        setActualMiles(r.data.actual_miles ? r.data.actual_miles.toFixed(1) : "");
        setRebillAmount(r.data.rebill_estimate ? r.data.rebill_estimate.toFixed(2) : "");
      } catch (e) { toast.error("Could not load route diff"); }
    })();
  }, [booking.booking_id]);

  const recompute = async () => {
    setBusy(true);
    try {
      const payload = actualMiles ? { actual_miles: Number(actualMiles) } : {};
      const r = await api.post(`/bookings/${booking.booking_id}/route-diff`, payload);
      setDiff(r.data);
      setRebillAmount(r.data.rebill_estimate ? r.data.rebill_estimate.toFixed(2) : "");
      toast.success("Route diff recomputed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recompute failed"); }
    finally { setBusy(false); }
  };

  const applyRebill = async () => {
    if (!rebillAmount || Number(rebillAmount) <= 0) {
      toast.error("Rebill amount must be > 0");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/route-diff`, {
        actual_miles: actualMiles ? Number(actualMiles) : undefined,
        apply_rebill: true,
        rebill_amount: Number(rebillAmount),
      });
      toast.success(`Rebilled +$${r.data.applied ? r.data.new_fare_total : ""}. New total $${r.data.new_fare_total?.toFixed(2)}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rebill failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-md w-full p-6" data-testid="route-diff-modal">
        <div className="space-y-3">
          <div>
            <div className="overline">Route audit</div>
            <h3 className="font-serif text-xl">{booking.confirmation_code} · {booking.assigned_driver_name || "—"}</h3>
            <div className="text-xs text-[#71717A] mt-1">Customer fare: ${booking.fare?.toFixed(2)} · {booking.vehicle_name}</div>
          </div>

          {diff && (
            <div className="border border-white/10 p-3 text-xs space-y-1.5 bg-[#080808]" data-testid="route-diff-snapshot">
              <Row k="Quoted miles" v={`${diff.quoted_miles?.toFixed(1)} mi`}/>
              <Row k="Actual miles (GPS or manual)" v={`${diff.actual_miles?.toFixed(1)} mi`}/>
              <Row k="Difference" v={`${diff.diff_miles >= 0 ? "+" : ""}${diff.diff_miles?.toFixed(1)} mi (${diff.diff_pct}%)`} gold={diff.flagged}/>
              <Row k="Per-mile rate" v={`$${diff.per_mile_rate?.toFixed(2)}`}/>
              <Row k="Suggested rebill" v={`$${diff.rebill_estimate?.toFixed(2)}`} gold/>
              <Row k="Source" v={diff.source}/>
              {booking.route_diff_applied && (
                <Row k="Previously rebilled" v={`$${booking.route_diff_applied.amount.toFixed(2)}`}/>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Actual miles (override)</label>
              <input type="number" step="0.1" data-testid="route-diff-actual-miles" value={actualMiles} onChange={e=>setActualMiles(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Rebill amount ($)</label>
              <input type="number" step="0.01" data-testid="route-diff-rebill-amount" value={rebillAmount} onChange={e=>setRebillAmount(e.target.value)} className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button data-testid="route-diff-recompute" onClick={recompute} disabled={busy} className="btn-ghost flex-1">{busy ? "…" : "Recompute"}</button>
            <button data-testid="route-diff-apply" onClick={applyRebill} disabled={busy} className="btn-gold flex-1 justify-center">{busy ? "Saving…" : "Apply rebill"}</button>
          </div>
          <button onClick={onClose} className="btn-ghost w-full">Close</button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, gold }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[#71717A]">{k}</span>
      <span className={gold ? "text-[#D4AF37] font-medium" : "text-[#F5F2EB]"}>{v}</span>
    </div>
  );
}


function ReportsPanel() {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(""); // "" = full year
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (month) params.month = Number(month);
      const r = await api.get("/reports/summary", { params });
      setSummary(r.data);
    } catch (e) {
      toast.error("Could not load report summary");
    } finally { setLoading(false); }
  }, [year, month]);

  React.useEffect(() => { load(); }, [load]);

  const periodTag = month ? `${year}-${String(month).padStart(2, "0")}` : `${year}`;
  const qs = `year=${year}${month ? `&month=${month}` : ""}`;
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-8" data-testid="reports-panel">
      <div className="flex justify-between items-end mb-5 flex-wrap gap-4">
        <div>
          <div className="overline mb-1">Reports &amp; Year-end exports</div>
          <div className="text-xs text-[#71717A]">Generate CSV &amp; PDF summaries for tax filing or accountant review.</div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Year</label>
            <select data-testid="reports-year" value={year} onChange={e => setYear(Number(e.target.value))}
              className="bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Month</label>
            <select data-testid="reports-month" value={month} onChange={e => setMonth(e.target.value)}
              className="bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-sm">
              <option value="">Full year</option>
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                <option key={i+1} value={i+1}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A1A1AA] text-sm">Loading…</div>
      ) : summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5" data-testid="reports-summary">
          <Mini label="Completed trips" value={summary.trip_count}/>
          <Mini label="Gross fare" value={`$${(summary.gross_fare || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}/>
          <Mini label="Paid revenue" value={`$${(summary.paid_fare || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} gold/>
          <Mini label="Driver payouts" value={`$${(summary.settlement_total || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`}/>
          <Mini label="Estimated profit" value={`$${(summary.estimated_profit || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`} gold/>
          <Mini label="Settlements" value={summary.settlement_count}/>
          <Mini label="Paid trips" value={summary.paid_trip_count}/>
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
        <a data-testid="reports-trips-csv" href={`${API}/reports/trips.csv?${qs}`} target="_blank" rel="noreferrer"
          className="btn-ghost text-[11px]">⬇ Trips CSV ({periodTag})</a>
        <a data-testid="reports-settlements-csv" href={`${API}/reports/settlements.csv?${qs}`} target="_blank" rel="noreferrer"
          className="btn-ghost text-[11px]">⬇ Settlements CSV ({periodTag})</a>
        <a data-testid="reports-summary-pdf" href={`${API}/reports/summary.pdf?${qs}`} target="_blank" rel="noreferrer"
          className="btn-gold text-[11px]">📄 Summary PDF ({periodTag})</a>
      </div>
    </div>
  );
}

function Mini({ label, value, gold }) {
  return (
    <div className="border border-white/5 bg-[#080808] p-3">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{label}</div>
      <div className={`font-serif text-lg ${gold ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{value}</div>
    </div>
  );
}



function SeriesActions({ series, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const isPaused = (series.active_count === 0) && series.completed_count + series.cancelled_count < series.total_occurrences;

  const pause = async () => {
    if (!window.confirm(`Pause this series? All upcoming rides will be set to "paused" and reminders cancelled.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/series/${series.series_id}/pause`);
      toast.success(`Paused · ${r.data.paused} upcoming rides`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pause failed"); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/series/${series.series_id}/resume`);
      toast.success(`Resumed · ${r.data.resumed} rides reactivated`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm(`Cancel all remaining rides in this series? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/bookings/series/${series.series_id}`);
      toast.success(`Cancelled · ${r.data.cancelled} rides`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex gap-2 justify-end" data-testid={`series-actions-${series.series_id}`}>
      {series.active_count > 0 && (
        <button data-testid={`series-pause-${series.series_id}`} onClick={pause} disabled={busy}
          className="text-[10px] uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 px-2 py-1 hover:bg-amber-500/10">Pause</button>
      )}
      <button data-testid={`series-resume-${series.series_id}`} onClick={resume} disabled={busy}
        className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/40 px-2 py-1 hover:bg-emerald-500/10">Resume</button>
      <button data-testid={`series-cancel-${series.series_id}`} onClick={cancel} disabled={busy}
        className="text-[10px] uppercase tracking-[0.18em] text-red-400 border border-red-500/40 px-2 py-1 hover:bg-red-500/10">Cancel</button>
    </div>
  );
}



// =============================================================
// CALENDAR TAB — month-grid view of all trips, color-coded
// =============================================================
const CAL_STATUS_COLOR = {
  pending:              { bg: "bg-amber-500/15",  text: "text-amber-300",   border: "border-amber-500/40",  dot: "bg-amber-400",  label: "Pending" },
  new:                  { bg: "bg-amber-500/15",  text: "text-amber-300",   border: "border-amber-500/40",  dot: "bg-amber-400",  label: "Pending" },
  accepted:             { bg: "bg-blue-500/15",   text: "text-blue-300",    border: "border-blue-500/40",   dot: "bg-blue-400",   label: "Accepted" },
  assigned:             { bg: "bg-blue-500/15",   text: "text-blue-300",    border: "border-blue-500/40",   dot: "bg-blue-400",   label: "Accepted" },
  dispatched:           { bg: "bg-indigo-500/15", text: "text-indigo-300",  border: "border-indigo-500/40", dot: "bg-indigo-400", label: "Dispatched" },
  accepted_by_driver:   { bg: "bg-violet-500/15", text: "text-violet-300",  border: "border-violet-500/40", dot: "bg-violet-400", label: "Driver accepted" },
  en_route:             { bg: "bg-orange-500/15", text: "text-orange-300",  border: "border-orange-500/40", dot: "bg-orange-400", label: "En route" },
  on_location:          { bg: "bg-pink-500/15",   text: "text-pink-300",    border: "border-pink-500/40",   dot: "bg-pink-400",   label: "On location" },
  customer_in_car:      { bg: "bg-[#D4AF37]/15",  text: "text-[#D4AF37]",   border: "border-[#D4AF37]/40",  dot: "bg-[#D4AF37]",  label: "In car" },
  in_progress:          { bg: "bg-[#D4AF37]/15",  text: "text-[#D4AF37]",   border: "border-[#D4AF37]/40",  dot: "bg-[#D4AF37]",  label: "In car" },
  clear_at_destination: { bg: "bg-cyan-500/15",   text: "text-cyan-300",    border: "border-cyan-500/40",   dot: "bg-cyan-400",   label: "Clear" },
  completed:            { bg: "bg-emerald-500/15",text: "text-emerald-300", border: "border-emerald-500/40",dot: "bg-emerald-400",label: "Completed" },
  cancelled:            { bg: "bg-red-500/15",    text: "text-red-300",     border: "border-red-500/40",    dot: "bg-red-400",    label: "Cancelled" },
  paused:               { bg: "bg-zinc-500/15",   text: "text-zinc-300",    border: "border-zinc-500/40",   dot: "bg-zinc-400",   label: "Paused" },
};

const AIRPORT_REGEX = /\b(MKE|ORD|MDW|MSN|ATW|GRB)\b/i;
function airportTag(addr) {
  if (!addr) return null;
  const m = addr.match(AIRPORT_REGEX);
  if (m) return m[1].toUpperCase();
  const lower = addr.toLowerCase();
  if (lower.includes("mitchell")) return "MKE";
  if (lower.includes("o'hare") || lower.includes("ohare")) return "ORD";
  if (lower.includes("midway")) return "MDW";
  return null;
}

function CalendarTab({ bookings, onOpen, onOpenRentals }) {
  const [cursor, setCursor] = React.useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [statusFilter, setStatusFilter] = React.useState("all");
  // Car rentals (incl. Turo-synced) shown alongside trips — unified fleet calendar
  const [rentals, setRentals] = React.useState([]);
  React.useEffect(() => {
    api.get("/admin/rental/rentals").then(r => setRentals(r.data || [])).catch(() => setRentals([]));
  }, []);
  // Status legend popover — clicking a chip opens an inline list of the
  // matching trips in the visible month (closes via the same status's chip,
  // ESC, or backdrop click).
  const [legendStatus, setLegendStatus] = React.useState(null);

  // Multi-select mode: tick trips (one client) → Generate Combined Invoice
  const [selectMode, setSelectMode] = React.useState(false);
  const [picked, setPicked] = React.useState(new Set());
  const [combining, setCombining] = React.useState(false);
  const togglePick = (b) => setPicked(p => { const n = new Set(p); n.has(b.booking_id) ? n.delete(b.booking_id) : n.add(b.booking_id); return n; });
  const pickedTrips = React.useMemo(() => bookings.filter(b => picked.has(b.booking_id)), [bookings, picked]);
  const pickedTotal = pickedTrips.reduce((a, b) => a + Number(b.fare || 0), 0);
  const exitSelect = () => { setSelectMode(false); setPicked(new Set()); };
  const cardOpen = selectMode ? togglePick : onOpen;
  // "+N more" day popover (YYYY-MM-DD of the open day)
  const [dayPopover, setDayPopover] = React.useState(null);
  React.useEffect(() => {
    if (!dayPopover) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDayPopover(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dayPopover]);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const today = React.useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  // Build a 6-week grid starting from the Sunday before the 1st
  const cells = React.useMemo(() => {
    const start = new Date(cursor);
    start.setDate(start.getDate() - start.getDay()); // back up to Sunday
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  // Group bookings by YYYY-MM-DD
  const byDay = React.useMemo(() => {
    const m = {};
    for (const b of bookings) {
      if (!b.pickup_datetime) continue;
      if (statusFilter === "unpaid") {
        if (b.payment_status === "paid" || b.status === "cancelled") continue;
      } else if (statusFilter === "paid") {
        if (b.payment_status !== "paid") continue;
      } else if (statusFilter !== "all" && b.status !== statusFilter) continue;
      const key = b.pickup_datetime.slice(0, 10);
      (m[key] = m[key] || []).push(b);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.pickup_datetime.localeCompare(b.pickup_datetime));
    return m;
  }, [bookings, statusFilter]);

  // Group rentals by every day they span (pickup → return, capped at 62 days)
  const rentalsByDay = React.useMemo(() => {
    const m = {};
    for (const r of rentals) {
      if (r.status === "cancelled" || !r.pickup_datetime || !r.return_datetime) continue;
      const end = new Date(r.return_datetime);
      let d = new Date(r.pickup_datetime);
      d.setHours(0, 0, 0, 0);
      for (let i = 0; i < 62 && d <= end; i++) {
        const key = d.toISOString().slice(0, 10);
        (m[key] = m[key] || []).push(r);
        d = new Date(d);
        d.setDate(d.getDate() + 1);
      }
    }
    return m;
  }, [rentals]);

  const monthCount = bookings.filter(b => {
    if (!b.pickup_datetime) return false;
    const d = new Date(b.pickup_datetime);
    return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear();
  }).length;

  const prev = () => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); };
  const next = () => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); };
  const goToday = () => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); setCursor(d); };

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="calendar-tab">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button data-testid="cal-prev" onClick={prev} className="btn-ghost !py-1.5 !px-3 text-[11px]">‹ Prev</button>
            <button data-testid="cal-today" onClick={goToday} className="btn-ghost !py-1.5 !px-3 text-[11px]">Today</button>
            <button data-testid="cal-next" onClick={next} className="btn-ghost !py-1.5 !px-3 text-[11px]">Next ›</button>
            <h2 className="font-serif text-2xl ml-2">{monthLabel}</h2>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{monthCount} trip{monthCount === 1 ? "" : "s"}</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cal-select-mode" onClick={() => selectMode ? exitSelect() : setSelectMode(true)}
              className={`!py-1.5 !px-3 text-[11px] ${selectMode ? "btn-gold" : "btn-ghost"}`}
              title="Tick several trips for one client and generate a single combined invoice">
              {selectMode ? "Done selecting" : "Select trips"}
            </button>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">Filter</label>
            <select data-testid="cal-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#0a0a0a] border border-white/10 px-2 py-1.5 text-xs">
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="en_route">En route</option>
              <option value="customer_in_car">In car</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="unpaid">Payment pending</option>
              <option value="paid">Payment completed</option>
            </select>
          </div>
        </div>

        {/* Legend — clickable to popup matching trips in the visible month */}
        <div className="flex flex-wrap gap-3 mb-4 text-[10px] uppercase tracking-[0.18em]">
          {[
            ["pending", "Pending"],
            ["accepted", "Accepted"],
            ["en_route", "En route"],
            ["customer_in_car", "In car"],
            ["completed", "Completed"],
            ["cancelled", "Cancelled"],
          ].map(([k, l]) => {
            const c = CAL_STATUS_COLOR[k];
            const count = bookings.filter(b => {
              if (b.status !== k || !b.pickup_datetime) return false;
              const d = new Date(b.pickup_datetime);
              return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear();
            }).length;
            return (
              <button
                key={k}
                type="button"
                data-testid={`cal-legend-${k}`}
                onClick={() => setLegendStatus(legendStatus === k ? null : k)}
                className={`flex items-center gap-1.5 px-2 py-1 border ${c.border} ${c.bg} ${c.text} hover:opacity-80 transition-opacity cursor-pointer`}
              >
                <span className={`w-2 h-2 rounded-full ${c.dot}`}/> {l}
                <span className="ml-1 opacity-70">· {count}</span>
              </button>
            );
          })}
          {/* Rentals legend — amber, links to the Car Rental tab */}
          <button
            type="button"
            data-testid="cal-legend-rentals"
            onClick={() => onOpenRentals && onOpenRentals()}
            className="flex items-center gap-1.5 px-2 py-1 border border-amber-500/40 bg-amber-400/10 text-amber-600 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <span className="w-2 h-2 rounded-full bg-amber-500"/> Car rental
            <span className="ml-1 opacity-70">· {rentals.filter(r => {
              if (r.status === "cancelled" || !r.pickup_datetime) return false;
              const s = new Date(r.pickup_datetime), e = new Date(r.return_datetime || r.pickup_datetime);
              const mStart = new Date(cursor), mEnd = new Date(cursor);
              mEnd.setMonth(mEnd.getMonth() + 1);
              return s < mEnd && e >= mStart;
            }).length}</span>
          </button>
          {/* Payment tracking chips — who paid, who hasn't (cancelled excluded) */}
          {(() => {
            const monthTrips = bookings.filter(b => {
              if (!b.pickup_datetime || b.status === "cancelled") return false;
              const d = new Date(b.pickup_datetime);
              return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear();
            });
            const unpaidCount = monthTrips.filter(b => b.payment_status !== "paid").length;
            const paidCount = monthTrips.length - unpaidCount;
            return (
              <>
                <button
                  type="button"
                  data-testid="cal-legend-unpaid"
                  onClick={() => setLegendStatus(legendStatus === "unpaid" ? null : "unpaid")}
                  className="flex items-center gap-1.5 px-2 py-1 border border-red-500/50 bg-red-500/10 text-red-500 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500"/> Payment pending
                  <span className="ml-1 opacity-80 font-bold">· {unpaidCount}</span>
                </button>
                <button
                  type="button"
                  data-testid="cal-legend-paid"
                  onClick={() => setLegendStatus(legendStatus === "paid" ? null : "paid")}
                  className="flex items-center gap-1.5 px-2 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500"/> Payment completed
                  <span className="ml-1 opacity-80 font-bold">· {paidCount}</span>
                </button>
              </>
            );
          })()}
        </div>

        {/* Grid */}
        {dayPopover && <div data-testid="cal-day-popover-backdrop" className="fixed inset-0 z-20" onClick={() => setDayPopover(null)}/>}
        <div className="border border-white/10 bg-[#0A0A0A]">
          <div className="grid grid-cols-7 border-b border-white/10">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
              <div key={d} className="text-center text-[10px] uppercase tracking-[0.2em] text-[#71717A] py-2 border-r border-white/10 last:border-r-0">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="cal-grid">
            {cells.map((d, i) => {
              const key = d.toISOString().slice(0, 10);
              const trips = byDay[key] || [];
              const rents = rentalsByDay[key] || [];
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = d.getTime() === today.getTime();
              return (
                <div key={i} data-testid={`cal-day-${key}`}
                  className={`relative border-r border-b border-white/10 last:border-r-0 min-h-[120px] p-1.5 align-top ${inMonth ? "bg-[#0A0A0A]" : "bg-[#070707]"} ${isToday ? "ring-1 ring-[#D4AF37]/40" : ""} ${dayPopover === key ? "z-30" : ""}`}>
                  <div className={`flex justify-between items-center mb-1 ${inMonth ? "text-[#F5F2EB]" : "text-[#3F3F46]"}`}>
                    <span className={`text-xs ${isToday ? "font-serif text-[#D4AF37]" : ""}`}>{d.getDate()}</span>
                    {(trips.length + rents.length) > 0 && <span className="text-[9px] uppercase tracking-[0.18em] text-[#71717A]">{trips.length + rents.length}</span>}
                  </div>
                  <div className="space-y-1">
                    {rents.slice(0, 2).map(r => (
                      <button
                        key={r.rental_id}
                        type="button"
                        data-testid={`cal-rental-${r.rental_id}`}
                        onClick={() => onOpenRentals && onOpenRentals()}
                        title={`${r.rental_code} · ${r.renter_name} · ${r.vehicle_name || r.vehicle_label || ""}`}
                        className="w-full text-left px-1 py-0.5 text-[9px] uppercase tracking-wide border-l-2 border-amber-500 bg-amber-400/10 text-amber-600 truncate hover:bg-amber-400/20"
                      >
                        {r.source === "turo" ? "Turo" : "Rental"} · {(r.renter_name || "").split(" ")[0]}
                      </button>
                    ))}
                    {rents.length > 2 && (
                      <button type="button" onClick={() => onOpenRentals && onOpenRentals()}
                        className="block text-[9px] uppercase tracking-[0.18em] text-amber-600/70 hover:text-amber-600 pl-1">
                        +{rents.length - 2} rental{rents.length - 2 === 1 ? "" : "s"}
                      </button>
                    )}
                    {(selectMode ? trips : trips.slice(0, 3)).map(b => <CalCard key={b.booking_id} b={b} onOpen={cardOpen} selectable={selectMode} checked={picked.has(b.booking_id)}/>)}
                    {!selectMode && trips.length > 3 && (
                      <button type="button" data-testid={`cal-more-${key}`}
                        onClick={(e) => { e.stopPropagation(); setDayPopover(dayPopover === key ? null : key); }}
                        className="block text-[9px] uppercase tracking-[0.18em] text-[#71717A] hover:text-[#D4AF37] pl-1">
                        +{trips.length - 3} more
                      </button>
                    )}
                  </div>
                  {dayPopover === key && (
                    <DayTripsPopover dateKey={key} trips={trips} onClose={() => setDayPopover(null)} alignRight={i % 7 >= 4}/>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Select-mode action bar — floats above the grid while trips are ticked */}
        {selectMode && (
          <div data-testid="cal-select-bar" className="sticky bottom-4 mt-4 z-40 border border-[#D4AF37]/50 bg-[#0A0A0A]/95 backdrop-blur px-5 py-3 flex items-center gap-4 flex-wrap shadow-xl">
            <span className="text-xs text-[#F5F2EB]"><span className="text-[#D4AF37] font-semibold" data-testid="cal-picked-count">{picked.size}</span> trip{picked.size === 1 ? "" : "s"} selected</span>
            <span className="font-mono text-sm text-[#D4AF37]" data-testid="cal-picked-total">${pickedTotal.toFixed(2)}</span>
            {pickedTrips.length > 0 && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] truncate max-w-xs">
                {[...new Set(pickedTrips.map(b => b.customer_name))].join(" · ")}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="cal-pick-clear" onClick={() => setPicked(new Set())} className="btn-ghost !py-1.5 !px-3 text-[10px]">Clear</button>
              <button data-testid="cal-combine-btn" disabled={picked.size < 2} onClick={() => setCombining(true)} className="btn-gold !py-1.5 !px-4 text-[10px] disabled:opacity-30">
                Generate Combined Invoice{picked.size > 1 ? ` (${picked.size})` : ""}
              </button>
            </div>
          </div>
        )}
        {combining && (
          <CombineInvoiceModal
            trips={pickedTrips}
            onClose={() => setCombining(false)}
            onCreated={() => exitSelect()}
          />
        )}

        {/* Status-legend popup — lists matching trips in the visible month */}
        {legendStatus && (() => {
          const isPayKey = legendStatus === "unpaid" || legendStatus === "paid";
          const filtered = bookings
            .filter(b => {
              if (!b.pickup_datetime) return false;
              if (isPayKey) {
                if (b.status === "cancelled") return false;
                const paid = b.payment_status === "paid";
                if (legendStatus === "unpaid" ? paid : !paid) return false;
              } else if (b.status !== legendStatus) return false;
              const d = new Date(b.pickup_datetime);
              return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear();
            })
            .sort((a, b) => a.pickup_datetime.localeCompare(b.pickup_datetime));
          const c = isPayKey
            ? (legendStatus === "unpaid" ? { dot: "bg-red-500" } : { dot: "bg-emerald-500" })
            : CAL_STATUS_COLOR[legendStatus];
          const label = { pending: "Pending", accepted: "Accepted", en_route: "En route",
                          customer_in_car: "In car", completed: "Completed", cancelled: "Cancelled",
                          unpaid: "Payment pending", paid: "Payment completed" }[legendStatus];
          return (
            <div
              data-testid="cal-legend-popup"
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setLegendStatus(null)}
              onKeyDown={(e) => { if (e.key === "Escape") setLegendStatus(null); }}
            >
              <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center px-5 py-4 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`}/>
                    <span className="font-serif text-lg text-[#F5F2EB]">{label} · {monthLabel}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                      {filtered.length} trip{filtered.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <button data-testid="cal-legend-popup-close" onClick={() => setLegendStatus(null)}
                    className="text-[#A1A1AA] hover:text-white text-xl leading-none">×</button>
                </div>
                {filtered.length === 0 ? (
                  <div className="p-10 text-center text-sm text-[#71717A]">No {label.toLowerCase()} trips this month.</div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {filtered.map(b => {
                      const t = new Date(b.pickup_datetime);
                      const when = t.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
                      return (
                        <button
                          key={b.booking_id}
                          data-testid={`cal-legend-trip-${b.booking_id}`}
                          onClick={() => { onOpen(b); setLegendStatus(null); }}
                          className="w-full text-left px-5 py-3 hover:bg-white/5 grid grid-cols-12 gap-3 items-center"
                        >
                          <div className="col-span-3 text-[#D4AF37] font-mono text-xs">{b.confirmation_code}</div>
                          <div className="col-span-3 text-[#F5F2EB] text-xs">{when}</div>
                          <div className="col-span-4 text-[#A1A1AA] text-xs truncate">
                            {b.customer_name || "—"} · {(b.pickup_address || "").split(",")[0]} → {(b.dropoff_address || "").split(",")[0] || "—"}
                          </div>
                          <div className="col-span-2 text-right text-[#D4AF37] text-xs">
                            ${Number(b.fare || 0).toFixed(2)}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </section>
  );
}

/** Floating list of every trip on one calendar day (opened from "+N more").
 *  Colour code: green = completed · blue = upcoming/scheduled with a driver ·
 *  red = needs attention (unassigned, cancelled, no-show). Click → reservation. */
function tripTone(b) {
  if (b.status === "completed") return { key: "completed", label: "Completed", bar: "border-emerald-500", bg: "bg-emerald-500/15", text: "text-emerald-300", dot: "bg-emerald-400" };
  if (["cancelled", "canceled", "no_show", "rejected"].includes(b.status) || !b.assigned_driver_id) {
    const label = b.status === "cancelled" ? "Cancelled" : b.status === "no_show" ? "No-show" : "Unassigned";
    return { key: "issue", label, bar: "border-red-500", bg: "bg-red-500/15", text: "text-red-300", dot: "bg-red-400" };
  }
  return { key: "upcoming", label: b.status === "customer_in_car" ? "In car" : b.status === "en_route" ? "En route" : "Scheduled", bar: "border-sky-500", bg: "bg-sky-500/15", text: "text-sky-300", dot: "bg-sky-400" };
}

function DayTripsPopover({ dateKey, trips, onClose, alignRight }) {
  const nav = useNavigate();
  const d = new Date(dateKey + "T12:00:00");
  const title = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const counts = trips.reduce((a, b) => { const k = tripTone(b).key; a[k] = (a[k] || 0) + 1; return a; }, {});
  return (
    <div
      data-testid={`cal-day-popover-${dateKey}`}
      role="dialog" aria-label={`Trips on ${title}`}
      onClick={(e) => e.stopPropagation()}
      className={`absolute top-6 ${alignRight ? "right-1" : "left-1"} w-[380px] max-w-[92vw] bg-[#0A0A0A]/98 backdrop-blur border border-[#D4AF37]/40 shadow-2xl z-40`}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10">
        <div>
          <div className="text-[#F5F2EB] text-sm font-medium">{title}</div>
          <div className="text-[9px] uppercase tracking-[0.18em] text-[#71717A] mt-0.5 flex gap-2">
            <span>{trips.length} trip{trips.length === 1 ? "" : "s"}</span>
            {counts.completed > 0 && <span className="text-emerald-400">· {counts.completed} completed</span>}
            {counts.upcoming > 0 && <span className="text-sky-400">· {counts.upcoming} upcoming</span>}
            {counts.issue > 0 && <span className="text-red-400">· {counts.issue} need attention</span>}
          </div>
        </div>
        <button data-testid={`cal-day-popover-close-${dateKey}`} onClick={onClose} aria-label="Close" className="text-[#A1A1AA] hover:text-white p-1"><X size={14}/></button>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {trips.map(b => {
          const tone = tripTone(b);
          const t = new Date(b.pickup_datetime).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
          return (
            <button
              key={b.booking_id}
              type="button"
              data-testid={`cal-popover-trip-${b.booking_id}`}
              onClick={() => nav(`/dispatcher/reservation/${b.booking_id}`)}
              className={`w-full text-left px-3 py-2.5 border-b border-white/5 last:border-b-0 border-l-[3px] ${tone.bar} ${tone.bg} hover:brightness-110 transition`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#F5F2EB] text-xs font-medium truncate">{b.customer_name}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`text-[9px] uppercase tracking-[0.15em] ${tone.text}`}>{tone.label}</span>
                  <span className="text-xs text-[#F5F2EB] tabular-nums">{t}</span>
                </span>
              </div>
              <div className="text-[10px] text-[#A1A1AA] mt-0.5 truncate" title={`${b.pickup_address} → ${b.dropoff_address}`}>
                {(b.pickup_address || "").split(",")[0]} → {(b.dropoff_address || b.trip_type === "hourly" && "Hourly charter" || "").split(",")[0] || "—"}
              </div>
              <div className="text-[9px] text-[#71717A] mt-0.5 flex justify-between">
                <span className="font-mono">{b.confirmation_code}{b.assigned_driver_name ? ` · ${b.assigned_driver_name}` : ""}</span>
                <span className="font-mono">${Number(b.fare || 0).toFixed(0)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalCard({ b, onOpen, selectable = false, checked = false }) {
  const c = CAL_STATUS_COLOR[b.status] || CAL_STATUS_COLOR.pending;
  const t = new Date(b.pickup_datetime);
  const time = t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const airport = airportTag(b.pickup_address) || airportTag(b.dropoff_address);
  // Live location indicator — fetched only when in-car so we don't hammer the
  // API for every calendar card. Lightweight 12-sec poll while card is mounted.
  const inCar = b.status === "customer_in_car" || b.status === "in_progress";
  const [loc, setLoc] = useState(null);
  const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    if (!inCar) return undefined;
    let live = true;
    const fetchLoc = async () => {
      try {
        const r = await api.get(`/admin/bookings/${b.booking_id}/driver-location`);
        if (live && r.data?.lat != null) setLoc(r.data);
      } catch { /* silent */ }
    };
    fetchLoc();
    const id = setInterval(fetchLoc, 12000);
    return () => { live = false; clearInterval(id); };
  }, [inCar, b.booking_id]);
  return (
    <div className="relative">
      <button
        data-testid={`cal-card-${b.booking_id}`}
        onClick={(e) => { e.stopPropagation(); onOpen(b); }}
        className={`w-full text-left ${c.bg} ${c.text} border-l-2 ${c.border} px-1.5 py-1 hover:bg-opacity-30 transition ${checked ? "ring-1 ring-[#D4AF37]" : ""}`}
        title={`${b.confirmation_code} · ${b.customer_name} · ${c.label}`}
        aria-pressed={selectable ? checked : undefined}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="flex items-center gap-1 min-w-0">
            {selectable && (
              <span data-testid={`cal-check-${b.booking_id}`}
                className={`shrink-0 w-3 h-3 border ${checked ? "bg-[#D4AF37] border-[#D4AF37]" : "border-current/60"} inline-flex items-center justify-center text-[8px] text-black font-bold`}>
                {checked ? "✓" : ""}
              </span>
            )}
            <span className="text-[10px] truncate font-medium">{b.customer_name}</span>
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {/* Live-location pulse — only when in-car AND we have a recent fix */}
            {inCar && loc && (
              <span
                role="button"
                tabIndex={0}
                data-testid={`cal-live-loc-${b.booking_id}`}
                onClick={(e) => { e.stopPropagation(); setShowMap(s => !s); }}
                title={`Live location · last seen ${loc.captured_at ? new Date(loc.captured_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }) : 'just now'}`}
                className="relative inline-flex items-center justify-center w-2.5 h-2.5 cursor-pointer"
              >
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping"/>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 border border-white"/>
              </span>
            )}
            {airport && <span className="text-[8px] font-bold uppercase tracking-wider bg-black/30 px-1">{airport}</span>}
            {/* Payment mark — red $ = pending, green $ = paid */}
            {b.status !== "cancelled" && (
              <span
                data-testid={`cal-pay-mark-${b.booking_id}`}
                title={b.payment_status === "paid" ? "Payment completed" : b.payment_status === "balance_due" ? `Partially paid · balance due $${((b.balance_due_cents || 0) / 100).toFixed(2)}` : "Payment pending"}
                className={`text-[9px] font-bold px-1 leading-tight ${b.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-500" : b.payment_status === "balance_due" ? "bg-amber-500/25 text-amber-400" : "bg-red-500/25 text-red-500"}`}
              >{b.payment_status === "balance_due" ? "$!" : "$"}</span>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between text-[9px] opacity-80">
          <span>{time}</span>
          <span className="font-mono">${(b.fare || 0).toFixed(0)}</span>
        </div>
      </button>
      {/* Live-location quick-view popover — opens above the calendar card with
          a tiny Google Static Map of the driver's current position. */}
      {showMap && loc && (
        <div
          data-testid={`cal-live-loc-panel-${b.booking_id}`}
          className="absolute z-30 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#D4AF37]/40 p-2 shadow-2xl text-[10px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-emerald-400 font-bold uppercase tracking-[0.18em]">● Live</span>
            <button onClick={() => setShowMap(false)} className="text-[#71717A] hover:text-[#F5F2EB]">✕</button>
          </div>
          <div className="text-[#A1A1AA] mb-1.5">
            {loc.lat?.toFixed(5)}, {loc.lng?.toFixed(5)}
          </div>
          <a
            href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}
            target="_blank" rel="noopener noreferrer"
            className="block text-[#D4AF37] hover:underline"
          >Open in Google Maps →</a>
          {loc.captured_at && (
            <div className="text-[9px] text-[#71717A] mt-1.5 tracking-wider uppercase">
              Updated {new Date(loc.captured_at).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



/* ───────────────────────────────────────────────────────────────────────────
   COMPLIANCE BANNER · expiring/expired license & insurance for drivers
   ─────────────────────────────────────────────────────────────────────────── */
function ComplianceBanner() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(null); // "driverId-kind" being sent

  const remind = async (a) => {
    const key = `${a.driver_id}-${a.kind}`;
    setReminding(key);
    try {
      const r = await api.post(`/drivers/${a.driver_id}/compliance-remind`, { kind: a.kind });
      const ch = [r.data.sent.sms && "text", r.data.sent.email && "email"].filter(Boolean).join(" + ");
      toast.success(ch ? `Reminder ${ch} sent to ${a.driver_name}` : `Reminder queued for ${a.driver_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send reminder");
    } finally {
      setReminding(null);
    }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await api.get("/drivers/compliance-alerts?days_ahead=60");
        if (live) setAlerts(r.data?.alerts || []);
      } catch { /* silent */ }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, []);

  if (loading || alerts.length === 0) return null;

  const expired = alerts.filter(a => a.severity === "expired");
  const urgent = alerts.filter(a => a.severity === "urgent");
  const warning = alerts.filter(a => a.severity === "warning");

  const sev = expired.length ? "expired" : urgent.length ? "urgent" : "warning";
  const palette = {
    expired: { border: "border-red-500/50", bg: "bg-red-500/10",   dot: "bg-red-400",    label: "EXPIRED" },
    urgent:  { border: "border-amber-500/50", bg: "bg-amber-500/10", dot: "bg-amber-400",  label: "EXPIRING SOON" },
    warning: { border: "border-yellow-500/40", bg: "bg-yellow-500/10", dot: "bg-yellow-300", label: "UPCOMING" },
  }[sev];

  return (
    <div data-testid="compliance-banner" className={`mb-6 border ${palette.border} ${palette.bg} p-4`}>
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${palette.dot} animate-pulse`}/>
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-[#F5F2EB]">
          Driver compliance · {alerts.length} alert{alerts.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {alerts.slice(0, 8).map((a, i) => (
          <div key={i} data-testid={`compliance-alert-${a.driver_id}-${a.kind}`}
            className="flex items-center justify-between border border-white/5 bg-black/30 px-3 py-2">
            <span className="text-[#F5F2EB]">
              <span className="text-[#D4AF37]">{a.driver_name}</span> · {a.kind}
            </span>
            <span className="flex items-center gap-3 whitespace-nowrap">
              <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${
                a.severity === "expired" ? "text-red-300"
                : a.severity === "urgent" ? "text-amber-300"
                : "text-yellow-300"
              }`}>
                {a.days_left < 0 ? `expired ${Math.abs(a.days_left)}d ago` : `in ${a.days_left}d`} · {a.expires}
              </span>
              <button
                data-testid={`remind-${a.driver_id}-${a.kind}`}
                disabled={reminding === `${a.driver_id}-${a.kind}`}
                onClick={() => remind(a)}
                title={`Text/email ${a.driver_name} to update their ${a.kind}`}
                className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5 hover:bg-[#D4AF37]/10 disabled:opacity-50"
              >
                {reminding === `${a.driver_id}-${a.kind}` ? "Sending…" : "Remind"}
              </button>
            </span>
          </div>
        ))}
        {alerts.length > 8 && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] pt-1">
            +{alerts.length - 8} more — scroll the table below
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   DRIVER ROW · inline-editable driver record
   ─────────────────────────────────────────────────────────────────────────── */
function DriverRow({ d, onChange, onOpen }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: d.name || "",
    email: d.email || "",
    phone: d.phone || "",
    vehicle_make: d.vehicle_make || "",
    vehicle_model: d.vehicle_model || "",
    vehicle_year: d.vehicle_year || "",
    vehicle_color: d.vehicle_color || "",
    license_plate: d.license_plate || "",
    license_no: d.license_no || "",
    license_expiry: d.license_expiry || "",
    insurance_provider: d.insurance_provider || "",
    insurance_policy_no: d.insurance_policy_no || "",
    insurance_expiry: d.insurance_expiry || "",
  });

  const today = new Date().toISOString().slice(0, 10);
  const licExpired = f.license_expiry && f.license_expiry < today;
  const insExpired = f.insurance_expiry && f.insurance_expiry < today;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/drivers/${d.driver_id}`, f);
      toast.success(`Saved · ${f.name || d.driver_id}`);
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove driver ${d.name}? They will be reverted to a customer role.`)) return;
    try {
      await api.delete(`/drivers/${d.driver_id}`);
      toast.success("Driver removed");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  // Primary garage vehicle (drivers may own several) — legacy single fields as fallback
  const prim = (d.vehicles || []).find(v => v.is_primary) || (d.vehicles || [])[0] || null;
  const vName = prim
    ? [prim.year, prim.make, prim.model].filter(Boolean).join(" ")
    : [d.vehicle_year, d.vehicle_make, d.vehicle_model].filter(Boolean).join(" ");
  const vColor = prim ? prim.color : d.vehicle_color;
  const vPlate = prim ? prim.plate : d.license_plate;
  if (!editing) {
    return (
      <tr data-testid={`driver-row-${d.driver_id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
        <td className="p-3">
          <div className="text-[#F5F2EB]">{d.name}</div>
          <div className="text-[10px] text-[#71717A]">{d.phone || "no phone"}</div>
        </td>
        <td className="p-3 text-[#A1A1AA]">{d.email}</td>
        <td className="p-3 text-xs">
          <div className="text-[#F5F2EB]">
            {vName || "—"}
            {(d.vehicles || []).length > 1 && <span className="text-[9px] text-[#D4AF37] ml-1.5">+{d.vehicles.length - 1} more</span>}
          </div>
        </td>
        <td className="p-3 text-xs text-[#A1A1AA]">{vColor || "—"}</td>
        <td className="p-3 text-xs font-mono text-[#D4AF37]">{vPlate || "—"}</td>
        <td className="p-3 text-xs">
          <div className="flex flex-col gap-1">
            <span className={licExpired ? "text-red-300" : "text-[#A1A1AA]"}>Lic: {d.license_expiry || "—"}</span>
            <span className={insExpired ? "text-red-300" : "text-[#A1A1AA]"}>Ins: {d.insurance_expiry || "—"}</span>
          </div>
        </td>
        <td className="p-3 text-right whitespace-nowrap">
          <button data-testid={`driver-view-${d.driver_id}`} onClick={() => onOpen && onOpen()}
            className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#D4AF37] mr-3">View</button>
          <button data-testid={`driver-edit-${d.driver_id}`} onClick={() => setEditing(true)}
            className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] hover:text-[#F5F2EB] mr-3">Edit</button>
          <button data-testid={`driver-remove-${d.driver_id}`} onClick={remove}
            className="text-xs uppercase tracking-[0.18em] text-red-400 hover:text-red-200">Remove</button>
        </td>
      </tr>
    );
  }

  const inp = (key, ph, type = "text") => (
    <input
      data-testid={`driver-edit-${d.driver_id}-${key}`}
      type={type}
      value={f[key]}
      placeholder={ph}
      onChange={e => setF({ ...f, [key]: e.target.value })}
      className="w-full bg-black border border-white/10 px-2 py-1 text-xs text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
    />
  );

  return (
    <tr data-testid={`driver-row-${d.driver_id}-editing`} className="border-t border-white/5 bg-[#0A0A0A]/80 align-top">
      <td className="p-3 space-y-2">
        {inp("name", "Driver name")}
        {inp("phone", "Phone", "tel")}
      </td>
      <td className="p-3">
        {inp("email", "email@…", "email")}
      </td>
      <td className="p-3 space-y-2">
        <div className="grid grid-cols-3 gap-1">
          {inp("vehicle_year", "Yr")}
          {inp("vehicle_make", "Make")}
          {inp("vehicle_model", "Model")}
        </div>
        <div className="grid grid-cols-2 gap-1">
          {inp("license_no", "DL #")}
          {inp("insurance_provider", "Ins. provider")}
        </div>
        {inp("insurance_policy_no", "Policy #")}
      </td>
      <td className="p-3">{inp("vehicle_color", "Color")}</td>
      <td className="p-3">{inp("license_plate", "Plate")}</td>
      <td className="p-3">
        <div className="space-y-2">
          <label className="block">
            <span className="block text-[9px] uppercase tracking-[0.18em] text-[#71717A]">License exp</span>
            {inp("license_expiry", "", "date")}
          </label>
          <label className="block">
            <span className="block text-[9px] uppercase tracking-[0.18em] text-[#71717A]">Insurance exp</span>
            {inp("insurance_expiry", "", "date")}
          </label>
        </div>
      </td>
      <td className="p-3 text-right whitespace-nowrap">
        <button data-testid={`driver-save-${d.driver_id}`} onClick={save} disabled={saving}
          className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] hover:text-white disabled:opacity-50 mr-3">
          {saving ? "…" : "Save"}
        </button>
        <button data-testid={`driver-cancel-${d.driver_id}`} onClick={() => setEditing(false)}
          className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-white">Cancel</button>
      </td>
    </tr>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   USER ROW · inline-editable customer record + opens detail drawer
   ─────────────────────────────────────────────────────────────────────────── */
function UserRow({ u, onChange, onOpen }) {
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: u.name || "",
    email: displayEmail(u.email),
    phone: u.phone || "",
    address: u.address || "",
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${u.user_id}`, f);
      toast.success("Customer updated");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const goCreateReservation = (e) => {
    e.stopPropagation();
    const params = new URLSearchParams();
    if (u.name) params.set("for_name", u.name);
    if (u.email && !isPlaceholderEmail(u.email)) params.set("for_email", u.email);
    if (u.phone) params.set("for_phone", u.phone);
    if (u.address) params.set("for_pickup", u.address);
    nav(`/book?${params.toString()}`);
  };

  const inp = (key, ph, type = "text") => (
    <input
      data-testid={`user-edit-${u.user_id}-${key}`}
      type={type}
      value={f[key]}
      placeholder={ph}
      onChange={e => setF({ ...f, [key]: e.target.value })}
      className="w-full bg-black border border-white/10 px-2 py-1 text-xs text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
    />
  );

  if (!editing) {
    return (
      <tr data-testid={`user-row-${u.user_id}`} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
        onClick={() => onOpen && onOpen()}>
        <td className="p-3 text-[#F5F2EB]">{u.name || <span className="text-[#71717A]">—</span>}</td>
        <td className="p-3 text-[#A1A1AA]">{displayEmail(u.email) || <span className="text-[#71717A]">—</span>}</td>
        <td className="p-3 text-[#A1A1AA]">{u.phone || <span className="text-[#71717A]">—</span>}</td>
        <td className="p-3 text-xs text-[#A1A1AA]">{u.address || <span className="text-[#71717A]">—</span>}</td>
        <td className="p-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
          <div className="flex justify-end items-center gap-3 flex-wrap text-xs uppercase tracking-[0.16em]">
            <button data-testid={`user-edit-${u.user_id}`} onClick={() => setEditing(true)}
              className="text-[#D4AF37] hover:text-white">Edit</button>
            <span className="text-white/10">|</span>
            <button data-testid={`user-create-reservation-${u.user_id}`} onClick={goCreateReservation}
              className="text-[#D4AF37] hover:text-white">Create reservation</button>
            <span className="text-white/10">|</span>
            <button data-testid={`user-cards-${u.user_id}`} onClick={(e) => { e.stopPropagation(); onOpen && onOpen("cards"); }}
              className="text-[#D4AF37] hover:text-white">Payment cards</button>
            <span className="text-white/10">|</span>
            <a data-testid={`user-export-${u.user_id}`}
              href={`${API}/admin/contacts/export?format=vcf&user_id=${u.user_id}`}
              onClick={e => e.stopPropagation()}
              className="text-[#A1A1AA] hover:text-[#D4AF37]">Export .vcf</a>
            <span className="text-white/10">|</span>
            <button data-testid={`user-view-${u.user_id}`} onClick={() => onOpen && onOpen()}
              className="text-[#A1A1AA] hover:text-[#D4AF37]">View</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr data-testid={`user-row-${u.user_id}-editing`} className="border-t border-white/5 bg-[#0A0A0A]/80">
      <td className="p-3">{inp("name", "Full name")}</td>
      <td className="p-3">{inp("email", "email@…", "email")}</td>
      <td className="p-3">{inp("phone", "Phone", "tel")}</td>
      <td className="p-3">{inp("address", "Address")}</td>
      <td className="p-3 text-right whitespace-nowrap">
        <button data-testid={`user-save-${u.user_id}`} onClick={save} disabled={saving}
          className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] hover:text-white disabled:opacity-50 mr-3">
          {saving ? "…" : "Save"}
        </button>
        <button data-testid={`user-cancel-${u.user_id}`} onClick={() => setEditing(false)}
          className="text-xs uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-white">Cancel</button>
      </td>
    </tr>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   PAYMENTS TAB · paid-bookings ledger
   ─────────────────────────────────────────────────────────────────────────── */
function PaymentsTab({ bookings, onOpen }) {
  // Client payment method toggles (e.g. hide Venmo while the account is frozen)
  const [toggles, setToggles] = useState(null);
  useEffect(() => {
    api.get("/admin/payment-toggles").then(r => setToggles(r.data)).catch(() => setToggles(null));
  }, []);
  const flip = async (method) => {
    try {
      const r = await api.post("/admin/payment-toggles", { [method]: !toggles[method] });
      setToggles(r.data);
      toast.success(`${method.charAt(0).toUpperCase() + method.slice(1)} ${r.data[method] ? "shown to clients again" : "hidden from all client payment pages"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const paid = (bookings || [])
    .filter(b => b.payment_status === "paid")
    .sort((a, b) => new Date(b.pickup_datetime) - new Date(a.pickup_datetime));

  const totals = paid.reduce(
    (acc, b) => {
      acc.gross += Number(b.fare || 0);
      acc.count += 1;
      const m = (b.payment_method || "unspecified").toLowerCase();
      acc.byMethod[m] = (acc.byMethod[m] || 0) + Number(b.fare || 0);
      return acc;
    },
    { gross: 0, count: 0, byMethod: {} },
  );

  const [ledgerOpen, setLedgerOpen] = useState(false); // minimized by default — summary cards first
  const [ledgerQ, setLedgerQ] = useState("");
  const ledgerRows = useMemo(() => {
    const q = ledgerQ.trim().toLowerCase();
    if (!q) return paid;
    return paid.filter(b =>
      [b.customer_name, b.customer_email, b.customer_phone, b.confirmation_code, b.payment_method, Number(b.fare || 0).toFixed(2)]
        .some(v => (v || "").toString().toLowerCase().includes(q)));
  }, [paid, ledgerQ]);

  return (
    <section className="pb-20 px-6 md:px-12" data-testid="payments-tab">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl">Payments ledger</h2>
          <span className="text-xs text-[#A1A1AA]">{totals.count} paid · ${totals.gross.toFixed(2)} gross</span>
        </div>

        {toggles && (
          <div className="border border-white/10 bg-[#0A0A0A] p-4 mb-6 flex flex-wrap items-center gap-6" data-testid="payment-toggles-card">
            <div>
              <div className="overline mb-0.5">Client payment options</div>
              <div className="text-[10px] text-[#71717A]">Hide a method from all client pay pages (e.g. while an account is frozen)</div>
            </div>
            {["venmo", "zelle", "cash"].map(m => (
              <label key={m} className="flex items-center gap-2.5 cursor-pointer select-none" data-testid={`toggle-${m}`}>
                <button onClick={() => flip(m)} role="switch" aria-checked={toggles[m]}
                  data-testid={`toggle-${m}-btn`}
                  className={`relative w-10 h-5 rounded-full transition-colors ${toggles[m] ? "bg-[#D4AF37]" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#0A0A0A] transition-all ${toggles[m] ? "left-5" : "left-0.5"}`}/>
                </button>
                <span className="text-xs capitalize text-[#F5F2EB]">{m}</span>
                <span className={`text-[9px] uppercase tracking-[0.18em] ${toggles[m] ? "text-emerald-400" : "text-red-400"}`}>
                  {toggles[m] ? "Visible" : "Hidden"}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {Object.entries(totals.byMethod).map(([m, v]) => (
            <div key={m} className="border border-white/10 bg-[#0A0A0A] p-4">
              <div className="overline mb-1">{m}</div>
              <div className="font-serif text-2xl text-[#D4AF37]">${v.toFixed(0)}</div>
            </div>
          ))}
          {totals.count === 0 && (
            <div className="md:col-span-5 border border-white/10 bg-[#0A0A0A] p-8 text-center text-[#71717A]">
              No payments recorded yet.
            </div>
          )}
        </div>

        {totals.count > 0 && (
          <div className="border border-white/10 bg-[#0A0A0A]" data-testid="ledger-card">
            <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-white/10">
              <button data-testid="ledger-toggle" onClick={() => setLedgerOpen(v => !v)} aria-expanded={ledgerOpen}
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:underline">
                {ledgerOpen ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                {ledgerOpen ? "Minimize" : "Expand"} transactions
              </button>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A]">
                {ledgerQ ? `${ledgerRows.length} of ${paid.length}` : paid.length} payment{paid.length === 1 ? "" : "s"}
              </span>
              <div className="ml-auto relative w-full sm:w-72">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#71717A]"/>
                <input data-testid="ledger-search" value={ledgerQ}
                  onChange={e => { setLedgerQ(e.target.value); if (e.target.value && !ledgerOpen) setLedgerOpen(true); }}
                  placeholder="Search customer, confirmation, method, amount…"
                  className="w-full bg-black border border-white/10 pl-8 pr-7 py-1.5 text-xs text-[#F5F2EB] placeholder:text-[#52525B] focus:border-[#D4AF37]/60 outline-none"/>
                {ledgerQ && <button data-testid="ledger-search-clear" onClick={() => setLedgerQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white" aria-label="Clear search"><X size={12}/></button>}
              </div>
            </div>
            {ledgerOpen && (
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Confirmation</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Method</th>
                  <th className="text-right p-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledgerRows.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-xs text-[#71717A]" data-testid="ledger-empty">No payments match "{ledgerQ}"</td></tr>
                )}
                {ledgerRows.slice(0, 200).map(b => (
                  <tr
                    key={b.booking_id}
                    data-testid={`payment-row-${b.booking_id}`}
                    onClick={() => onOpen && onOpen(b)}
                    className="border-t border-white/5 cursor-pointer hover:bg-white/[0.03] transition-colors"
                  >
                    <td className="p-3 text-xs text-[#A1A1AA]">{new Date(b.pickup_datetime).toLocaleDateString()}</td>
                    <td className="p-3 font-mono text-xs text-[#D4AF37] hover:underline">{b.confirmation_code}</td>
                    <td className="p-3 text-[#F5F2EB]">{b.customer_name}<div className="text-[10px] text-[#71717A]">{b.customer_email}</div></td>
                    <td className="p-3 text-xs uppercase tracking-[0.18em] text-[#A1A1AA]">{b.payment_method || "—"}</td>
                    <td className="p-3 text-right font-mono">${Number(b.fare || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        )}

        <DriverPayrollSheet/>
        <Annual1099Summary/>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   ADD CUSTOMER MODAL · dispatcher creates a customer record (no password)
   ─────────────────────────────────────────────────────────────────────────── */
function AddCustomerModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/admin/customers", f);
      toast.success(`Customer "${f.name}" created`);
      onCreated && onCreated(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };

  const inp = (key, ph, type = "text", required = false) => (
    <input
      data-testid={`add-customer-${key}`}
      type={type}
      required={required}
      value={f[key]}
      placeholder={ph}
      onChange={e => setF({ ...f, [key]: e.target.value })}
      className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"
    />
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="add-customer-modal"
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-8 space-y-3"
      >
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="overline">Dispatcher</div>
            <h2 className="font-serif text-2xl">New customer</h2>
          </div>
          <button type="button" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>
        <p className="text-[11px] text-[#71717A] leading-relaxed -mt-1 mb-1">
          The customer can later create their own login via email/password or Google using the same email.
        </p>
        {inp("name", "Full name", "text", true)}
        {inp("email", "Email (optional if phone provided)", "email")}
        {inp("phone", "Phone", "tel")}
        {inp("address", "Default pickup address")}
        {err && <div data-testid="add-customer-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="flex-1 border border-white/10 text-[#A1A1AA] py-3 text-xs uppercase tracking-[0.22em]">Cancel</button>
          <button data-testid="add-customer-submit" type="submit" disabled={busy}
            className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
            {busy ? "Creating…" : "Create customer"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   CUSTOMER DETAIL DRAWER · profile + trips + payments + invoices + cards
   ─────────────────────────────────────────────────────────────────────────── */
function CustomerDetailDrawer({ userId, initialSection, onClose, onChanged }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [section, setSection] = useState(initialSection || "trips"); // trips | payments | invoices | cards
  const [cards, setCards] = useState([]);
  const [cardsBusy, setCardsBusy] = useState(false);
  const [sqResults, setSqResults] = useState(null); // null = not searched yet
  const [sqBusy, setSqBusy] = useState(false);
  const [sqLinking, setSqLinking] = useState("");
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/customers/${userId}`);
      setData(r.data);
      setForm({
        name: r.data.user.name || "",
        email: displayEmail(r.data.user.email),
        phone: r.data.user.phone || "",
        address: r.data.user.address || "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load customer");
      onClose();
    } finally { setBusy(false); }
  }, [userId, onClose]);

  useEffect(() => { load(); }, [load]);

  const loadCards = useCallback(async (email) => {
    if (!email) { setCards([]); return; }
    setCardsBusy(true);
    try {
      const r = await api.get(`/payments/cards/by-email`, { params: { email } });
      setCards(r.data || []);
    } catch {
      setCards([]);
    } finally { setCardsBusy(false); }
  }, []);

  useEffect(() => {
    if (section === "cards" && data?.user?.email) loadCards(data.user.email);
  }, [section, data, loadCards]);

  // Search the Square Customer Directory (cards saved via old Square invoicing)
  const searchSquareDirectory = async () => {
    const email = displayEmail(data?.user?.email);
    const phone = data?.user?.phone || "";
    if (!email && !phone) { toast.error("This client has no email or phone to search with"); return; }
    setSqBusy(true);
    setSqResults(null);
    try {
      const r = await api.get("/payments/square-directory/search", { params: { email, phone } });
      setSqResults(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Square search failed");
      setSqResults([]);
    } finally { setSqBusy(false); }
  };

  const linkSquareCard = async (m, card) => {
    setSqLinking(card.square_card_id);
    try {
      await api.post("/payments/cards/link-square", {
        user_id: userId,
        square_customer_id: m.square_customer_id,
        square_card_id: card.square_card_id,
      });
      toast.success(`${card.brand || "Card"} ····${card.last4} linked — ready to charge on new reservations`);
      setSqResults(rs => (rs || []).map(x => x.square_customer_id !== m.square_customer_id ? x : ({
        ...x, cards: x.cards.map(c => c.square_card_id === card.square_card_id ? { ...c, already_linked: true } : c),
      })));
      await loadCards(data.user.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link card");
    } finally { setSqLinking(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}`, form);
      toast.success("Profile saved");
      setEditing(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const goCreateReservation = () => {
    if (!data?.user) return;
    const params = new URLSearchParams();
    if (data.user.name) params.set("for_name", data.user.name);
    const e = data.user.email || "";
    if (e && !e.endsWith("@no-email.deluxlimo")) params.set("for_email", e);
    if (data.user.phone) params.set("for_phone", data.user.phone);
    if (data.user.address) params.set("for_pickup", data.user.address);
    nav(`/book?${params.toString()}`);
  };

  const removeCard = async (card) => {
    if (!window.confirm(`Remove ${card.brand} ···· ${card.last4}? The customer will need to re-add it.`)) return;
    try {
      await api.delete(`/payments/cards/${card.saved_card_id}`);
      toast.success("Card removed");
      await loadCards(data.user.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-drawer-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div
        data-testid="customer-drawer"
        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0A0A] border border-white/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        <header className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex justify-between items-center">
          <div>
            <div className="overline">Customer profile</div>
            <h2 className="font-serif text-2xl text-[#D4AF37]">{data?.user?.name || "…"}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="customer-drawer-create-reservation" onClick={goCreateReservation}
              className="btn-gold !py-2 !px-3 text-[10px]">+ Reservation</button>
            <button data-testid="customer-drawer-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white ml-2"><X size={20}/></button>
          </div>
        </header>

        {busy && <div className="p-12 text-center text-[#71717A]">Loading…</div>}

        {!busy && data && (
          <div className="p-6 space-y-8">
            {/* PROFILE */}
            <section data-testid="customer-profile-section">
              <div className="flex justify-between items-center mb-4">
                <div className="overline">Profile</div>
                {!editing && (
                  <button data-testid="customer-drawer-edit" onClick={() => setEditing(true)}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">Edit</button>
                )}
              </div>
              {!editing ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <DrawerField label="Name" value={data.user.name}/>
                  <DrawerField label="Email" value={displayEmail(data.user.email) || "No email on file"}/>
                  <DrawerField label="Phone" value={data.user.phone || "—"}/>
                  <DrawerField label="Address" value={data.user.address || "—"}/>
                  <DrawerField label="Role" value={data.user.role}/>
                  <DrawerField label="Joined" value={data.user.created_at ? new Date(data.user.created_at).toLocaleDateString() : "—"}/>
                </div>
              ) : (
                <div className="space-y-2">
                  {["name","email","phone","address"].map(k => (
                    <input key={k} data-testid={`customer-drawer-${k}`}
                      type={k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                      value={form[k]} placeholder={k}
                      onChange={e => setForm({ ...form, [k]: e.target.value })}
                      className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"/>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <button data-testid="customer-drawer-cancel" onClick={() => setEditing(false)}
                      className="flex-1 border border-white/10 text-[#A1A1AA] py-2.5 text-xs uppercase tracking-[0.22em]">Cancel</button>
                    <button data-testid="customer-drawer-save" onClick={save} disabled={saving}
                      className="flex-1 bg-[#D4AF37] text-black py-2.5 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
                      {saving ? "…" : "Save"}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* ROLLUP */}
            <section className="grid grid-cols-3 gap-3">
              <Stat2 n={data.rollup.total_trips} l="Total trips"/>
              <Stat2 n={data.rollup.completed} l="Completed"/>
              <Stat2 n={`$${data.rollup.lifetime_paid_usd.toFixed(0)}`} l="Lifetime paid" gold/>
              {data.rollup.payment_methods_used.length > 0 && (
                <div className="col-span-3 border border-white/10 bg-black/40 p-3">
                  <div className="overline mb-1">Payment methods used</div>
                  <div className="flex gap-2 flex-wrap">
                    {data.rollup.payment_methods_used.map(m => (
                      <span key={m} className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-[#D4AF37]/40 text-[#D4AF37]">{m}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* SECTION TABS */}
            <div className="flex border-b border-white/10">
              {[
                ["trips", `Trips (${data.trips.length})`],
                ["payments", `Payments (${data.payments.length})`],
                ["invoices", `Invoices (${data.invoices.length})`],
                ["cards", "Saved cards"],
              ].map(([k, l]) => (
                <button key={k} data-testid={`customer-section-${k}`} onClick={() => setSection(k)}
                  className={`flex-1 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 transition ${section === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A1A1AA] hover:text-white"}`}>
                  {l}
                </button>
              ))}
            </div>

            {section === "trips" && (
              <section data-testid="customer-trips" className="space-y-2">
                {data.trips.length === 0 ? (
                  <div className="text-center text-[#71717A] py-10">
                    No trips yet.
                    <div className="mt-3">
                      <button data-testid="customer-trips-create" onClick={goCreateReservation}
                        className="btn-gold !py-2 !px-4 text-[11px]">+ Create first reservation</button>
                    </div>
                  </div>
                ) : data.trips.map(t => (
                  <div key={t.booking_id} data-testid={`customer-trip-${t.booking_id}`} className="border border-white/5 bg-black/40 p-3 flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-xs text-[#D4AF37]">{t.confirmation_code}</span>
                        <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: STATUS_META[t.status]?.color }}>
                          {STATUS_META[t.status]?.label || t.status}
                        </span>
                        {t.is_quick_charge && (
                          <span data-testid={`quick-charge-badge-${t.booking_id}`} className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#D4AF37]/40 text-[#D4AF37]">Quick charge</span>
                        )}
                      </div>
                      <div className="text-xs text-[#F5F2EB] truncate">{t.pickup_address}</div>
                      {t.dropoff_address && <div className="text-xs text-[#A1A1AA] truncate">→ {t.dropoff_address}</div>}
                      <div className="text-[10px] text-[#71717A] mt-1">{new Date(t.pickup_datetime).toLocaleString()} · {t.vehicle_name}</div>
                    </div>
                    <div className="text-right whitespace-nowrap ml-3">
                      <div className="font-serif text-lg text-[#D4AF37]">${Number(t.fare || 0).toFixed(0)}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{t.payment_status}</div>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {section === "payments" && (
              <section data-testid="customer-payments" className="space-y-2">
                {data.payments.length === 0 ? (
                  <div className="text-center text-[#71717A] py-10">No payment events recorded.</div>
                ) : data.payments.map((p, i) => {
                  const trip = data.trips.find(t => t.booking_id === p.booking_id);
                  const canReceipt = trip && trip.payment_status === "paid" && trip.confirmation_code && data.user.email;
                  const receiptHref = canReceipt
                    ? `${API}/bookings/${trip.confirmation_code}/receipt.pdf?email=${encodeURIComponent(data.user.email)}&detailed=true`
                    : null;
                  return (
                    <div key={i} data-testid={`customer-payment-${i}`} className="border border-white/5 bg-black/40 p-3 flex justify-between items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#F5F2EB]">{p.tag || p.kind || "payment"} · <span className="font-mono text-[#D4AF37]">{trip?.confirmation_code || p.booking_id}</span></div>
                        <div className="text-[10px] text-[#71717A] mt-0.5">
                          {(p.at ? new Date(p.at).toLocaleString() : "—")} · method: {p.method || p.payment_method || trip?.payment_method || "—"}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        {p.amount != null && <div className="font-mono text-sm text-[#F5F2EB]">${Number(p.amount).toFixed(2)}</div>}
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{p.status || trip?.payment_status || "—"}</div>
                      </div>
                      {canReceipt && (
                        <a data-testid={`customer-payment-receipt-${i}`} href={receiptHref} target="_blank" rel="noreferrer"
                          className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white whitespace-nowrap ml-2">📄 Receipt</a>
                      )}
                    </div>
                  );
                })}
                {data.payments.length === 0 && data.trips.some(t => t.payment_status === "paid") && (
                  <div className="text-[10px] text-[#71717A] mt-3">Trips marked paid but with no payment event — receipts are available from the Trips tab.</div>
                )}
              </section>
            )}

            {section === "invoices" && (
              <section data-testid="customer-invoices" className="space-y-2">
                <div className="flex justify-end -mt-2 mb-2">
                  <button data-testid="customer-invoice-create"
                    onClick={() => setShowCreateInvoice(true)}
                    className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">
                    + Create invoice
                  </button>
                </div>
                {data.invoices.length === 0 ? (
                  <div className="text-center text-[#71717A] py-10">No invoices issued.</div>
                ) : data.invoices.map(inv => (
                  <div key={inv.invoice_id} data-testid={`customer-invoice-${inv.invoice_id}`} className="border border-white/5 bg-black/40 p-3 flex justify-between items-center">
                    <div>
                      <div className="font-mono text-xs text-[#D4AF37]">{inv.invoice_number}</div>
                      <div className="text-[10px] text-[#71717A] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}</span>
                        <span>·</span>
                        <span>{inv.trip_ids?.length || inv.booking_ids?.length || 0} trips</span>
                        <span>·</span>
                        <span>{inv.payment_method || "—"}</span>
                        {inv.payment_method === "reply-confirmed" && (
                          <span data-testid={`invoice-reply-confirmed-${inv.invoice_id}`}
                            className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-emerald-400/40 text-emerald-300">
                            📱 SMS reply
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-lg text-[#D4AF37]">${Number(inv.total || 0).toFixed(0)}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: inv.status === "paid" ? "#10B981" : "#A1A1AA" }}>{inv.status}</div>
                    </div>
                  </div>
                ))}
              </section>
            )}

            {section === "cards" && (
              <section data-testid="customer-cards" className="space-y-2">
                {cardsBusy && <div className="text-center text-[#71717A] py-6 text-sm">Loading cards…</div>}
                {!cardsBusy && cards.length === 0 && (
                  <div className="text-center text-[#71717A] py-6">
                    No saved cards yet — add one below, or the customer can add one from their portal at /customer or during checkout.
                  </div>
                )}
                {!cardsBusy && cards.map(c => (
                  <div key={c.saved_card_id} data-testid={`customer-card-${c.saved_card_id}`}
                    className="border border-white/5 bg-black/40 p-3 flex justify-between items-center">
                    <div>
                      <div className="text-sm text-[#F5F2EB]">
                        <span className="uppercase tracking-[0.2em] text-[10px] text-[#D4AF37] mr-2">{c.brand || "card"}</span>
                        <span className="font-mono">···· ···· ···· {c.last4}</span>
                      </div>
                      <div className="text-[10px] text-[#71717A] mt-1">
                        Exp {String(c.exp_month || "—").padStart(2, "0")}/{String(c.exp_year || "—").slice(-2)}
                        {c.cardholder_name && <> · {c.cardholder_name}</>}
                      </div>
                    </div>
                    <button data-testid={`customer-card-remove-${c.saved_card_id}`} onClick={() => removeCard(c)}
                      className="text-xs uppercase tracking-[0.18em] text-red-400 hover:text-red-200">Remove</button>
                  </div>
                ))}

                {/* Dispatcher adds a card for this customer (works even if they never used a card) */}
                {!cardsBusy && data?.user?.email && (
                  <div className="pt-2">
                    <DispatcherAddCard email={data.user.email} onSaved={() => loadCards(data.user.email)}/>
                  </div>
                )}

                {/* Square directory import — cards clients saved via old Square invoicing */}
                <div className="border border-white/10 bg-black/30 p-3 mt-4" data-testid="square-directory-block">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Cards saved in Square (pre-website)</div>
                    <button data-testid="find-in-square-btn" onClick={searchSquareDirectory} disabled={sqBusy}
                      className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white disabled:opacity-50">
                      {sqBusy ? "Searching…" : "Find in Square"}
                    </button>
                  </div>
                  {sqResults !== null && !sqBusy && (
                    sqResults.length === 0 ? (
                      <div className="text-xs text-[#71717A] mt-3">
                        No match in your Square directory for this client's email/phone.
                      </div>
                    ) : sqResults.map(m => (
                      <div key={m.square_customer_id} className="mt-3 border border-white/5 bg-black/40 p-3">
                        <div className="text-sm text-[#F5F2EB]">{m.name}</div>
                        <div className="text-[10px] text-[#71717A] mt-0.5">
                          {[m.email, m.phone].filter(Boolean).join(" · ") || "no contact on Square profile"}
                        </div>
                        {m.cards.length === 0 ? (
                          <div className="text-xs text-[#71717A] mt-2">No cards on file for this Square profile.</div>
                        ) : m.cards.map(card => (
                          <div key={card.square_card_id} className="flex justify-between items-center mt-2 pt-2 border-t border-white/5">
                            <div className="text-sm text-[#F5F2EB]">
                              <span className="uppercase tracking-[0.2em] text-[10px] text-[#D4AF37] mr-2">{card.brand || "card"}</span>
                              <span className="font-mono">····{card.last4}</span>
                              <span className="text-[10px] text-[#71717A] ml-2">
                                Exp {String(card.exp_month || "—").padStart(2, "0")}/{String(card.exp_year || "—").slice(-2)}
                              </span>
                            </div>
                            {card.already_linked ? (
                              <span className="text-[10px] uppercase tracking-[0.18em] text-[#10B981]">✓ Linked</span>
                            ) : (
                              <button
                                data-testid={`link-square-card-${card.square_card_id}`}
                                disabled={sqLinking === card.square_card_id}
                                onClick={() => linkSquareCard(m, card)}
                                className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-white disabled:opacity-50"
                              >
                                {sqLinking === card.square_card_id ? "Linking…" : "+ Link card"}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      {showCreateInvoice && data && (
        <CreateInvoiceModal
          customer={data.user}
          trips={data.trips}
          onClose={() => setShowCreateInvoice(false)}
          onCreated={() => { setShowCreateInvoice(false); setSection("invoices"); load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

function CreateInvoiceModal({ customer, trips, onClose, onCreated }) {
  // Only trips not already attached to a consolidated invoice and with a fare
  const billable = (trips || []).filter(t => !t.consolidated_invoice_id && Number(t.fare || 0) > 0);
  const [selected, setSelected] = useState(() => new Set(billable.map(t => t.booking_id)));
  const [markPaid, setMarkPaid] = useState(true);
  const [method, setMethod] = useState("cash");
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [discount, setDiscount] = useState(0);
  const [taxPct, setTaxPct] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [issued, setIssued] = useState(null); // success state: {invoice_id, invoice_number, total, ...}
  const [emailing, setEmailing] = useState(false);
  const [smsing, setSmsing] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectedTrips = billable.filter(t => selected.has(t.booking_id));
  const subtotal = selectedTrips.reduce((s, t) => s + Number(t.fare || 0), 0);
  const dscNum = Number(discount || 0);
  const taxNum = Number(taxPct || 0);
  const tax = Math.max(0, (subtotal - dscNum) * taxNum / 100);
  const total = Math.max(0, subtotal - dscNum + tax);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (selectedTrips.length === 0) { setErr("Select at least one trip."); return; }
    if (!customer.email && !customer.phone) {
      setErr("This customer has no email or phone on file — add one before issuing an invoice.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/invoices/consolidated", {
        customer_email: customer.email || null,
        customer_phone: customer.phone || null,
        customer_name: customer.name,
        trip_ids: Array.from(selected),
        notes: notes || undefined,
        discount: dscNum,
        tax_pct: taxNum,
        mark_paid: markPaid,
        payment_method: markPaid ? method : null,
        paid_date: markPaid ? paidDate : null,
      });
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setIssued(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create invoice");
    } finally { setBusy(false); }
  };

  const emailInvoice = async () => {
    if (!issued?.invoice_id) return;
    setEmailing(true);
    try {
      await api.post(`/invoices/consolidated/${issued.invoice_id}/email`);
      toast.success(`Invoice emailed to ${customer.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email invoice");
    } finally { setEmailing(false); }
  };

  const smsInvoice = async (channel = "sms") => {
    if (!issued?.invoice_id) return;
    setSmsing(true);
    try {
      const r = await api.post(`/invoices/consolidated/${issued.invoice_id}/sms`, null, { params: { channel } });
      const to = r.data?.to || customer.phone;
      const ch = channel === "whatsapp" ? "WhatsApp" : "SMS";
      if ((r.data?.status || "").toUpperCase() === "MOCKED") {
        toast.success(`${ch} pay link queued (MOCKED) for ${to}`);
      } else {
        toast.success(`${ch} pay link sent to ${to}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not send ${channel}`);
    } finally { setSmsing(false); }
  };

  const downloadPdf = () => {
    if (!issued?.invoice_id) return;
    window.open(`${API}/invoices/consolidated/${issued.invoice_id}/pdf`, "_blank");
  };

  const finish = () => {
    const created = issued;
    setIssued(null);
    onCreated && onCreated(created);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={issued ? finish : onClose}>
      <form
        data-testid="create-invoice-modal"
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto p-7 space-y-4"
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="overline">{issued ? "Invoice issued" : "Issue invoice"}</div>
            <h2 className="font-serif text-2xl">{issued ? issued.invoice_number : customer.name}</h2>
            <div className="text-xs text-[#A1A1AA] mt-0.5">{displayEmail(customer.email) || "No email on file"}</div>
          </div>
          <button type="button" onClick={issued ? finish : onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        {issued ? (
          <div data-testid="inv-success" className="space-y-5">
            <div className="border border-emerald-500/40 bg-emerald-500/10 p-5 text-center">
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 mb-1">✓ Issued · {issued.status === "paid" ? `paid by ${issued.payment_method}` : "open"}</div>
              <div className="font-serif text-4xl text-[#D4AF37]">${Number(issued.total).toFixed(2)}</div>
              <div className="text-[11px] text-[#A1A1AA] mt-1">{issued.trip_count} trip{issued.trip_count !== 1 ? "s" : ""} · {new Date(issued.created_at).toLocaleString()}</div>
            </div>
            <p className="text-xs text-[#A1A1AA] text-center leading-relaxed">
              Send the receipt to {customer.name?.split(" ")[0] || "the customer"} now — it goes a long way toward repeat bookings.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="inv-email-customer" onClick={emailInvoice}
                disabled={emailing || !displayEmail(customer.email)}
                title={!displayEmail(customer.email) ? "No email on file" : ""}
                className="bg-[#D4AF37] text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40 hover:opacity-90">
                {emailing ? "Sending…" : (customer.email ? "📧 Email" : "📧 No email")}
              </button>
              <button type="button" data-testid="inv-sms-customer" onClick={() => smsInvoice("sms")}
                disabled={smsing || !customer.phone}
                title={!customer.phone ? "No phone on file" : "Text the pay link via Twilio"}
                className="bg-[#D4AF37] text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40 hover:opacity-90">
                {smsing ? "Sending…" : (customer.phone ? "📨 SMS pay-link" : "📨 No phone")}
              </button>
              <button type="button" data-testid="inv-whatsapp-customer" onClick={() => smsInvoice("whatsapp")}
                disabled={smsing || !customer.phone}
                title={!customer.phone ? "No phone on file" : "Send the pay link via WhatsApp"}
                className="bg-emerald-600 text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40 hover:opacity-90">
                {smsing ? "Sending…" : (customer.phone ? "💬 WhatsApp" : "💬 No phone")}
              </button>
              <button type="button" data-testid="inv-download-pdf" onClick={downloadPdf}
                className="border border-[#D4AF37]/60 text-[#D4AF37] py-3 text-[10px] uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10">
                📄 Download PDF
              </button>
            </div>
            {issued.status !== "paid" && issued.payment_url && (
              <div className="border border-white/10 bg-black/40 p-3">
                <div className="overline mb-1">Hosted pay link</div>
                <div className="flex items-center gap-2">
                  <code data-testid="inv-pay-url" className="flex-1 text-[10px] text-[#F5F2EB] break-all">{issued.payment_url}</code>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(issued.payment_url); toast.success("Copied"); }}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white whitespace-nowrap">Copy</button>
                </div>
              </div>
            )}
            <button type="button" data-testid="inv-finish" onClick={finish}
              className="w-full border border-white/10 text-[#A1A1AA] py-3 text-xs uppercase tracking-[0.22em] hover:text-white">Done</button>
          </div>
        ) : billable.length === 0 ? (
          <div className="border border-amber-500/40 bg-amber-500/10 text-amber-200 p-4 text-sm">
            No billable trips for this customer (all trips are either invoiced already or have $0 fare).
          </div>
        ) : (
          <>
            <div>
              <div className="overline mb-2">Trips</div>
              <div className="border border-white/10 max-h-64 overflow-y-auto">
                {billable.map(t => {
                  const isSel = selected.has(t.booking_id);
                  return (
                    <label key={t.booking_id} data-testid={`inv-trip-${t.booking_id}`}
                      className={`flex items-center gap-3 px-3 py-2.5 border-b border-white/5 last:border-b-0 cursor-pointer ${isSel ? "bg-[#D4AF37]/10" : "hover:bg-white/[0.03]"}`}>
                      <input type="checkbox" checked={isSel} onChange={() => toggle(t.booking_id)}
                        className="accent-[#D4AF37]"/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[#D4AF37]">{t.confirmation_code}</span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{t.status}</span>
                          {t.payment_status === "paid" && <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">paid · {t.payment_method || "—"}</span>}
                        </div>
                        <div className="text-[11px] text-[#A1A1AA] mt-0.5 truncate">
                          {new Date(t.pickup_datetime).toLocaleDateString()} · {t.pickup_address}
                          {t.dropoff_address && <> → {t.dropoff_address}</>}
                        </div>
                      </div>
                      <div className="font-mono text-sm text-[#F5F2EB]">${Number(t.fare || 0).toFixed(2)}</div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline block mb-1">Discount ($)</span>
                <input data-testid="inv-discount" type="number" min="0" step="0.01" value={discount}
                  onChange={e => setDiscount(e.target.value)}
                  className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
              </label>
              <label className="block">
                <span className="overline block mb-1">Tax %</span>
                <input data-testid="inv-tax" type="number" min="0" step="0.01" value={taxPct}
                  onChange={e => setTaxPct(e.target.value)}
                  className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
              </label>
            </div>

            <label className="block">
              <span className="overline block mb-1">Notes (optional)</span>
              <textarea data-testid="inv-notes" rows={2} value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Thanks for choosing Delux Limo!"
                className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
            </label>

            <div className="border border-white/10 bg-black/40 p-4 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="inv-mark-paid" type="checkbox" checked={markPaid}
                  onChange={e => setMarkPaid(e.target.checked)} className="accent-[#D4AF37]"/>
                <span className="text-sm">Mark this invoice as already paid</span>
              </label>
              {markPaid && (
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] block mb-1">Method</span>
                    <select data-testid="inv-method" value={method}
                      onChange={e => setMethod(e.target.value)}
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
                      <option value="cash">Cash</option>
                      <option value="zelle">Zelle</option>
                      <option value="venmo">Venmo</option>
                      <option value="check">Check</option>
                      <option value="card">Card (offline)</option>
                      <option value="paypal">PayPal</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] block mb-1">Paid date</span>
                    <input data-testid="inv-paid-date" type="date" value={paidDate}
                      onChange={e => setPaidDate(e.target.value)}
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"/>
                  </label>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-1">
              <InvTotalRow k="Subtotal" v={`$${subtotal.toFixed(2)}`}/>
              {dscNum > 0 && <InvTotalRow k="Discount" v={`-$${dscNum.toFixed(2)}`} muted/>}
              {tax > 0 && <InvTotalRow k={`Tax (${taxNum}%)`} v={`$${tax.toFixed(2)}`} muted/>}
              <InvTotalRow k="Total" v={`$${total.toFixed(2)}`} gold/>
            </div>

            {err && <div data-testid="inv-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 border border-white/10 text-[#A1A1AA] py-3 text-xs uppercase tracking-[0.22em]">Cancel</button>
              <button data-testid="inv-submit" type="submit" disabled={busy || selectedTrips.length === 0}
                className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
                {busy ? "Issuing…" : `Issue invoice · $${total.toFixed(2)}`}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function InvTotalRow({ k, v, muted, gold }) {
  return (
    <div className="flex justify-between">
      <span className={`text-xs uppercase tracking-[0.18em] ${gold ? "text-[#D4AF37]" : muted ? "text-[#71717A]" : "text-[#A1A1AA]"}`}>{k}</span>
      <span className={`font-mono ${gold ? "text-[#D4AF37] text-lg font-medium" : "text-[#F5F2EB] text-sm"}`}>{v}</span>
    </div>
  );
}

function DrawerField({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm text-[#F5F2EB] break-all">{value || "—"}</div>
    </div>
  );
}

function Stat2({ n, l, gold }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3">
      <div className={`font-serif text-2xl ${gold ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-0.5">{l}</div>
    </div>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
   DRIVER DETAIL DRAWER · profile + trips driven + settlement receipts
   ─────────────────────────────────────────────────────────────────────────── */
/* Onboarding packet status — W-9 + contractor agreement chips, send/copy link */
function OnboardingSection({ driver, driverId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(driver?.onboarding_token
    ? `${window.location.origin}/driver-onboarding/${driver.onboarding_token}` : "");
  const w9Done = !!driver?.w9_completed_at;
  const contractDone = !!driver?.contract_signed_at;
  const complete = w9Done && contractDone;

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/drivers/${driverId}/onboarding/send`);
      setLink(r.data.link);
      try { await navigator.clipboard.writeText(r.data.link); } catch {}
      toast.success(`Packet sent${r.data.sms_sent ? " · SMS" : ""}${r.data.email_sent ? " · email" : ""} — link copied`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send packet");
    } finally { setBusy(false); }
  };

  const Chip = ({ done, label, sub }) => (
    <div className={`flex-1 border p-3 ${done ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] ${done ? "text-emerald-300" : "text-amber-300"}`}>
        {done ? "✓" : "⚠"} {label}
      </div>
      <div className="text-[11px] text-[#A1A1AA] mt-1">{sub}</div>
    </div>
  );

  return (
    <section data-testid="driver-onboarding-section" className="border border-white/10 bg-[#0A0A0A] p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">
          Onboarding packet {!complete && <span className="text-amber-300 ml-1">· incomplete</span>}
        </span>
        <div className="flex items-center gap-2">
          {link && (
            <button data-testid="onboarding-copy-link"
              onClick={() => { navigator.clipboard.writeText(link).then(() => toast.success("Link copied")); }}
              className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#D4AF37]">
              Copy link
            </button>
          )}
          <button data-testid="onboarding-send-btn" onClick={send} disabled={busy}
            className="btn-ghost !py-1.5 !px-3 text-[10px] disabled:opacity-50">
            {busy ? "Sending…" : (driver?.onboarding_sent_at ? "Re-send packet" : "Send packet")}
          </button>
        </div>
      </div>
      <div className="flex gap-3">
        <Chip done={w9Done} label="W-9"
          sub={w9Done
            ? `${driver.w9?.legal_name || ""} · ${(driver.w9?.tin_type || "").toUpperCase()} ***${driver.w9?.tin_last4 || ""} · ${new Date(driver.w9_completed_at).toLocaleDateString()}`
            : "Tax info not on file"}/>
        <Chip done={contractDone} label="Contract"
          sub={contractDone
            ? `Signed by ${driver.contract_signed_name} · ${new Date(driver.contract_signed_at).toLocaleDateString()}`
            : "Agreement not signed"}/>
      </div>
      {driver?.onboarding_sent_at && !complete && (
        <div className="text-[10px] text-[#71717A] mt-2">
          Packet sent {new Date(driver.onboarding_sent_at).toLocaleString()} — driver can still receive trips meanwhile.
        </div>
      )}
    </section>
  );
}


/* Contract template editor — Drivers tab header button + modal */
function ContractTemplateButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = async () => {
    setOpen(true);
    try {
      const r = await api.get("/admin/onboarding/contract-template");
      setText(r.data.text || "");
    } catch (e) {
      toast.error("Could not load contract template");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/onboarding/contract-template", { text });
      toast.success("Contract template saved — new signings use this text");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="contract-template-btn" onClick={openModal}
        className="btn-ghost !py-2.5 !px-4 text-[11px]">📄 Contract template</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contract-template-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}/>
          <div className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0A0A] border border-white/10 p-6 flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="overline">Driver onboarding</div>
                <h3 className="font-serif text-xl text-[#D4AF37]">Independent Contractor Agreement template</h3>
              </div>
              <button data-testid="contract-template-close" onClick={() => setOpen(false)} className="text-[#A1A1AA] hover:text-white"><X size={20}/></button>
            </div>
            <div className="text-[11px] text-[#71717A] mb-3">
              Drivers see and sign this exact text in their onboarding packet. Separate paragraphs with a blank line.
            </div>
            <textarea data-testid="contract-template-text" value={text} onChange={e => setText(e.target.value)}
              className="flex-1 min-h-[50vh] w-full bg-black/40 border border-white/10 p-4 text-xs text-[#F5F2EB] leading-relaxed font-mono focus:outline-none focus:border-[#D4AF37]/60"/>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setOpen(false)} className="btn-ghost !py-2 !px-4 text-[11px]">Cancel</button>
              <button data-testid="contract-template-save" onClick={save} disabled={busy}
                className="btn-gold !py-2 !px-5 text-[11px] disabled:opacity-50">{busy ? "Saving…" : "Save template"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function DriverDetailDrawer({ driverId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [showScorecard, setShowScorecard] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/drivers/${driverId}/detail`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load driver");
      onClose();
    } finally { setBusy(false); }
  }, [driverId, onClose]);

  useEffect(() => { load(); }, [load]);

  const openSettlementReceipt = (settlement_id) => {
    if (!settlement_id) return;
    window.open(`${API}/settlements/${settlement_id}/receipt.pdf`, "_blank");
  };

  const openTripManifest = (trip) => {
    if (!trip?.confirmation_code) return;
    window.open(`${API}/bookings/${trip.confirmation_code}/manifest.pdf`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="driver-drawer-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}/>
      <div
        data-testid="driver-drawer"
        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0A0A] border border-white/10 rounded-lg overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        <header className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex justify-between items-center">
          <div>
            <div className="overline">Driver profile</div>
            <h2 className="font-serif text-2xl text-[#D4AF37]">{data?.driver?.name || "…"}</h2>
            {data?.driver && (
              <div className="text-[11px] text-[#A1A1AA] mt-0.5">
                {data.driver.email} · {data.driver.phone || "no phone"}
              </div>
            )}
          </div>
          <button data-testid="driver-drawer-close" onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={20}/></button>
        </header>

        {!busy && data && (
          <div className="px-6 pt-4">
            <button data-testid="driver-scorecard-open" onClick={() => setShowScorecard(true)}
              className="w-full border border-[#D4AF37]/60 text-[#D4AF37] py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#D4AF37]/10">
              📊 Email scorecard to driver
            </button>
          </div>
        )}

        {busy && <div className="p-12 text-center text-[#71717A]">Loading…</div>}

        {!busy && data && (
          <div className="p-6 space-y-6">
            {/* PROFILE */}
            <section className="grid grid-cols-2 gap-3 text-sm">
              <DrawerField label="License #" value={data.driver.license_no || "—"}/>
              <DrawerField label="License expiry" value={data.driver.license_expiry || "—"}/>
              <DrawerField label="Insurance expiry" value={data.driver.insurance_expiry || "—"}/>
              <DrawerField label="Status" value={data.driver.status || "—"}/>
            </section>

            {/* GARAGE — drivers may own multiple vehicles; primary auto-applies on assignment */}
            <section data-testid="driver-garage-section">
              <DriverGarage driverId={driverId}/>
            </section>

            {/* ROLLUP */}
            <section className="grid grid-cols-4 gap-3">
              <Stat2 n={data.rollup.total_trips} l="Trips assigned"/>
              <Stat2 n={data.rollup.completed} l="Completed"/>
              <Stat2 n={data.rollup.settlements_paid} l="Settled"/>
              <Stat2 n={`$${data.rollup.lifetime_take_home_usd.toFixed(0)}`} l="Lifetime payout" gold/>
            </section>

            {/* ONBOARDING — W-9 + contractor agreement status + send packet */}
            <OnboardingSection driver={data.driver} driverId={driverId} onChange={load}/>

            {/* DOCUMENTS — license, insurance, registration with expiry tracking */}
            <FileUploadSection mode="driver_doc" driverId={driverId} apiClient={api} canDelete title="Driver documents"/>

            {/* TRIP HISTORY */}
            <section data-testid="driver-trips">
              <div className="overline mb-3">Trip history ({data.trips.length})</div>
              {data.trips.length === 0 ? (
                <div className="text-center text-[#71717A] py-8 border border-white/5 bg-black/40">
                  This driver hasn't been assigned any trips yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.trips.map(t => {
                    const s = t.settlement;
                    return (
                      <div key={t.booking_id} data-testid={`driver-trip-${t.booking_id}`}
                        className="border border-white/5 bg-black/40 p-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1 flex-wrap">
                              <span className="font-mono text-xs text-[#D4AF37]">{t.confirmation_code}</span>
                              <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: STATUS_META[t.status]?.color }}>
                                {STATUS_META[t.status]?.label || t.status}
                              </span>
                              {s && (
                                <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border"
                                  style={{
                                    color: s.status === "paid" ? "#10B981" : "#A1A1AA",
                                    borderColor: s.status === "paid" ? "#10B98155" : "#27272A",
                                  }}>
                                  Settlement · {s.status}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-[#F5F2EB] truncate">{t.pickup_address}</div>
                            {t.dropoff_address && <div className="text-xs text-[#A1A1AA] truncate">→ {t.dropoff_address}</div>}
                            <div className="text-[10px] text-[#71717A] mt-1">{new Date(t.pickup_datetime).toLocaleString()} · {t.vehicle_name}</div>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="font-serif text-lg text-[#D4AF37]">${Number(t.fare || 0).toFixed(0)}</div>
                            {s && s.driver_take_home != null && (
                              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">payout ${Number(s.driver_take_home).toFixed(0)}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-3 mt-2 pt-2 border-t border-white/5">
                          {s ? (
                            <button data-testid={`driver-trip-settlement-${t.booking_id}`}
                              onClick={() => openSettlementReceipt(s.settlement_id)}
                              className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">
                              📄 Settlement receipt {s.receipt_number ? `· ${s.receipt_number}` : ""}
                            </button>
                          ) : (
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">No settlement yet</span>
                          )}
                          <button data-testid={`driver-trip-manifest-${t.booking_id}`}
                            onClick={() => openTripManifest(t)}
                            className="text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#D4AF37]">
                            📄 Trip manifest
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
      {showScorecard && data && (
        <DriverScorecardModal driver={data.driver} onClose={() => setShowScorecard(false)}/>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   DRIVER SCORECARD MODAL · preview + send via Resend
   ─────────────────────────────────────────────────────────────────────────── */
function DriverScorecardModal({ driver, onClose }) {
  const defaultMonth = () => {
    const d = new Date();
    d.setDate(1); d.setDate(0); // last day of previous month
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [month, setMonth] = useState(defaultMonth());
  const [sc, setSc] = useState(null);
  const [busy, setBusy] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr(""); setSent(false);
    try {
      const r = await api.get(`/admin/drivers/${driver.driver_id}/scorecard`, { params: { month } });
      setSc(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load scorecard");
    } finally { setBusy(false); }
  }, [driver.driver_id, month]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true); setErr("");
    try {
      await api.post(`/admin/drivers/${driver.driver_id}/scorecard/email`, null, { params: { month } });
      toast.success(`Scorecard emailed to ${driver.email}`);
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="driver-scorecard-modal"
        onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-7 space-y-4"
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="overline">Driver scorecard</div>
            <h2 className="font-serif text-2xl">{driver.name}</h2>
            <div className="text-xs text-[#A1A1AA] mt-0.5">{driver.email || "no email on file"}</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        <label className="block">
          <span className="overline block mb-1">Period</span>
          <input data-testid="scorecard-month" type="month" value={month}
            onChange={e => setMonth(e.target.value)}
            className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm text-[#F5F2EB] focus:border-[#D4AF37] outline-none"/>
        </label>

        {busy && <div className="text-center text-[#71717A] py-6 text-sm">Loading preview…</div>}

        {!busy && sc && (
          <div data-testid="scorecard-preview" className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <ScCard n={sc.kpis.trips_completed} l="Completed"/>
              <ScCard n={`$${sc.kpis.earnings_usd.toFixed(0)}`} l="Earnings" gold/>
              <ScCard n={sc.kpis.five_star_count} l="5-star"/>
            </div>

            <div className="border border-white/10 bg-black/40 p-3">
              <div className="overline mb-2">Top routes — {sc.month}</div>
              {sc.top_routes.length === 0 ? (
                <div className="text-[11px] text-[#71717A] italic">No completed trips this month.</div>
              ) : (
                <div className="space-y-1">
                  {sc.top_routes.map((r, i) => (
                    <div key={i} className="flex justify-between items-center text-xs">
                      <span className="text-[#F5F2EB] truncate flex-1 mr-2">{r.route}</span>
                      <span className="text-[#D4AF37] font-mono">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {sc.kpis.ratings_count > 0 ? (
              <div className="text-xs text-[#A1A1AA]">
                ⭐ {sc.kpis.five_star_count} of {sc.kpis.ratings_count} ratings were 5-star · avg {sc.kpis.avg_rating}
              </div>
            ) : (
              <div className="text-[11px] text-[#71717A] italic">No customer ratings this month.</div>
            )}

            {sc.compliance_alerts.length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="overline mb-1 text-amber-300">⚠️ Compliance reminders</div>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  {sc.compliance_alerts.map((a, i) => (
                    <li key={i} className={a.expired ? "text-red-300" : "text-amber-200"}>
                      {a.kind} — expires <b>{a.expires}</b>{a.expired ? " (EXPIRED)" : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {err && <div data-testid="scorecard-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}

        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 border border-white/10 text-[#A1A1AA] py-3 text-xs uppercase tracking-[0.22em]">
            {sent ? "Done" : "Cancel"}
          </button>
          {!sent && (
            <button data-testid="scorecard-send" onClick={send} disabled={busy || sending || !driver.email}
              className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
              {sending ? "Sending…" : "📧 Email to driver"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ScCard({ n, l, gold }) {
  return (
    <div className="border border-white/10 bg-black/40 p-3 text-center">
      <div className={`font-serif text-2xl ${gold ? "text-[#D4AF37]" : "text-[#F5F2EB]"}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mt-0.5">{l}</div>
    </div>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
   EXPORT CONTACTS BUTTON · dropdown picker for CSV / VCF / XLSX
   ─────────────────────────────────────────────────────────────────────────── */
function ExportContactsButton() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const fmt = (f) => `${API}/admin/contacts/export?format=${f}`;
  return (
    <div className="relative" onMouseLeave={close}>
      <button data-testid="export-contacts-btn" onClick={() => setOpen(v => !v)}
        className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white">
        ↑ Export
      </button>
      {open && (
        <div data-testid="export-contacts-menu" className="absolute right-0 mt-2 z-30 min-w-[170px] border border-white/10 bg-[#0A0A0A] shadow-xl">
          <a data-testid="export-csv" href={fmt("csv")} onClick={close}
            className="block px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-[#F5F2EB] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] border-b border-white/5">
            CSV (Excel, Google)
          </a>
          <a data-testid="export-xlsx" href={fmt("xlsx")} onClick={close}
            className="block px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-[#F5F2EB] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] border-b border-white/5">
            XLSX (Excel)
          </a>
          <a data-testid="export-vcf" href={fmt("vcf")} onClick={close}
            className="block px-4 py-2.5 text-xs uppercase tracking-[0.2em] text-[#F5F2EB] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]">
            vCard .vcf (iPhone)
          </a>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   IMPORT CONTACTS MODAL · CSV / VCF / XLSX uploader with merge semantics
   ─────────────────────────────────────────────────────────────────────────── */
function ImportContactsModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [googleSyncing, setGoogleSyncing] = useState(false);

  useEffect(() => {
    api.get("/contacts/google/status").then(r => setGoogleStatus(r.data)).catch(() => {});
  }, []);

  const connectGoogle = () => {
    // Open OAuth in popup so the dispatcher doesn't lose their modal state
    const w = 520, h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    window.open(`${API}/contacts/google/auth`, "google-contacts",
      `width=${w},height=${h},left=${left},top=${top}`);
  };

  const syncGoogle = async () => {
    setGoogleSyncing(true);
    try {
      const r = await api.post("/contacts/google/sync");
      setResult(r.data);
      toast.success(`Google synced · ${r.data.created} new · ${r.data.enriched} enriched`);
      // Refresh status
      const s = await api.get("/contacts/google/status");
      setGoogleStatus(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google sync failed");
    } finally { setGoogleSyncing(false); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Contacts? You'll need to re-grant access to sync again.")) return;
    try {
      await api.post("/contacts/google/disconnect");
      toast.success("Google Contacts disconnected");
      setGoogleStatus({ ...googleStatus, connected: false, last_sync_at: null });
    } catch (e) {
      toast.error("Disconnect failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setResult(null);
    if (!file) { setErr("Pick a file first."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", "merge");
    setBusy(true);
    try {
      const r = await api.post("/admin/contacts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.created} new · merged ${r.data.merged} · skipped ${r.data.skipped}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const finish = () => { onDone && onDone(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={result ? finish : onClose}>
      <form
        data-testid="import-contacts-modal"
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-7 space-y-4"
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="overline">Bulk import</div>
            <h2 className="font-serif text-2xl">Import customers</h2>
            <p className="text-[11px] text-[#71717A] mt-1 leading-relaxed">
              Upload <b>CSV</b> (Excel / Google Contacts / Outlook), <b>vCard .vcf</b> (iPhone / iCloud), or <b>XLSX</b>.
              Existing customers (matched by email, or by name+phone) are <span className="text-[#D4AF37]">merged</span> — duplicate phones/emails are skipped, and additional ones are stored as a second number/email on the same record.
            </p>
          </div>
          <button type="button" onClick={result ? finish : onClose} className="text-[#A1A1AA] hover:text-white"><X size={18}/></button>
        </div>

        {!result && (
          <>
            {/* Google Contacts OAuth — one-click sync from the dispatcher's Google account */}
            {googleStatus?.configured && (
              <div data-testid="google-contacts-block" className="border border-[#004EEB]/40 bg-[#004EEB]/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#004EEB] font-medium">
                    Google Contacts {googleStatus.connected ? "· connected" : ""}
                  </div>
                  {googleStatus.last_sync_at && (
                    <div className="text-[10px] text-[#71717A]">
                      Last sync · {new Date(googleStatus.last_sync_at).toLocaleString()}
                      {googleStatus.last_sync_total != null && ` · ${googleStatus.last_sync_total} contacts`}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {!googleStatus.connected ? (
                    <button data-testid="google-connect" type="button" onClick={connectGoogle}
                      className="flex-1 bg-white text-[#050505] py-2.5 px-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-[#004EEB] hover:text-white transition flex items-center justify-center gap-2">
                      <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                      Connect Google Contacts
                    </button>
                  ) : (
                    <>
                      <button data-testid="google-sync" type="button" onClick={syncGoogle} disabled={googleSyncing}
                        className="flex-1 bg-[#004EEB] text-white py-2.5 px-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-[#003BB5] disabled:opacity-50">
                        {googleSyncing ? "Syncing…" : "↻ Re-sync now"}
                      </button>
                      <button data-testid="google-disconnect" type="button" onClick={disconnectGoogle}
                        className="border border-white/15 text-[#A1A1AA] py-2.5 px-3 text-[10px] uppercase tracking-[0.22em] hover:border-red-500/40 hover:text-red-400">
                        Disconnect
                      </button>
                    </>
                  )}
                </div>
                <div className="text-[10px] text-[#71717A] leading-relaxed">
                  Read-only access via Google People API. Same smart dedup (name+email+phone) — duplicate values are skipped, new ones land in <span className="font-mono">phone_2</span> / <span className="font-mono">email_2</span> slots.
                </div>
              </div>
            )}

            <div className="relative flex items-center my-1">
              <div className="flex-1 h-px bg-white/10"/>
              <span className="px-3 text-[9px] uppercase tracking-[0.22em] text-[#71717A] font-bold">or upload file</span>
              <div className="flex-1 h-px bg-white/10"/>
            </div>

            <label className="block">
              <span className="overline block mb-2">File</span>
              <input data-testid="import-file" type="file" accept=".csv,.vcf,.vcard,.xlsx"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[#F5F2EB] file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#D4AF37] file:text-black file:uppercase file:tracking-[0.18em] file:text-[10px] file:font-medium hover:file:opacity-90"/>
            </label>
            <div className="border border-white/5 bg-black/40 p-3 text-[11px] text-[#A1A1AA] leading-relaxed">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">Expected fields</div>
              Name · Email · Phone · Address · Notes
              <div className="mt-1 text-[10px] text-[#71717A]">Column headers from Gmail / Outlook / Apple exports are auto-mapped.</div>
            </div>
            {err && <div data-testid="import-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 border border-white/10 text-[#A1A1AA] py-3 text-xs uppercase tracking-[0.22em]">Cancel</button>
              <button data-testid="import-submit" type="submit" disabled={busy || !file}
                className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50">
                {busy ? "Importing…" : "Import contacts"}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="import-result" className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <ScCard n={result.created} l="Created"/>
              <ScCard n={result.merged} l="Merged" gold/>
              <ScCard n={result.skipped} l="Skipped"/>
            </div>
            <div className="text-[11px] text-[#A1A1AA] text-center">
              {result.total_rows} row{result.total_rows === 1 ? "" : "s"} processed
            </div>
            {result.errors?.length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="overline mb-2 text-amber-300">{result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped</div>
                <ul className="text-xs space-y-1 list-disc pl-4 text-amber-200">
                  {result.errors.map((e, i) => (
                    <li key={i}>Row {e.row}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" data-testid="import-done" onClick={finish}
              className="w-full bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium">Done</button>
          </div>
        )}
      </form>
    </div>
  );
}


/* ───────────────────────────────────────────────────────────────────────────
   QUICK INVOICE MODAL · one-off custom-amount invoice for special events
   (weddings, concerts, proms — no prior booking required)
   ─────────────────────────────────────────────────────────────────────────── */
function QuickInvoiceModal({ customers, onClose, onCreated }) {
  const [customerId, setCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "" });
  const [description, setDescription] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [notes, setNotes] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [method, setMethod] = useState("cash");
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [issued, setIssued] = useState(null);

  const usingExisting = !!customerId;
  const sel = customers?.find(c => c.user_id === customerId);
  const effective = usingExisting
    ? { name: sel?.name, email: displayEmail(sel?.email), phone: sel?.phone }
    : { name: newCustomer.name, email: newCustomer.email, phone: newCustomer.phone };

  const validItems = items
    .map(it => ({ description: (it.description || "").trim(), amount: Number(it.amount || 0) }))
    .filter(it => it.amount > 0);
  const total = validItems.reduce((s, it) => s + it.amount, 0);

  const addLine = () => setItems([...items, { description: "", amount: "" }]);
  const removeLine = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);
  const updateLine = (i, key, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [key]: v } : it));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!effective.name?.trim()) { setErr("Customer name is required."); return; }
    if (!effective.email && !effective.phone) { setErr("Add a customer email or phone (at least one)."); return; }
    if (!description.trim()) { setErr("Add a short description / event title."); return; }
    if (validItems.length === 0) { setErr("Add at least one line item with an amount > 0."); return; }
    setBusy(true);
    try {
      const r = await api.post("/invoices/quick", {
        customer_email: effective.email || null,
        customer_phone: effective.phone || null,
        customer_name: effective.name,
        description: description.trim(),
        items: validItems,
        notes: notes || null,
        mark_paid: markPaid,
        payment_method: markPaid ? method : null,
        paid_date: markPaid ? paidDate : null,
      });
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setIssued(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create invoice");
    } finally { setBusy(false); }
  };

  const emailInvoice = async () => {
    try { await api.post(`/invoices/consolidated/${issued.invoice_id}/email`); toast.success(`Emailed to ${effective.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not email"); }
  };
  const smsInvoice = async (ch) => {
    try {
      const r = await api.post(`/invoices/consolidated/${issued.invoice_id}/sms`, null, { params: { channel: ch } });
      const lbl = ch === "whatsapp" ? "WhatsApp" : "SMS";
      toast.success((r.data?.status || "").toUpperCase() === "MOCKED" ? `${lbl} queued (MOCKED)` : `${lbl} sent`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };
  const downloadPdf = () => { window.open(`${API}/invoices/consolidated/${issued.invoice_id}/pdf`, "_blank"); };
  const finish = () => { onCreated && onCreated(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-0 manual-invoice-light" onClick={issued ? finish : onClose}>
      <form data-testid="quick-invoice-modal" onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-white text-neutral-900 border border-neutral-300 shadow-2xl max-w-lg w-full mx-auto p-6 my-6 sm:my-8 md:my-12 rounded space-y-4">
        <div className="flex justify-between items-start sticky top-0 bg-white -mx-6 -mt-6 px-6 pt-6 pb-3 border-b border-neutral-200 z-10">
          <div>
            <div className="overline">{issued ? "Invoice issued" : "Quick invoice · special event"}</div>
            <h2 className="font-serif text-2xl">{issued ? issued.invoice_number : "New custom invoice"}</h2>
          </div>
          <button type="button" onClick={issued ? finish : onClose} className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none"><X size={18}/></button>
        </div>

        {!issued ? (
          <>
            <label className="block">
              <span className="overline !text-neutral-500 block mb-1">Pick existing customer (optional)</span>
              <select data-testid="qi-customer" value={customerId} onChange={e => setCustomerId(e.target.value)}
                className="w-full bg-white border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded">
                <option value="">— New / phone-only customer —</option>
                {(customers || []).map(c => (
                  <option key={c.user_id} value={c.user_id}>{c.name} {displayEmail(c.email) ? `· ${displayEmail(c.email)}` : c.phone ? `· ${c.phone}` : ""}</option>
                ))}
              </select>
            </label>

            {!usingExisting && (
              <div className="space-y-2 border border-neutral-200 bg-neutral-50 p-3 rounded">
                <div className="overline !text-neutral-500">New customer (email OR phone required)</div>
                <input data-testid="qi-new-name" placeholder="Full name *" value={newCustomer.name}
                  onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
                <input data-testid="qi-new-email" type="email" placeholder="Email" value={newCustomer.email}
                  onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
                <input data-testid="qi-new-phone" type="tel" placeholder="Phone" value={newCustomer.phone}
                  onChange={e => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
              </div>
            )}

            <label className="block">
              <span className="overline !text-neutral-500 block mb-1">Description / event *</span>
              <input data-testid="qi-description" value={description} onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Concert trip · Hartland WI → Milwaukee downtown · 2026-02-15"
                className="w-full bg-white border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
            </label>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="overline !text-neutral-500">Line items *</span>
                <button type="button" data-testid="qi-add-line" onClick={addLine}
                  className="text-[10px] uppercase tracking-[0.22em] text-[#A8841A] hover:text-neutral-900">+ Add line</button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} data-testid={`qi-line-${i}`} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`qi-line-${i}-desc`} value={it.description}
                      onChange={e => updateLine(i, "description", e.target.value)}
                      placeholder={i === 0 ? "Concert pickup" : i === 1 ? "Wait time 45 min" : "Tolls / surcharge"}
                      className="col-span-7 bg-white border border-neutral-300 px-2 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
                    <input data-testid={`qi-line-${i}-amt`} type="number" min="0" step="0.01" value={it.amount}
                      onChange={e => updateLine(i, "amount", e.target.value)}
                      placeholder="0.00"
                      className="col-span-4 bg-white border border-neutral-300 px-2 py-2 text-sm text-right text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
                    <button type="button" data-testid={`qi-line-${i}-remove`} onClick={() => removeLine(i)} disabled={items.length === 1}
                      className="col-span-1 text-neutral-500 hover:text-red-600 text-xl leading-none disabled:opacity-30">×</button>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-neutral-200">
                <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">Total</span>
                <span data-testid="qi-total" className="font-serif text-2xl text-[#A8841A]">${total.toFixed(2)}</span>
              </div>
            </div>

            <label className="block">
              <span className="overline !text-neutral-500 block mb-1">Notes (optional)</span>
              <textarea data-testid="qi-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Thanks for choosing us!"
                className="w-full bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
            </label>

            <div className="border border-neutral-200 bg-neutral-50 p-3 space-y-3 rounded">
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="qi-mark-paid" type="checkbox" checked={markPaid}
                  onChange={e => setMarkPaid(e.target.checked)} className="accent-[#D4AF37]"/>
                <span className="text-sm text-neutral-800">Mark this invoice as already paid</span>
              </label>
              {markPaid && (
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <select data-testid="qi-method" value={method} onChange={e => setMethod(e.target.value)}
                    className="bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded">
                    <option value="cash">Cash</option><option value="zelle">Zelle</option>
                    <option value="venmo">Venmo</option><option value="check">Check</option>
                    <option value="card">Card (offline)</option><option value="paypal">PayPal</option>
                  </select>
                  <input data-testid="qi-paid-date" type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)}
                    className="bg-white border border-neutral-300 px-3 py-2 text-sm text-neutral-900 focus:border-[#D4AF37] outline-none rounded"/>
                </div>
              )}
            </div>

            {err && <div data-testid="qi-error" className="text-xs text-red-700 border border-red-300 bg-red-50 p-2.5 rounded">{err}</div>}

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 border border-neutral-300 text-neutral-700 py-3 text-xs uppercase tracking-[0.22em] hover:bg-neutral-100 rounded">Cancel</button>
              <button data-testid="qi-submit" type="submit" disabled={busy || total <= 0}
                className="flex-1 bg-[#D4AF37] text-black py-3 text-xs uppercase tracking-[0.22em] font-medium disabled:opacity-50 rounded">
                {busy ? "Issuing…" : `Issue invoice${total > 0 ? ` · $${total.toFixed(2)}` : ""}`}
              </button>
            </div>
          </>
        ) : (
          <div data-testid="qi-success" className="space-y-4">
            <div className="border border-emerald-300 bg-emerald-50 p-5 text-center rounded">
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 mb-1">✓ Issued · {issued.status === "paid" ? `paid by ${issued.payment_method}` : "open"}</div>
              <div className="font-serif text-4xl text-[#A8841A]">${Number(issued.total).toFixed(2)}</div>
              <div className="text-[11px] text-neutral-600 mt-1 italic">{issued.description}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs border border-neutral-200 bg-neutral-50 p-3 rounded" data-testid="qi-invoice-meta">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Reservation #</div>
                <div className="font-mono text-neutral-900 mt-0.5" data-testid="qi-reservation-id">{issued.confirmation_code || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Invoice #</div>
                <div className="font-mono text-neutral-900 mt-0.5" data-testid="qi-invoice-number">{issued.invoice_number}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Trip booked on</div>
                <div className="text-neutral-900 mt-0.5" data-testid="qi-booked-date">
                  {issued.created_at
                    ? new Date(issued.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Chicago", timeZoneName: "short" })
                    : new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "America/Chicago", timeZoneName: "short" })}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={emailInvoice} disabled={!effective.email}
                className="bg-[#D4AF37] text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40">
                {effective.email ? "📧 Email" : "📧 No email"}
              </button>
              <button type="button" onClick={() => smsInvoice("sms")} disabled={!effective.phone}
                className="bg-[#D4AF37] text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40">
                {effective.phone ? "📨 SMS pay-link" : "📨 No phone"}
              </button>
              <button type="button" onClick={() => smsInvoice("whatsapp")} disabled={!effective.phone}
                className="bg-emerald-600 text-black py-3 text-[10px] uppercase tracking-[0.2em] font-medium disabled:opacity-40">
                {effective.phone ? "💬 WhatsApp" : "💬 No phone"}
              </button>
              <button type="button" onClick={downloadPdf}
                className="border border-[#D4AF37]/60 text-[#D4AF37] py-3 text-[10px] uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37]/10">
                📄 Download PDF
              </button>
            </div>
            {issued.status !== "paid" && issued.payment_url && (
              <div className="border border-neutral-200 bg-neutral-50 p-3 rounded">
                <div className="overline !text-neutral-500 mb-1">Hosted pay link</div>
                <div className="flex items-center gap-2">
                  <code data-testid="qi-pay-url" className="flex-1 text-[10px] text-neutral-900 break-all">{issued.payment_url}</code>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(issued.payment_url); toast.success("Copied"); }}
                    className="text-[10px] uppercase tracking-[0.22em] text-[#A8841A] hover:text-neutral-900 whitespace-nowrap">Copy</button>
                </div>
              </div>
            )}
            <button type="button" data-testid="qi-finish" onClick={finish}
              className="w-full border border-neutral-300 text-neutral-700 py-3 text-xs uppercase tracking-[0.22em] hover:bg-neutral-100 rounded">Done</button>
          </div>
        )}
      </form>
    </div>
  );
}

